import './styles/main.css';
import { Pool } from './game/Pool.js';
import { Player } from './game/Player.js';
import { Enemy, Boss } from './game/Enemy.js';
import { Coin } from './game/Coin.js';
import { Collectible } from './game/Collectible.js';
import { Projectile, Particle } from './game/Projectile.js';
import { abilities, getRandomAbilities } from './game/abilities.js';
import { SkinManager } from './game/skins.js';
import { Leaderboard } from './game/leaderboard.js';
import { TelegramIntegration } from './game/telegram.js';
import { BACKGROUNDS, BOSS_SPAWN_INTERVAL, WORLD_WIDTH, WORLD_HEIGHT, WAVE_BATCH_GROWTH_INTERVAL, WAVE_SPAWN_BATCH_MAX, VFX, SHOOT_RANGE, PROJECTILE_MAX_DIST, SHIP_SLOTS } from './game/constants.js';
import { Inventory, applyLoadoutToPlayer } from './game/space/inventory.js';
import { Cosmos } from './game/space/cosmos.js';
import { getFaction } from './game/space/factions.js';
import {
    generateLoot, generateCombatDrop, generateArtefact, generateStationStock, generateShopStock,
    mutateItem, upgradeItem, getMutateCost, getUpgradeCost,
    RARITY_COLOR, getSellPrice, getItemInfo, repairItem, getRepairCost,
} from './game/space/items.js';
import { PilotProgress, BADGE_DEFS } from './game/space/progress.js';
import { loadShipAssets, getHangarPreviewUrl, resolveItemIcon } from './game/space/shipSprites.js';
import { WorldShop, loadShopSprites } from './game/WorldShop.js';
import {
    loadPilotLook, savePilotLook, randomPilotLook, defaultPilotLook,
    PILOT_TYPES, PILOT_SUITS, PILOT_DYES, PILOT_TONES, hairsForType,
    loadPilotAssets, drawPilotPortrait, lookLabel,
} from './game/space/pilot.js';
import { PowerUp } from './game/PowerUp.js';
import { ElectricGate } from './game/ElectricGate.js';
import { GeoBomb } from './game/GeoBomb.js';
import { Portal } from './game/Portal.js';
import { Bot } from './game/Bot.js';
import { PlayerClone } from './game/Clone.js';
import { AudioManager } from './game/audio.js';
import { IAPManager } from './game/iap.js';
import { GameServices } from './game/gameServices.js';
import { CrazyGamesIntegration } from './game/crazygames.js';
import { MultiplayerClient } from './game/multiplayer.js';
import { WarpGrid } from './game/WarpGrid.js';
import { PerfStats } from './game/PerfStats.js';
import { applyCanvasShadowPolicy, setRenderQuality } from './game/renderQuality.js';
import { initDragScroll } from './game/dragScroll.js';

class Game {
    constructor() {
        this.canvas = document.getElementById('gameCanvas');
        // Opaque buffer skips destination-alpha blending. desynchronized lets
        // the compositor present without waiting on the main thread.
        this.ctx = this.canvas.getContext('2d', { alpha: false, desynchronized: true });
        this._screenInsets = { top: 0, right: 0, bottom: 0, left: 0 };

        // Game systems
        this.telegram = new TelegramIntegration();
        this.warpGrid = new WarpGrid({ cell: 48 });
        this.perf = new PerfStats();
        this.warpGrid.setQuality(this.perf.quality);
        this.inventory = new Inventory();
        this.cosmos = new Cosmos(5);
        this.progress = new PilotProgress();
        this.progress.onUnlock((badge) => this._onBadgeUnlock(badge));
        this.audio = new AudioManager();
        this._mapMode = 'system';
        this._selectedPlanetId = null;
        this._hangarTab = 'loadout';
        this._abilityLog = []; // chosen level-up abilities this run
        this.iap = new IAPManager(this.telegram);
        this.skinManager = new SkinManager(this.iap);
        this.gameServices = new GameServices();
        this.leaderboard = new Leaderboard(this.gameServices);
        this.crazyGames = new CrazyGamesIntegration();
        this.multiplayer = new MultiplayerClient();

        // Initialize IAP and platform game services
        this.iap.initialize();
        this.gameServices.initialize();
        this.crazyGames.initialize(
            () => this._cgPause(),
            () => this._cgResume()
        );

        // Game state
        this.running = false;
        this.paused = false;
        this.currentBackground = BACKGROUNDS[0];

        // Game entities
        this.player = null;
        this.enemies = [];
        this.coins = [];
        this.collectibles = [];
        this.projectiles = [];
        this.particles = [];
        this.powerUps = [];
        this.electricGates = [];
        this.geoBombs = [];
        this.portals = [];
        this.bots = [];

        // Object pools — reuse entities instead of allocating each frame
        this.enemyPool      = new Pool(() => new Enemy(0, 0));
        this.bossPool       = new Pool(() => new Boss(0, 0));
        this.projectilePool = new Pool(() => new Projectile(0, 0, 0, 0, 0));
        this.particlePool   = new Pool(() => new Particle(0, 0, '#fff'));
        this.coinPool       = new Pool(() => new Coin(0, 0));
        this.collectiblePool = new Pool(() => new Collectible(0, 0));
        this.speechBubbles = [];
        this.floatingTexts = [];
        this.clones = [];

        // Camera (tracks player through the world)
        this.camera = { x: 0, y: 0 };
        this.shake = 0;
        this.hitFlash = 0;
        this.stars = null;
        this.starSeed = 0;
        this.bombCooldown = 0;
        this.bombFlash = 0;
        this.arenaMode = null; // void boss pocket / duel arena
        this.tradeBeacon = null; // { stock, expires, x, y } mid-fight trader
        this.callTradeCd = 0;
        this.callFightCd = 0;
        this.worldShops = [];
        this._activeShop = null;
        this.skyBodies = [];
        this.pilotLook = loadPilotLook();

        // Game stats
        this.kills = 0;
        this.frame = 0;
        this.lastSpawn = 0;
        this.spawnRate = 120;
        this.lastBossSpawn = 0;
        this.lastPowerUpSpawn = 0;
        this.lastGateSpawn = 0;
        this.lastGeoBombSpawn = 0;
        this.lastPortalSpawn = 0;
        this.collectibleCount = 0;
        this.collectibleThreshold = 10; // Show ability popup every 10 collectibles
        this.currentLevel = 1;
        this.levelKillTarget = 100; // Kills needed to complete level

        // Wave system
        this.currentWave    = 0;   // wave index within current level
        this.waveActive     = false;
        this.waveEnemiesLeft = 0;  // enemies still to spawn this wave
        this.waveSpawnTimer  = 0;  // frames between spawns inside a wave
        this.wavePauseTimer  = 0;  // cooldown frames between waves

        // Input state
        this.input = {
            joystick: {
                active: false,
                x: 0,
                y: 0,
                baseX: 0,
                baseY: 0
            },
            mouse: {
                x: 0,
                y: 0,
                active: false
            },
            keys: {
                up: false, down: false, left: false, right: false
            },
            // Twin-stick aim (gamepad right stick / optional)
            aim: { x: 0, y: 0, active: false },
            gamepadConnected: false,
            // Edge-trigger button state for gamepad
            gpPrevButtons: {},
        };
        this.bombCooldown = 0;

        this.init();
    }

    init() {
        this.bindSafeArea();
        this.resizeCanvas();
        window.addEventListener('resize', () => this.resizeCanvas());

        this.setupInput();
        this.setupUI();

        // Load ship / shop sprites then start
        Promise.all([loadShipAssets(), loadShopSprites(), loadPilotAssets()]).finally(() => this.start());
    }

    resizeCanvas() {
        const w = window.innerWidth;
        const h = window.innerHeight;
        if (this.canvas.width !== w || this.canvas.height !== h) {
            this.canvas.width = w;
            this.canvas.height = h;
            // Resizing the bitmap resets context state, including any shadow lock.
            this._lastQuality = undefined;
            if (this.ctx) {
                this.ctx.__shadowLocked = false;
                if (Object.prototype.hasOwnProperty.call(this.ctx, 'shadowBlur')) {
                    try { delete this.ctx.shadowBlur; } catch { /* host object */ }
                }
            }
        }
        this.applySafeAreaFallback();
    }

    /** True when menus/popups own the keyboard (no gameplay hotkeys). */
    isGameplayInputBlocked() {
        if (!this.running || this.paused) return true;
        const itemOverlay = document.getElementById('itemInfoOverlay');
        if (itemOverlay && !itemOverlay.hidden) return true;
        for (const popup of document.querySelectorAll('.popup')) {
            const d = popup.style.display;
            if (d === 'block' || d === 'flex') return true;
        }
        return false;
    }

    clearMovementKeys() {
        if (!this.input?.keys) return;
        this.input.keys.up = false;
        this.input.keys.down = false;
        this.input.keys.left = false;
        this.input.keys.right = false;
    }

    setupInput() {
        const joystickArea = document.getElementById('joystickArea');
        const joystickStick = document.getElementById('joystickStick');

        // Keyboard WASD / arrows (Geometry Wars-style movement fallback)
        const setKey = (e, down) => {
            switch (e.code) {
                case 'KeyW': case 'ArrowUp':    this.input.keys.up = down; break;
                case 'KeyS': case 'ArrowDown':  this.input.keys.down = down; break;
                case 'KeyA': case 'ArrowLeft':  this.input.keys.left = down; break;
                case 'KeyD': case 'ArrowRight': this.input.keys.right = down; break;
                default: return;
            }
            e.preventDefault();
        };
        window.addEventListener('keydown', (e) => {
            // Never hijack typing in settings / multiplayer fields
            const tag = (e.target && e.target.tagName) ? e.target.tagName.toLowerCase() : '';
            if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable) {
                return;
            }

            // Hotkeys + movement only during active gameplay (no open popups)
            if (this.isGameplayInputBlocked()) {
                // Keep Esc free for the item-info modal
                if (e.key === 'Escape') this.hideItemInfo();
                this.clearMovementKeys();
                return;
            }

            if (e.code === 'F3') {
                e.preventDefault();
                this.perf.toggle();
                return;
            }
            if (e.code === 'F4') {
                e.preventDefault();
                this.perf.resetMinMax();
                return;
            }
            if (e.code === 'KeyI' && !e.repeat) {
                e.preventDefault();
                this.toggleInventory();
                return;
            }
            if (e.code === 'KeyM' && !e.repeat) {
                e.preventDefault();
                this.toggleStarMap();
                return;
            }
            if (e.code === 'KeyT' && !e.repeat) {
                e.preventDefault();
                this.toggleTrade();
                return;
            }
            if (e.code === 'KeyE' && !e.repeat) {
                e.preventDefault();
                if (!this.tryDockShop()) {
                    this.floatingTexts.push({
                        x: this.player?.x || 0,
                        y: (this.player?.y || 0) - 40,
                        text: 'No shop in range — fly to a glowing pad',
                        color: '#ffaa88', age: 0, duration: 70, pulse: false,
                    });
                }
                return;
            }
            if (e.code === 'KeyY' && !e.repeat) {
                e.preventDefault();
                this.callTradeBeacon();
                return;
            }
            if (e.code === 'KeyF' && !e.repeat) {
                e.preventDefault();
                this.callBattlefield();
                return;
            }
            setKey(e, true);
        });
        window.addEventListener('keyup', (e) => setKey(e, false));

        // Gamepad connect/disconnect — browsers only expose pads after a button press
        window.addEventListener('gamepadconnected', (e) => {
            this.input.gamepadConnected = true;
            console.log('[gamepad] connected:', e.gamepad.id);
        });
        window.addEventListener('gamepaddisconnected', () => {
            const pads = navigator.getGamepads?.() || [];
            this.input.gamepadConnected = Array.from(pads).some(Boolean);
        });

        this.canvas.addEventListener('touchstart', (e) => {
            e.preventDefault();
            const touch = e.touches[0];
            const rect = this.canvas.getBoundingClientRect();
            const touchX = touch.clientX - rect.left;
            const touchY = touch.clientY - rect.top;

            if (touchY > this.canvas.height * 0.5) {
                this.input.joystick.active = true;
                this.input.joystick.baseX = touchX;
                this.input.joystick.baseY = touchY;
                joystickArea.style.left = (touchX - 75) + 'px';
                joystickArea.style.top = (touchY - 75) + 'px';
                joystickArea.classList.add('active');
            }
        }, { passive: false });

        this.canvas.addEventListener('touchmove', (e) => {
            e.preventDefault();
            if (!this.input.joystick.active) return;

            const touch = e.touches[0];
            const rect = this.canvas.getBoundingClientRect();
            const touchX = touch.clientX - rect.left;
            const touchY = touch.clientY - rect.top;

            const dx = touchX - this.input.joystick.baseX;
            const dy = touchY - this.input.joystick.baseY;
            const distance = Math.sqrt(dx * dx + dy * dy);
            const maxDistance = 60;

            if (distance > maxDistance) {
                this.input.joystick.x = (dx / distance) * maxDistance;
                this.input.joystick.y = (dy / distance) * maxDistance;
            } else {
                this.input.joystick.x = dx;
                this.input.joystick.y = dy;
            }

            joystickStick.style.transform = `translate(calc(-50% + ${this.input.joystick.x}px), calc(-50% + ${this.input.joystick.y}px))`;
        }, { passive: false });

        this.canvas.addEventListener('touchend', (e) => {
            e.preventDefault();
            this.input.joystick.active = false;
            this.input.joystick.x = 0;
            this.input.joystick.y = 0;
            joystickArea.classList.remove('active');
            joystickStick.style.transform = 'translate(-50%, -50%)';
        }, { passive: false });

        // Mouse/Touchpad controls for laptop
        this.canvas.addEventListener('mousemove', (e) => {
            const rect = this.canvas.getBoundingClientRect();
            this.input.mouse.x = e.clientX - rect.left;
            this.input.mouse.y = e.clientY - rect.top;
            this.input.mouse.active = true;
        });

        this.canvas.addEventListener('mouseleave', () => {
            this.input.mouse.active = false;
        });
    }

    bindSafeArea() {
        window.__applyNativeSafeInsets = (top, right, bottom, left) => {
            this._nativeInset = {
                top: Number(top) || 0,
                right: Number(right) || 0,
                bottom: Number(bottom) || 0,
                left: Number(left) || 0,
            };
            // Distinguishes "the OS reported 0" from "the bridge has not run yet".
            this._nativeInsetReady = true;
            this.applySafeAreaFallback();
        };
        const refresh = () => this.applySafeAreaFallback();
        window.visualViewport?.addEventListener('resize', refresh);
        window.addEventListener('orientationchange', refresh);
        const tg = window.Telegram?.WebApp;
        tg?.onEvent?.('safeAreaChanged', refresh);
        tg?.onEvent?.('contentSafeAreaChanged', refresh);
        tg?.onEvent?.('viewportChanged', refresh);
        requestAnimationFrame(() => requestAnimationFrame(refresh));
    }

    async initNativeSafeArea() {
        try {
            const { StatusBar, Style } = await import('@capacitor/status-bar');
            await StatusBar.setOverlaysWebView({ overlay: true });
            await StatusBar.setStyle({ style: Style.Light });
        } catch {
            // StatusBar plugin unavailable (web / missing native)
        }
        this.applySafeAreaFallback();
    }

    _readEnvInsets() {
        if (!this._safeProbe) {
            const probe = document.createElement('div');
            probe.setAttribute('aria-hidden', 'true');
            probe.style.cssText =
                'position:fixed;visibility:hidden;pointer-events:none;top:0;left:0;width:0;height:0;' +
                'padding:env(safe-area-inset-top,0px) env(safe-area-inset-right,0px) ' +
                'env(safe-area-inset-bottom,0px) env(safe-area-inset-left,0px)';
            document.documentElement.appendChild(probe);
            this._safeProbe = probe;
        }
        const cs = getComputedStyle(this._safeProbe);
        return {
            top: parseFloat(cs.paddingTop) || 0,
            right: parseFloat(cs.paddingRight) || 0,
            bottom: parseFloat(cs.paddingBottom) || 0,
            left: parseFloat(cs.paddingLeft) || 0,
        };
    }

    _telegramInsets() {
        const tg = window.Telegram?.WebApp;
        if (!tg) return null;
        const a = tg.safeAreaInset || {};
        const b = tg.contentSafeAreaInset || {};
        return {
            top: Math.max(Number(a.top) || 0, Number(b.top) || 0),
            right: Math.max(Number(a.right) || 0, Number(b.right) || 0),
            bottom: Math.max(Number(a.bottom) || 0, Number(b.bottom) || 0),
            left: Math.max(Number(a.left) || 0, Number(b.left) || 0),
        };
    }

    /** Used only when the WebView reports no insets at all (older Android, some WKWebViews). */
    _nativeSafeHeuristic() {
        const ua = navigator.userAgent || '';
        const ios = /iPad|iPhone|iPod/.test(ua)
            || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        const landscape = window.innerWidth > window.innerHeight;
        if (ios) {
            const longSide = Math.max(window.screen.width, window.screen.height);
            const shortSide = Math.min(window.screen.width, window.screen.height);
            const notched = longSide / Math.max(1, shortSide) > 1.9;
            if (!notched) return { top: 20, right: 0, bottom: 0, left: 0 };
            if (landscape) return { top: 0, right: 47, bottom: 21, left: 47 };
            return { top: 54, right: 0, bottom: 34, left: 0 };
        }
        if (landscape) return { top: 0, right: 24, bottom: 24, left: 24 };
        return { top: 28, right: 0, bottom: 28, left: 0 };
    }

    /**
     * Publish fallbacks for --safe-* . CSS takes the max of env(), these
     * fallbacks, and Telegram variables, so a real inset is never applied twice.
     */
    applySafeAreaFallback() {
        const envI = this._readEnvInsets();
        const tg = this._telegramInsets();
        const nativeI = this._nativeInset;
        const sides = ['top', 'right', 'bottom', 'left'];
        const anyKnown = sides.some((side) =>
            (envI[side] || 0) > 0 || (tg?.[side] || 0) > 0 || (nativeI?.[side] || 0) > 0
        );
        const isNative = document.body.classList.contains('is-native');
        const guess = (!anyKnown && isNative && !this._nativeInsetReady)
            ? this._nativeSafeHeuristic()
            : null;
        const style = document.documentElement.style;
        const screen = { top: 0, right: 0, bottom: 0, left: 0 };
        for (const side of sides) {
            const fallback = Math.max(tg?.[side] || 0, nativeI?.[side] || 0, guess?.[side] || 0);
            style.setProperty(`--safe-${side}-fallback`, `${Math.round(fallback)}px`);
            screen[side] = Math.max(envI[side] || 0, fallback);
        }
        this._screenInsets = screen;
    }

    screenInsets() {
        return this._screenInsets || { top: 0, right: 0, bottom: 0, left: 0 };
    }

    setupActionDock() {
        const dock = document.getElementById('actionDock');
        const toggle = document.getElementById('menuToggle');
        const nav = document.getElementById('topActions');
        if (!dock || !toggle || !nav) return;

        if (this.gameServices?.isNative) {
            document.body.classList.add('is-native');
            this.initNativeSafeArea();
        }
        this.perf?.syncCompact();

        const setOpen = (open) => {
            dock.classList.toggle('open', open);
            toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        };

        toggle.addEventListener('click', (e) => {
            e.stopPropagation();
            this.audio.init();
            setOpen(!dock.classList.contains('open'));
        });

        nav.addEventListener('click', (e) => {
            const btn = e.target.closest('.nav-btn');
            if (!btn || btn.hidden || btn.style.display === 'none') return;
            setOpen(false);
        });

        document.addEventListener('pointerdown', (e) => {
            if (!dock.classList.contains('open')) return;
            if (dock.contains(e.target)) return;
            setOpen(false);
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && dock.classList.contains('open')) {
                setOpen(false);
            }
        });
    }

    setupUI() {
        this.setupActionDock();
        // Shop button
        const shopBtn = document.getElementById('shopBtn');
        if (shopBtn) {
            shopBtn.addEventListener('click', () => {
                this.audio.init(); // Initialize on first user interaction
                this.showSkinShop();
            });
        }

        // Leaderboard button
        const leaderboardBtn = document.getElementById('leaderboardBtn');
        if (leaderboardBtn) {
            leaderboardBtn.addEventListener('click', () => {
                this.audio.init();
                this.showLeaderboard();
            });
        }

        // Settings button
        const settingsBtn = document.getElementById('settingsBtn');
        if (settingsBtn) {
            settingsBtn.addEventListener('click', () => {
                this.audio.init();
                this.showSettings();
            });
        }

        document.getElementById('inventoryBtn')?.addEventListener('click', () => {
            this.audio.init();
            this.toggleInventory();
        });
        document.getElementById('pilotPortraitHud')?.addEventListener('click', () => {
            this.audio.init();
            this._hangarTab = 'pilot';
            const el = document.getElementById('inventoryPopup');
            if (el?.style.display === 'block') {
                this.renderInventory();
            } else {
                this.toggleInventory();
            }
        });

        // Hangar tabs (loadout / abilities / systems)
        document.querySelectorAll('.hangar-tab').forEach(tab => {
            tab.addEventListener('click', () => {
                const id = tab.getAttribute('data-hangar-tab');
                if (!id) return;
                this._hangarTab = id;
                this.renderInventory();
            });
        });
        document.getElementById('starMapBtn')?.addEventListener('click', () => {
            this.audio.init();
            this.toggleStarMap();
        });
        document.getElementById('tradeBtn')?.addEventListener('click', () => {
            this.audio.init();
            this.toggleTrade();
        });
        document.getElementById('shopDockBtn')?.addEventListener('click', () => {
            this.audio.init();
            this.tryDockShop();
        });
        document.getElementById('callTradeBtn')?.addEventListener('click', () => {
            this.audio.init();
            this.callTradeBeacon();
        });
        document.getElementById('callFightBtn')?.addEventListener('click', () => {
            this.audio.init();
            this.callBattlefield();
        });

        document.querySelectorAll('.map-tab').forEach(tab => {
            tab.addEventListener('click', () => {
                document.querySelectorAll('.map-tab').forEach(t => t.classList.remove('active'));
                tab.classList.add('active');
                this._mapMode = tab.dataset.map;
                this.renderStarMap();
            });
        });

        // Player name change
        const playerNameInput = document.getElementById('playerNameInput');
        const saveNameBtn = document.getElementById('saveNameBtn');
        const nameChangeMsg = document.getElementById('nameChangeMsg');
        if (playerNameInput && saveNameBtn) {
            playerNameInput.value = this.telegram.getLocalPlayerName();
            saveNameBtn.addEventListener('click', () => {
                const newName = this.telegram.setLocalPlayerName(playerNameInput.value);
                playerNameInput.value = newName;
                if (this.player) this.player.name = newName;
                nameChangeMsg.textContent = 'Name saved!';
                setTimeout(() => { nameChangeMsg.textContent = ''; }, 2000);
            });
        }

        // Settings controls
        const musicToggle = document.getElementById('musicToggle');
        if (musicToggle) {
            musicToggle.checked = this.audio.musicEnabled;
            musicToggle.addEventListener('change', (e) => {
                this.audio.init();
                this.audio.setMusicEnabled(e.target.checked);
            });
        }

        const effectsToggle = document.getElementById('effectsToggle');
        if (effectsToggle) {
            effectsToggle.checked = this.audio.effectsEnabled;
            effectsToggle.addEventListener('change', (e) => {
                this.audio.setEffectsEnabled(e.target.checked);
            });
        }

        // Initialize audio on first canvas interaction
        this.canvas.addEventListener('click', () => {
            this.audio.init();
        }, { once: true });

        // Drag-to-scroll on all overflow panels (settings, hangar, trade, etc.)
        initDragScroll();
    }

    start() {
        this.running = true;
        if (this.multiplayer.isConnected()) {
            this.multiplayer.setPlayer(this.player);
        }
        this.crazyGames.gameplayStart();
        const playerName = this.crazyGames.getUserName() ?? this.telegram.getPlayerName();
        this.player = new Player(
            WORLD_WIDTH / 2,
            WORLD_HEIGHT / 2,
            this.skinManager.getCurrentSkin(),
            playerName
        );
        // Space inventory / loadout (once per run)
        if (!this._starterGiven) {
            this.inventory.giveStarter();
            this._starterGiven = true;
        }
        applyLoadoutToPlayer(this.player, this.inventory);
        if (this.pilotLook?.name) this.player.name = this.pilotLook.name;
        this.applyPlanetEnvironment(this.cosmos.getPlanet());
        this.refreshPilotHud();
        // Seed career planet visit + pilot sync
        const startPlanet = this.cosmos.getPlanet();
        if (startPlanet) {
            this.trackProgress({
                planetsVisited: startPlanet.id,
                planetTypes: startPlanet.type ? { [startPlanet.type]: true } : {},
                setPilotLevel: this.player.level,
            });
        }
        // Snap camera to player on game start
        this.camera.x = Math.max(0, Math.min(WORLD_WIDTH - this.canvas.width,  this.player.x - this.canvas.width  / 2));
        this.camera.y = Math.max(0, Math.min(WORLD_HEIGHT - this.canvas.height, this.player.y - this.canvas.height / 2));

        // Release all pooled entities before clearing arrays
        this.enemyPool.releaseAll(this.enemies.filter(e => !e.isBoss));
        this.bossPool.releaseAll(this.enemies.filter(e => e.isBoss));
        this.projectilePool.releaseAll(this.projectiles);
        this.particlePool.releaseAll(this.particles);
        this.coinPool.releaseAll(this.coins);
        this.collectiblePool.releaseAll(this.collectibles);

        this.enemies = [];
        this.coins = [];
        this.collectibles = [];
        this.projectiles = [];
        this.particles = [];
        this.powerUps = [];
        this.electricGates = [];
        this.geoBombs = [];
        this.portals = [];
        this.bots = [];
        this.speechBubbles = [];
        this.floatingTexts = [];
        this.clones = [];
        this._abilityLog = [];
        this._hangarTab = 'loadout';
        this.arenaMode = null;
        this.tradeBeacon = null;
        this.callTradeCd = 0;
        this.callFightCd = 0;
        this._activeShop = null;
        this.kills = 0;
        this.frame = 0;
        this.lastSpawn = 0;
        this.lastBossSpawn = Date.now();
        this.lastPowerUpSpawn = Date.now();
        this.lastGateSpawn = Date.now();
        this.lastGeoBombSpawn = Date.now();
        this.lastPortalSpawn = Date.now();
        this.collectibleCount = 0;
        this.currentLevel = 1;
        this.levelKillTarget = 100;
        this.currentWave     = 0;
        this.waveActive      = false;
        this.waveEnemiesLeft = 0;
        this.waveSpawnTimer  = 0;
        this.wavePauseTimer  = 0;
        this.shake = 0;
        this.hitFlash = 0;
        this.bombFlash = 0;
        this.bombCooldown = 0;
        this._ensureStars();

        // Spawn initial bots (3-5 random)
        const botCount = 3 + Math.floor(Math.random() * 3);
        for (let i = 0; i < botCount; i++) {
            this.spawnBot();
        }

        // Geometry Wars bomb geodes (fly over to detonate)
        for (let i = 0; i < 6; i++) {
            this.spawnGeoBomb();
        }

        // Spawn initial portal
        this.spawnPortal();

        this.gameLoop();
    }

    /**
     * Apply radial deadzone + re-scale (standard gamepad feel).
     */
    _stickAxis(x, y, deadzone = 0.18) {
        const mag = Math.hypot(x, y);
        if (mag < deadzone) return { x: 0, y: 0, mag: 0 };
        const scaled = Math.min(1, (mag - deadzone) / (1 - deadzone));
        return { x: (x / mag) * scaled, y: (y / mag) * scaled, mag: scaled };
    }

    /**
     * Read twin-stick gamepad: left = move, right = aim/fire.
     * Also handles A/B bomb and Start pause-ish (settings).
     */
    pollGamepad() {
        const pads = navigator.getGamepads?.() || [];
        let move = null;
        let aim = null;
        let anyPad = false;

        for (const gp of pads) {
            if (!gp) continue;
            anyPad = true;

            // Left stick (axes 0,1) — movement
            const left = this._stickAxis(gp.axes[0] || 0, gp.axes[1] || 0, 0.16);
            if (left.mag > 0) move = left;

            // Right stick — aim. Standard mapping: axes 2,3.
            // Some pads (especially older WebKit) use 3,4 — try both.
            let rx = gp.axes[2], ry = gp.axes[3];
            if (rx === undefined || (Math.abs(rx) < 0.01 && Math.abs(ry || 0) < 0.01 && gp.axes.length > 4)) {
                rx = gp.axes[3] ?? 0;
                ry = gp.axes[4] ?? 0;
            }
            const right = this._stickAxis(rx || 0, ry || 0, 0.22);
            if (right.mag > 0) aim = right;

            // D-pad fallback for movement (buttons 12-15)
            const dpadX = (gp.buttons[15]?.pressed ? 1 : 0) - (gp.buttons[14]?.pressed ? 1 : 0);
            const dpadY = (gp.buttons[13]?.pressed ? 1 : 0) - (gp.buttons[12]?.pressed ? 1 : 0);
            if (!move && (dpadX || dpadY)) {
                move = this._stickAxis(dpadX, dpadY, 0.1);
            }

            // Face buttons: A (0) or B (1) or X (2) = bomb when available
            const pressed = (i) => !!gp.buttons[i]?.pressed;
            const edge = (name, i) => {
                const now = pressed(i);
                const was = !!this.input.gpPrevButtons[name];
                this.input.gpPrevButtons[name] = now;
                return now && !was;
            };
            if (edge('a', 0) || edge('b', 1) || edge('x', 2)) {
                this.tryActivateBombFromInput();
            }

            // Only use first active pad
            break;
        }

        this.input.gamepadConnected = anyPad;
        return { move, aim };
    }

    tryActivateBombFromInput() {
        // Geometry Wars–style bomb (A/B/X). Cooldown so it isn't free spam.
        if (!this.player || this.isGameplayInputBlocked()) return;
        if (this.bombCooldown > 0) return;

        this.bombCooldown = 480; // ~8s at 60fps
        this.trackProgress({ bombsUsed: 1 });
        this.detonateRadiusBomb(this.player.x, this.player.y, 240, {
            bossDamage: 90,
            color: '#ff66aa',
        });
        this.telegram.hapticFeedback('impact');
    }

    /**
     * Geometry Wars radial bomb: ONE center warp + kill enemies inside radius.
     * Avoids per-enemy warp/VFX storms that tank FPS.
     */
    detonateRadiusBomb(x, y, radius = 280, opts = {}) {
        const bossDamage = opts.bossDamage ?? 100;
        const color = opts.color ?? '#00e8ff';

        // Single bomb shockwave (replaces stacking kill pulses)
        this.warpGrid.pulse(x, y, 'bomb');
        this.warpGrid.beginBombBoost(); // temporarily cheaper grid while boom runs
        this.addShake(Math.min(10, VFX.BOSS_DEATH_SHAKE * 0.55));
        this.bombFlash = 10;
        // Modest central VFX only — not 40+ particles per enemy
        this.createParticles(x, y, color, 18, 'spark');
        this.createParticles(x, y, '#ffffff', 1, 'ring', { radius: 16, growth: 8, life: 28 });
        this.createParticles(x, y, color, 2, 'glow', { radius: 70, life: 16 });
        this.audio.playExplosion();

        this.floatingTexts.push({
            x, y: y - 20,
            text: 'BOMB',
            color,
            age: 0,
            duration: 40,
            pulse: true,
        });

        // Batch-kill without per-enemy warp / heavy VFX / audio spam
        const batch = { skipVfx: true, skipWarp: true, skipShake: true, skipAudio: true, quietDrops: true };
        let killed = 0;
        for (let i = this.enemies.length - 1; i >= 0; i--) {
            const e = this.enemies[i];
            const dx = e.x - x;
            const dy = e.y - y;
            if (dx * dx + dy * dy > radius * radius) continue;

            if (e.isBoss) {
                if (e.takeDamage(bossDamage)) {
                    // Tiny spark only for boss kill
                    this.createParticles(e.x, e.y, e.color, 6, 'spark');
                    this.handleEnemyDeath(e, i, batch);
                    killed++;
                } else {
                    this.createParticles(e.x, e.y, e.color, 4, 'spark');
                }
            } else {
                // Sample a few sparks across kills (max 6 clusters)
                if (killed < 6 && this.inView(e.x, e.y, 20)) {
                    this.createParticles(e.x, e.y, e.color, 3, 'spark');
                }
                this.handleEnemyDeath(e, i, batch);
                killed++;
            }
        }

        // One shared XP float if we wiped a pack
        if (killed > 0) {
            this.floatingTexts.push({
                x, y: y - 40,
                text: `×${killed}`,
                color: '#ffd700',
                age: 0,
                duration: 60,
                pulse: true,
            });
        }
    }

    updatePlayer() {
        const gp = this.pollGamepad();
        let controlled = false;

        // 1) Twin-stick gamepad movement
        if (gp.move && gp.move.mag > 0) {
            this.player.vx = gp.move.x * this.player.speed * 1.35;
            this.player.vy = gp.move.y * this.player.speed * 1.35;
            controlled = true;
        }

        // Right stick aim (also used by shooting)
        if (gp.aim && gp.aim.mag > 0) {
            this.input.aim.x = gp.aim.x;
            this.input.aim.y = gp.aim.y;
            this.input.aim.active = true;
            // Face movement or aim — Geometry Wars faces fire direction when aiming
            this.player.angle = Math.atan2(gp.aim.y, gp.aim.x);
        } else {
            this.input.aim.active = false;
        }

        // 2) Keyboard
        if (!controlled) {
            let kx = 0, ky = 0;
            if (this.input.keys.left) kx -= 1;
            if (this.input.keys.right) kx += 1;
            if (this.input.keys.up) ky -= 1;
            if (this.input.keys.down) ky += 1;
            if (kx || ky) {
                const len = Math.hypot(kx, ky) || 1;
                this.player.vx = (kx / len) * this.player.speed * 1.25;
                this.player.vy = (ky / len) * this.player.speed * 1.25;
                controlled = true;
            }
        }

        // 3) Virtual joystick (mobile)
        if (!controlled && this.input.joystick.active) {
            const maxDistance = 60;
            this.player.vx = (this.input.joystick.x / maxDistance) * this.player.speed * 1.5;
            this.player.vy = (this.input.joystick.y / maxDistance) * this.player.speed * 1.5;
            controlled = true;
        }

        // 4) Mouse follow (laptop fallback)
        if (!controlled && this.input.mouse.active) {
            const worldMouseX = this.input.mouse.x + this.camera.x;
            const worldMouseY = this.input.mouse.y + this.camera.y;
            const dx = worldMouseX - this.player.x;
            const dy = worldMouseY - this.player.y;
            const dist = Math.sqrt(dx * dx + dy * dy);

            if (dist > 30) {
                const factor = Math.min(dist / 100, 1);
                this.player.vx = (dx / dist) * this.player.speed * factor;
                this.player.vy = (dy / dist) * this.player.speed * factor;
            }
        }

        this.player.update(WORLD_WIDTH, WORLD_HEIGHT);
        this.player.updatePowerUps();
        // Apply pending equipment wear from combat
        if (this.player._pendingWear && this.inventory) {
            const wear = this.inventory.wearRandomPart(this.player._pendingWear);
            this.player._pendingWear = 0;
            if (wear?.broke) {
                applyLoadoutToPlayer(this.player, this.inventory);
                this.floatingTexts.push({
                    x: this.player.x,
                    y: this.player.y - 48,
                    text: `${wear.item.name} BROKEN!`,
                    color: '#ff4466',
                    age: 0,
                    duration: 90,
                    pulse: true,
                });
            }
        }
    }

    getShootRange() {
        return SHOOT_RANGE + (this.player?.shootRangeBonus || 0);
    }

    getProjectileRange() {
        return PROJECTILE_MAX_DIST + (this.player?.shootRangeBonus || 0) * 0.5;
    }

    autoShoot() {
        this.player.autoShootCooldown--;
        if (this.player.autoShootCooldown > 0) return;

        let dx = 0, dy = 0, haveTarget = false;
        const maxRange = this.getShootRange();
        const projRange = this.getProjectileRange();

        // Twin-stick: fire in aim direction (still range-limited by projectile)
        if (this.input.aim.active) {
            dx = this.input.aim.x;
            dy = this.input.aim.y;
            haveTarget = true;
        } else if (this.enemies.length > 0) {
            const nearest = this.findNearestEnemy(maxRange);
            if (nearest) {
                dx = nearest.x - this.player.x;
                dy = nearest.y - this.player.y;
                haveTarget = true;
            }
        }

        if (!haveTarget) return;

        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        // Auto-aim must be within shoot range (visible engagement)
        if (!this.input.aim.active && dist > maxRange) return;

        const speed = 9;
        const multishot = this.player.hasPowerUp('multishot');
        const angles = multishot ? [-0.22, 0, 0.22] : [0];
        const baseAng = Math.atan2(dy, dx);

        for (const off of angles) {
            const a = baseAng + off;
            this.projectiles.push(this.projectilePool.get(
                this.player.x,
                this.player.y,
                Math.cos(a) * speed,
                Math.sin(a) * speed,
                this.player.damage,
                'normal',
                projRange
            ));
        }

        this.audio.playShoot();
        const cd = this.input.aim.active
            ? Math.max(6, this.player.autoShootMaxCooldown * 0.65)
            : this.player.autoShootMaxCooldown;
        this.player.autoShootCooldown = cd;
    }

    findNearestEnemy(maxRange = Infinity) {
        let nearest = null;
        let minDist = Infinity;
        const pr = maxRange;

        this.enemies.forEach(enemy => {
            const dx = enemy.x - this.player.x;
            const dy = enemy.y - this.player.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < minDist && dist <= pr) {
                minDist = dist;
                nearest = enemy;
            }
        });

        return nearest;
    }

    updateWeapons() {
        this.player.weapons.forEach(weapon => {
            if (weapon.type === 'orbit') {
                weapon.angle += 0.05;

                for (let i = 0; i < weapon.count; i++) {
                    const angle = weapon.angle + (i * (Math.PI * 2 / weapon.count));
                    const sx = this.player.x + Math.cos(angle) * weapon.radius;
                    const sy = this.player.y + Math.sin(angle) * weapon.radius;

                    this.enemies.forEach((enemy, idx) => {
                        const dx = enemy.x - sx;
                        const dy = enemy.y - sy;
                        const dist = Math.sqrt(dx * dx + dy * dy);

                        if (dist < enemy.radius + 10) {
                            if (enemy.takeDamage(weapon.damage * this.player.damage / 10)) {
                                this.handleEnemyDeath(enemy, idx);
                            }
                        }
                    });
                }
            } else {
                weapon.cooldown--;
                if (weapon.cooldown <= 0 && this.enemies.length > 0) {
                    this.fireWeapon(weapon);
                    weapon.cooldown = weapon.maxCooldown;
                }
            }
        });

        // Draw orbital shields
        this.player.weapons.forEach(weapon => {
            if (weapon.type === 'orbit') {
                for (let i = 0; i < weapon.count; i++) {
                    const angle = weapon.angle + (i * (Math.PI * 2 / weapon.count));
                    const sx = this.player.x + Math.cos(angle) * weapon.radius;
                    const sy = this.player.y + Math.sin(angle) * weapon.radius;

                    this.ctx.save();
                    this.ctx.fillStyle = '#00ff88';
                    this.ctx.shadowColor = '#00ff88';
                    this.ctx.shadowBlur = 15;
                    this.ctx.beginPath();
                    this.ctx.arc(sx, sy, 10, 0, Math.PI * 2);
                    this.ctx.fill();
                    this.ctx.restore();
                }
            }
        });
    }

    fireWeapon(weapon) {
        const nearest = this.findNearestEnemy(this.getShootRange());
        if (!nearest) return;

        const dx = nearest.x - this.player.x;
        const dy = nearest.y - this.player.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const projRange = this.getProjectileRange();

        if (weapon.type === 'laser') {
            const speed = 8;
            this.projectiles.push(this.projectilePool.get(
                this.player.x,
                this.player.y,
                (dx / dist) * speed,
                (dy / dist) * speed,
                weapon.damage * this.player.damage / 10,
                'laser',
                projRange
            ));
        } else if (weapon.type === 'shotgun') {
            const angle = Math.atan2(dy, dx);
            const speed = 7;

            for (let i = -2; i <= 2; i++) {
                const spreadAngle = angle + (i * 0.15);
                this.projectiles.push(this.projectilePool.get(
                    this.player.x,
                    this.player.y,
                    Math.cos(spreadAngle) * speed,
                    Math.sin(spreadAngle) * speed,
                    weapon.damage * this.player.damage / 10,
                    'shotgun',
                    projRange
                ));
            }
        } else if (weapon.type === 'chain') {
            this.chainLightning(nearest, weapon);
        } else if (weapon.type === 'boomerang') {
            const angle = Math.atan2(dy, dx);
            const count = weapon.count || 2;

            for (let i = 0; i < count; i++) {
                const offsetAngle = angle + (i * Math.PI * 2 / count);
                const speed = 6;
                this.projectiles.push(this.projectilePool.get(
                    this.player.x,
                    this.player.y,
                    Math.cos(offsetAngle) * speed,
                    Math.sin(offsetAngle) * speed,
                    weapon.damage * this.player.damage / 10,
                    'boomerang',
                    weapon.range || 200
                ));
            }
        }
    }

    chainLightning(startEnemy, weapon) {
        const chained = [startEnemy];
        let current = startEnemy;

        for (let i = 0; i < 2; i++) {
            let next = null;
            let minDist = Infinity;

            this.enemies.forEach(enemy => {
                if (chained.includes(enemy)) return;
                const dx = enemy.x - current.x;
                const dy = enemy.y - current.y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                if (dist < minDist && dist < 200) {
                    minDist = dist;
                    next = enemy;
                }
            });

            if (next) {
                chained.push(next);
                current = next;
            }
        }

        chained.forEach(enemy => {
            if (enemy.takeDamage(weapon.damage * this.player.damage / 10)) {
                const idx = this.enemies.indexOf(enemy);
                if (idx > -1) {
                    this.handleEnemyDeath(enemy, idx);
                }
            }
        });
    }

    spawnEnemy() {
        // Spawn just off the visible viewport edges
        const pad = 60;
        const vx = this.camera.x, vy = this.camera.y;
        const vw = this.canvas.width, vh = this.canvas.height;
        const side = Math.floor(Math.random() * 4);
        let x, y;
        switch (side) {
            case 0: x = vx + Math.random() * vw; y = vy - pad; break;
            case 1: x = vx + vw + pad; y = vy + Math.random() * vh; break;
            case 2: x = vx + Math.random() * vw; y = vy + vh + pad; break;
            case 3: x = vx - pad; y = vy + Math.random() * vh; break;
        }
        x = Math.max(-pad, Math.min(WORLD_WIDTH + pad, x));
        y = Math.max(-pad, Math.min(WORLD_HEIGHT + pad, y));
        this.enemies.push(this.enemyPool.get(x, y, this._enemyTheme()));
    }

    spawnBoss() {
        const pad = 80;
        const vx = this.camera.x, vy = this.camera.y;
        const vw = this.canvas.width, vh = this.canvas.height;
        const side = Math.floor(Math.random() * 4);
        let x, y;
        switch (side) {
            case 0: x = vx + vw / 2; y = vy - pad; break;
            case 1: x = vx + vw + pad; y = vy + vh / 2; break;
            case 2: x = vx + vw / 2; y = vy + vh + pad; break;
            case 3: x = vx - pad; y = vy + vh / 2; break;
        }
        x = Math.max(-pad, Math.min(WORLD_WIDTH + pad, x));
        y = Math.max(-pad, Math.min(WORLD_HEIGHT + pad, y));
        this.enemies.push(this.bossPool.get(x, y, this._enemyTheme()));
        this.telegram.hapticFeedback('error');
    }

    /** Current planet faction + danger for enemy pool resets */
    _enemyTheme() {
        const faction = this._planetFaction || getFaction(this.currentBackground?.type);
        return {
            faction,
            danger: this._planetDanger ?? 0.5,
            type: this.currentBackground?.type,
        };
    }

    spawnPowerUp(x, y) {
        this.powerUps.push(new PowerUp(x, y));
    }

    spawnGeoBomb(x = null, y = null) {
        if (this.geoBombs.length >= 12) return;
        const margin = 180;
        const bx = x ?? (margin + Math.random() * (WORLD_WIDTH - margin * 2));
        const by = y ?? (margin + Math.random() * (WORLD_HEIGHT - margin * 2));
        // Keep away from player spawn / current player
        if (this.player) {
            const d = Math.hypot(bx - this.player.x, by - this.player.y);
            if (d < 160 && x === null) {
                // Retry once with far offset
                const angle = Math.random() * Math.PI * 2;
                const nx = this.player.x + Math.cos(angle) * 320;
                const ny = this.player.y + Math.sin(angle) * 320;
                this.geoBombs.push(new GeoBomb(
                    Math.max(margin, Math.min(WORLD_WIDTH - margin, nx)),
                    Math.max(margin, Math.min(WORLD_HEIGHT - margin, ny))
                ));
                return;
            }
        }
        this.geoBombs.push(new GeoBomb(bx, by));
    }

    spawnElectricGate() {
        // Legacy — prefer GeoBombs. Keep a few gates for variety.
        const vertical = Math.random() > 0.5;
        const margin = 200;
        const x = margin + Math.random() * (WORLD_WIDTH - margin * 2);
        const y = margin + Math.random() * (WORLD_HEIGHT - margin * 2);
        this.electricGates.push(new ElectricGate(x, y, vertical));
    }

    spawnPortal() {
        if (this.arenaMode) return;
        const worldBounds = { width: WORLD_WIDTH, height: WORLD_HEIGHT };
        const portal = Portal.createRandomPair(worldBounds, {
            voidChance: 0.34,
            hue: 270 + Math.floor(Math.random() * 40),
        });
        this.portals.push(portal);
    }

    /**
     * Pull player into a void pocket with one hard boss + escape black hole.
     */
    enterVoidArena(fromX, fromY) {
        if (this.arenaMode) return;

        // Snapshot open-world combat state
        const stash = {
            returnX: fromX,
            returnY: fromY,
            enemies: this.enemies.slice(),
            portals: this.portals.slice(),
            projectiles: this.projectiles.slice(),
            geoBombs: this.geoBombs.slice(),
            powerUps: this.powerUps.slice(),
            waveActive: this.waveActive,
            waveEnemiesLeft: this.waveEnemiesLeft,
            waveSpawnTimer: this.waveSpawnTimer,
            wavePauseTimer: this.wavePauseTimer,
            background: this.currentBackground ? { ...this.currentBackground } : null,
            atmosphere: this._atmosphere,
        };

        // Detach live entities from main lists (restored on exit)
        this.enemies = [];
        this.portals = [];
        this.projectiles = [];
        this.geoBombs = [];
        this.powerUps = [];
        this.waveActive = false;
        this.waveEnemiesLeft = 0;

        // Arena layout: player at center, boss offset, escape rift far edge
        const ax = WORLD_WIDTH / 2;
        const ay = WORLD_HEIGHT / 2;
        this.player.x = ax;
        this.player.y = ay + 180;
        this.player.vx = 0;
        this.player.vy = 0;
        this.camera.x = this.player.x - this.canvas.width / 2;
        this.camera.y = this.player.y - this.canvas.height / 2;

        // Hard boss
        const boss = this.bossPool.get(ax, ay - 120, {
            faction: this._planetFaction || getFaction(this.cosmos?.getPlanet?.()),
            danger: Math.min(1, (this._planetDanger || 0.5) + 0.35),
        });
        // Supercharge arena boss
        boss.hp = Math.round(boss.hp * 1.65);
        boss.maxHp = boss.hp;
        boss.damage = Math.round(boss.damage * 1.25);
        boss.speed = Math.max(0.55, boss.speed * 0.95);
        boss.radius = Math.max(boss.radius, 48);
        boss.shootInterval = Math.max(40, Math.floor((boss.shootInterval || 70) * 0.75));
        boss.shootRange = 480;
        boss._arenaBoss = true;
        this.enemies.push(boss);

        // Escape black hole
        const exit = Portal.createArenaExit(ax, ay + 320);
        this.portals.push(exit);

        this.arenaMode = {
            stash,
            bossAlive: true,
            enteredAt: Date.now(),
            killedBoss: false,
        };
        this.trackProgress({ voidEntries: 1 });

        // Void sky
        this.currentBackground = {
            id: 'void-arena',
            name: 'Void Arena',
            color: '#050208',
            accent: '#2a0840',
            starColor: '#e0b0ff',
            mist: 0.55,
            mistColor: '80,20,120',
            flare: true,
            flareColor: '#cc66ff',
            type: 'storm',
        };
        this._atmosphere = {
            bg: '#050208',
            accent: '#2a0840',
            starColor: '#e0b0ff',
            mist: 0.55,
            mistColor: '80,20,120',
            starCount: 220,
            mistSeed: Math.random() * 1000,
            flare: true,
            flareColor: '#cc66ff',
        };
        this._ensureStars?.(this._atmosphere, true);
        if (this.canvas) this.canvas.style.background = '#050208';

        // Teleport VFX
        this.warpGrid.pulse(this.player.x, this.player.y, 'boss');
        this.addShake(10);
        this.bombFlash = 8;
        this.createParticles(this.player.x, this.player.y, '#cc66ff', 24, 'spark');
        this.createParticles(this.player.x, this.player.y, '#9900ff', 2, 'ring', { radius: 20, growth: 10, life: 32 });

        this.floatingTexts.push({
            x: this.player.x,
            y: this.player.y - 70,
            text: 'VOID ARENA',
            color: '#dd88ff',
            age: 0,
            duration: 140,
            pulse: true,
        });
        this.floatingTexts.push({
            x: this.player.x,
            y: this.player.y - 40,
            text: 'Defeat the boss · or escape via the rift',
            color: '#aa77cc',
            age: 0,
            duration: 160,
            pulse: false,
        });
        this.audio.playPortal?.();
        this.telegram.hapticFeedback('error');
    }

    /** Leave void arena — restore open-world combat snapshot */
    exitVoidArena() {
        if (!this.arenaMode) return;
        const { stash } = this.arenaMode;
        if (!this.arenaMode.killedBoss) {
            this.trackProgress({ voidEscapes: 1 });
        }

        // Release arena entities
        this.enemies.forEach(e => {
            if (e.isBoss) this.bossPool.release(e);
            else this.enemyPool.release(e);
        });
        this.projectilePool.releaseAll(this.projectiles);
        this.enemies = stash.enemies || [];
        this.portals = stash.portals || [];
        this.projectiles = stash.projectiles || [];
        this.geoBombs = stash.geoBombs || [];
        this.powerUps = stash.powerUps || [];
        this.waveActive = stash.waveActive;
        this.waveEnemiesLeft = stash.waveEnemiesLeft;
        this.waveSpawnTimer = stash.waveSpawnTimer;
        this.wavePauseTimer = stash.wavePauseTimer;

        this.player.x = stash.returnX;
        this.player.y = stash.returnY;
        this.player.vx = 0;
        this.player.vy = 0;
        this.camera.x = this.player.x - this.canvas.width / 2;
        this.camera.y = this.player.y - this.canvas.height / 2;

        if (stash.background) this.currentBackground = stash.background;
        if (stash.atmosphere) {
            this._atmosphere = stash.atmosphere;
            this._ensureStars?.(stash.atmosphere, true);
            if (this.canvas) this.canvas.style.background = stash.atmosphere.bg || '#03040a';
        } else {
            const planet = this.cosmos?.getPlanet?.();
            if (planet) this.applyPlanetEnvironment(planet);
        }

        this.arenaMode = null;

        this.warpGrid.pulse(this.player.x, this.player.y, 'kill');
        this.addShake(6);
        this.createParticles(this.player.x, this.player.y, '#cc88ff', 18, 'spark');
        this.floatingTexts.push({
            x: this.player.x,
            y: this.player.y - 50,
            text: 'Returned to open space',
            color: '#c9a0ff',
            age: 0,
            duration: 100,
            pulse: true,
        });
        this.audio.playPortal?.();
        this.telegram.hapticFeedback('impact');
    }

    /** Visual punch when stepping through a black hole */
    _portalTransitFx(x, y, color = '#b44dff') {
        this.warpGrid.pulse(x, y, 'kill');
        this.addShake(5);
        this.bombFlash = Math.max(this.bombFlash, 5);
        this.createParticles(x, y, color, 16, 'spark');
        this.createParticles(x, y, '#ffffff', 1, 'ring', { radius: 12, growth: 7, life: 22 });
        this.createParticles(x, y, color, 2, 'glow', { radius: 40, life: 14 });
    }

    spawnBot() {
        const margin = 50;
        const x = margin + Math.random() * (WORLD_WIDTH - margin * 2);
        const y = margin + Math.random() * (WORLD_HEIGHT - margin * 2);
        const name = Bot.generateBotNames();
        const faction = this._planetFaction || getFaction(this.currentBackground?.type);
        const color = Bot.generateBotColor(faction);
        const bot = new Bot(x, y, name, color, faction.shipStyle, faction.playerSecondary);
        bot.applyFactionTheme(faction, color);
        this.bots.push(bot);
    }

    handlePowerUpCollection(powerUp) {
        switch (powerUp.type.id) {
            case 'bomb':
                // Power-up bomb = strong radial detonation at player (not full wipe)
                this.detonateRadiusBomb(this.player.x, this.player.y, 360, {
                    bossDamage: 140,
                    color: '#ff4488',
                });
                break;
            case 'clone2':
                this.spawnClones(2);
                break;
            case 'clone3':
                this.spawnClones(3);
                break;
            case 'freeze':
                this.activateFreeze(powerUp.type.duration);
                break;
            case 'supermagnet':
                this.activateSuperMagnet(powerUp.type.duration);
                break;
            default:
                this.player.applyPowerUp(powerUp);
        }
    }

    activateBomb() {
        // Full-field super bomb (kept for rare use)
        const px = this.player?.x ?? 0;
        const py = this.player?.y ?? 0;
        this.detonateRadiusBomb(px, py, 99999, { bossDamage: 9999, color: '#ff4488' });
        this.bombFlash = 18;
    }

    spawnClones(count) {
        this.trackProgress({ clonesSpawned: 1 });
        // Remove existing clones before adding new ones
        this.clones = [];
        for (let i = 0; i < count; i++) {
            const angle = (i / count) * Math.PI * 2;
            this.clones.push(new PlayerClone(this.player, angle));
        }
    }

    activateFreeze(duration) {
        const until = Date.now() + duration;
        this.enemies.forEach(enemy => {
            enemy.frozenUntil = until;
        });
    }

    activateSuperMagnet(duration) {
        const prev = this.player.baseMagnetRange;
        this.player.baseMagnetRange = 900;
        this.player.magnetRange = 900;
        setTimeout(() => {
            this.player.baseMagnetRange = prev;
            this.player.magnetRange = prev;
        }, duration);
    }

    spawnSpeechBubble(x, y, text) {
        this.speechBubbles.push({ x, y, text, alpha: 1.0, age: 0, duration: 180 });
    }

    getRandomBossLastWords() {
        const lines = [
            "I demand a rematch!",
            "My mother warned me about you.",
            "I was THIS close to retiring.",
            "Tell my minions I said hi.",
            "Worth it.",
            "Curse your tiny bullets!",
            "I had a dentist appointment tomorrow...",
            "You call THAT a finishing move?",
            "I regret nothing. Wait—everything.",
            "That tickled. Ow.",
            "My therapist was right.",
            "I should've worn a helmet.",
            "I knew I should've called in sick.",
            "Wait, I can explain—",
            "My evil plan was almost perfect!",
            "Respawn in 3... 2... nope.",
            "I'm not dead, I'm resting.",
            "Fine, you win this round.",
            "My lawyer will hear about this!",
            "I just wanted a hug.",
            "Plot twist: I'm the good guy.",
            "I have a family, you know.",
            "Unsubscribe from this.",
            "Okay okay, I give up. Mostly.",
            "My evil lair has a no-dying policy.",
            "Technically, I'm still winning.",
            "404: Victory not found.",
            "Should've eaten breakfast.",
            "I blame the algorithm.",
            "That was my final form. Honest.",
            "But my Yelp reviews were so good!",
            "Is this the tutorial?",
            "I was buffed last patch, I swear.",
            "I need better henchmen.",
            "Skill issue.",
            "GG no re.",
            "Do you even lift, player?",
            "I'll be back. Probably not.",
            "You haven't seen the last of me!",
        ];
        return lines[Math.floor(Math.random() * lines.length)];
    }

    /**
     * @param {object} [opts]
     * @param {boolean} [opts.skipVfx] skip death particles
     * @param {boolean} [opts.skipWarp] skip grid pulse
     * @param {boolean} [opts.skipShake]
     * @param {boolean} [opts.skipAudio]
     * @param {boolean} [opts.quietDrops] fewer floats / lower drop spam (bombs)
     */
    handleEnemyDeath(enemy, idx, opts = {}) {
        this.enemies.splice(idx, 1);
        // Return enemy to the appropriate pool
        if (enemy.isBoss) {
            this.bossPool.release(enemy);
        } else {
            this.enemyPool.release(enemy);
        }
        this.kills++;

        // Career progression + badges
        const vKey = enemy.variant || (enemy.isBoss ? 'boss' : 'swarm');
        this.trackProgress({
            kills: 1,
            bossKills: enemy.isBoss ? 1 : 0,
            killsByVariant: { [vKey]: 1 },
            setPilotLevel: this.player?.level,
        });

        if (enemy.isBoss && !opts.quietDrops) {
            this.spawnSpeechBubble(enemy.x, enemy.y, this.getRandomBossLastWords());
        }
        if (enemy._arenaBoss) {
            this.trackProgress({ voidBossKills: 1 });
        }

        const xpGain = enemy.isBoss ? 5 : 1;
        if (this.player.addXP(xpGain)) {
            this.player.levelUp();
            this.trackProgress({ setPilotLevel: this.player.level });
            this.audio.playLevelUp();
            this.showAbilityPopup();
        }

        if (!opts.skipAudio) this.audio.playExplosion();
        if (!opts.skipVfx) this.spawnDeathVfx(enemy.x, enemy.y, enemy.color, enemy.isBoss);
        if (!opts.skipWarp) this.warpGrid.pulse(enemy.x, enemy.y, enemy.isBoss ? 'boss' : 'kill');
        if (!opts.skipShake) this.addShake(enemy.isBoss ? VFX.BOSS_DEATH_SHAKE : VFX.DEATH_SHAKE);

        const coinCount = enemy.isBoss ? 5 : 1;
        // Bomb batch: one coin max to avoid flooding the coin list
        const coinsToSpawn = opts.quietDrops ? (enemy.isBoss ? 2 : (Math.random() < 0.35 ? 1 : 0)) : coinCount;
        for (let i = 0; i < coinsToSpawn; i++) {
            this.coins.push(this.coinPool.get(enemy.x, enemy.y));
        }

        if (!opts.quietDrops) {
            // Short pops only — crystals themselves are the loot
            if (enemy.isBoss) {
                this.floatingTexts.push({
                    x: enemy.x,
                    y: enemy.y - 28,
                    text: 'BOSS SLAIN',
                    color: '#ff66aa',
                    age: 0,
                    duration: 90,
                    pulse: true,
                });
            }
        }

        // Drop green collectibles
        if (!opts.quietDrops) {
            const collectibleCount = enemy.isBoss ? 2 + Math.floor(Math.random() * 2) : (Math.random() < 0.5 ? 1 : 0);
            for (let i = 0; i < collectibleCount; i++) {
                this.collectibles.push(this.collectiblePool.get(enemy.x, enemy.y));
            }
        } else if (enemy.isBoss || Math.random() < 0.12) {
            this.collectibles.push(this.collectiblePool.get(enemy.x, enemy.y));
        }

        // Random power-up drop (5% chance, 20% for bosses) — reduced during bomb batch
        const dropChance = opts.quietDrops
            ? (enemy.isBoss ? 0.12 : 0.02)
            : (enemy.isBoss ? 0.2 : 0.05);
        if (Math.random() < dropChance) {
            this.spawnPowerUp(enemy.x, enemy.y);
        }

        // Geometry Wars bomb geode drops (almost never from bomb-chain kills)
        const bombChance = opts.quietDrops
            ? (enemy.isBoss ? 0.15 : 0.02)
            : (enemy.isBoss ? 0.45 : 0.12);
        if (Math.random() < bombChance) {
            this.spawnGeoBomb(
                enemy.x + (Math.random() - 0.5) * 40,
                enemy.y + (Math.random() - 0.5) * 40
            );
        }

        // Modules, trade goods, or combat artefacts
        const lootChance = opts.quietDrops
            ? (enemy.isBoss ? 0.35 : 0.04)
            : (enemy.isBoss ? 0.7 : 0.12);
        if (Math.random() < lootChance) {
            const loot = generateCombatDrop(!!enemy.isBoss) || generateLoot(enemy.isBoss ? 0.65 : 0.35);
            if (loot && this.inventory.add(loot)) {
                if (loot.artefact) this.trackProgress({ artefactsFound: 1 });
                const tag = loot.artefact ? '✦ ' : '';
                this.floatingTexts.push({
                    x: enemy.x,
                    y: enemy.y - 40,
                    text: `${tag}${loot.name}`,
                    color: RARITY_COLOR[loot.rarity] || '#88ccff',
                    age: 0,
                    duration: loot.artefact ? 100 : 70,
                    pulse: !!loot.artefact,
                });
            }
        }

        // Check for level completion
        if (this.kills >= this.levelKillTarget) {
            this.trackProgress({ sectorsCleared: 1 });
            this.showLevelCompletePopup();
        }

        this.telegram.hapticFeedback('success');
    }

    createParticles(x, y, color, count, kind = 'spark', opts = {}) {
        const maxP = this.perf ? this.perf.maxParticles() : VFX.MAX_PARTICLES;
        // Scale particle count down on lower quality
        let n = count;
        if (this.perf?.quality === 'medium') n = Math.ceil(count * 0.65);
        if (this.perf?.quality === 'low') n = Math.ceil(count * 0.4);
        const room = maxP - this.particles.length;
        n = Math.min(n, Math.max(0, room));
        for (let i = 0; i < n; i++) {
            this.particles.push(this.particlePool.get(x, y, color, kind, opts));
        }
    }

    /** True if world point is near the camera view (with margin). */
    inView(x, y, margin = 80) {
        const cam = this.camera;
        return x >= cam.x - margin && x <= cam.x + this.canvas.width + margin
            && y >= cam.y - margin && y <= cam.y + this.canvas.height + margin;
    }

    spawnDeathVfx(x, y, color, isBoss = false) {
        const burst = isBoss ? 28 : 14;
        this.createParticles(x, y, color, burst, 'spark');
        this.createParticles(x, y, color, isBoss ? 10 : 5, 'debris');
        this.createParticles(x, y, color, isBoss ? 6 : 3, 'smoke', { radius: isBoss ? 16 : 10 });
        this.createParticles(x, y, color, 1, 'ring', { radius: isBoss ? 12 : 6, growth: isBoss ? 3.2 : 2.2, life: 24 });
        this.createParticles(x, y, '#ffffff', 1, 'glow', { radius: isBoss ? 36 : 18, life: 16 });
    }

    addShake(amount) {
        this.shake = Math.min(22, this.shake + amount);
    }

    /**
     * Build starfield + mist clouds for a planet atmosphere.
     * @param {object|null} atmosphere from planet.atmosphere
     * @param {boolean} force rebuild even if stars exist
     */
    _ensureStars(atmosphere = null, force = false) {
        if (this.stars && !force) return;
        const atmo = atmosphere || this._atmosphere || {};
        const count = Math.min(260, Math.max(40, atmo.starCount || 100));
        this.stars = [];
        for (let i = 0; i < count; i++) {
            const bright = Math.random() < 0.12;
            this.stars.push({
                x: Math.random() * WORLD_WIDTH,
                y: Math.random() * WORLD_HEIGHT,
                r: bright ? 1.4 + Math.random() * 1.6 : 0.35 + Math.random() * 1.1,
                a: bright ? 0.75 + Math.random() * 0.25 : 0.25 + Math.random() * 0.55,
                tw: Math.random() * Math.PI * 2,
                // occasional colored star
                tint: Math.random() < 0.18 ? 1 : 0,
            });
        }

        // Soft mist / nebula blobs — more when planet has mist
        const mist = atmo.mist ?? 0.25;
        const mistCount = Math.round(6 + mist * 16);
        this.nebulae = [];
        for (let i = 0; i < mistCount; i++) {
            this.nebulae.push({
                x: Math.random() * WORLD_WIDTH,
                y: Math.random() * WORLD_HEIGHT,
                r: 200 + Math.random() * 520,
                a: 0.05 + mist * 0.14 + Math.random() * 0.07,
                phase: Math.random() * Math.PI * 2,
                drift: 0.06 + Math.random() * 0.22,
                stretch: 0.55 + Math.random() * 0.7,
            });
        }
        this._atmosphere = atmo;
    }

    showAbilityPopup() {
        this.closeAllPopups();
        this.paused = true;
        const popup = document.getElementById('abilityPopup');
        const choicesDiv = document.getElementById('abilityChoices');
        choicesDiv.innerHTML = '';

        const choices = getRandomAbilities(3);

        choices.forEach(ability => {
            const div = document.createElement('div');
            div.className = 'ability-choice';
            div.innerHTML = `
                <h3>${ability.name}</h3>
                <p>${ability.description}</p>
            `;
            div.onclick = () => {
                this._pickAbility(ability);
                popup.style.display = 'none';
                this.paused = false;
                this.telegram.hapticFeedback('impact');
            };
            choicesDiv.appendChild(div);
        });

        popup.style.display = 'block';
    }

    showCollectibleAbilityPopup() {
        this.closeAllPopups();
        this.paused = true;
        const popup = document.getElementById('abilityPopup');
        const choicesDiv = document.getElementById('abilityChoices');
        choicesDiv.innerHTML = '';

        // Filter to weapon/offensive abilities
        const weaponAbilities = abilities.filter(a =>
            ['orbit', 'chain', 'boomerang', 'laser', 'shotgun'].includes(a.id)
        );
        const choices = weaponAbilities.sort(() => Math.random() - 0.5).slice(0, 3);

        choices.forEach(ability => {
            const div = document.createElement('div');
            div.className = 'ability-choice';
            div.innerHTML = `
                <h3>${ability.name}</h3>
                <p>${ability.description}</p>
            `;
            div.onclick = () => {
                this._pickAbility(ability);
                popup.style.display = 'none';
                this.paused = false;
                this.telegram.hapticFeedback('impact');
            };
            choicesDiv.appendChild(div);
        });

        popup.style.display = 'block';
    }

    /** Apply ability and record it for hangar Abilities tab */
    _pickAbility(ability) {
        if (!ability || !this.player) return;
        ability.apply(this.player);
        if (!this._abilityLog) this._abilityLog = [];
        this._abilityLog.push({
            id: ability.id,
            name: ability.name,
            description: ability.description,
            at: Date.now(),
            level: this.player.level || 1,
        });
        if (ability.id) this.trackProgress({ abilities: { [ability.id]: true } });
    }

    showSkinShop() {
        this.closeAllPopups();
        this.paused = true;
        const popup = document.getElementById('skinShop');
        const skinGrid = document.getElementById('skinGrid');
        skinGrid.innerHTML = '';

        const allSkins = this.skinManager.getAllSkins();

        allSkins.forEach(skin => {
            const div = document.createElement('div');
            div.className = 'skin-item';
            if (skin.owned) div.classList.add('owned');
            if (skin.selected) div.classList.add('selected');

            const preview = document.createElement('div');
            preview.className = 'skin-preview';
            preview.style.backgroundColor = skin.color;

            div.appendChild(preview);

            const name = document.createElement('div');
            name.className = 'skin-name';
            name.textContent = skin.name;
            div.appendChild(name);

            if (!skin.owned) {
                const price = document.createElement('div');
                price.className = 'skin-price';
                // Get formatted price from IAP manager (handles Telegram Stars, IAP, or default)
                this.iap.getFormattedPrice(skin.id).then(priceText => {
                    price.textContent = priceText;
                });
                div.appendChild(price);

                div.onclick = () => {
                    // Use IAP manager for purchase (handles Telegram Stars, native IAP, or web)
                    this.iap.purchaseSkin(
                        skin.id,
                        () => {
                            this.skinManager.selectSkin(skin.id);
                            this.player.skin = this.skinManager.getCurrentSkin();
                            this.showSkinShop(); // Refresh
                            this.telegram.showAlert('Skin purchased!');
                            this.telegram.hapticFeedback('success');
                        },
                        (error) => {
                            this.telegram.showAlert(error || 'Purchase failed');
                            this.telegram.hapticFeedback('error');
                        }
                    );
                };
            } else {
                div.onclick = () => {
                    this.skinManager.selectSkin(skin.id);
                    this.player.skin = this.skinManager.getCurrentSkin();
                    this.showSkinShop(); // Refresh
                    this.telegram.hapticFeedback('impact');
                };
            }

            skinGrid.appendChild(div);
        });

        // Remove Ads item
        this._appendRemoveAdsItem(skinGrid);

        popup.style.display = 'block';
    }

    _appendRemoveAdsItem(container) {
        const div = document.createElement('div');
        div.className = 'skin-item';

        const preview = document.createElement('div');
        preview.className = 'skin-preview';
        preview.style.fontSize = '36px';
        preview.style.display = 'flex';
        preview.style.alignItems = 'center';
        preview.style.justifyContent = 'center';
        preview.textContent = 'NO ADS';
        preview.style.color = '#ffd700';
        preview.style.fontWeight = 'bold';
        preview.style.fontSize = '14px';
        div.appendChild(preview);

        const name = document.createElement('div');
        name.className = 'skin-name';
        name.textContent = 'Remove Ads';
        div.appendChild(name);

        const priceEl = document.createElement('div');
        priceEl.className = 'skin-price';
        priceEl.textContent = '$1.99';
        div.appendChild(priceEl);

        this.iap.hasRemovedAds().then(removed => {
            if (removed) {
                div.classList.add('owned');
                priceEl.textContent = 'Active';
                priceEl.style.color = '#00ff88';
            } else {
                div.onclick = () => {
                    this.iap.purchaseRemoveAds(
                        () => {
                            div.classList.add('owned');
                            priceEl.textContent = 'Active';
                            priceEl.style.color = '#00ff88';
                            div.onclick = null;
                            this.telegram.showAlert('Ads removed! Thank you for your support.');
                            this.telegram.hapticFeedback('success');
                        },
                        (err) => {
                            if (err !== 'User cancelled') {
                                this.telegram.showAlert(err || 'Purchase failed');
                            }
                            this.telegram.hapticFeedback('error');
                        }
                    );
                };
            }
        });

        container.appendChild(div);
    }

    async showLeaderboard() {
        this.closeAllPopups();
        this.paused = true;

        // Use native Game Center / Google Play Games UI when available
        const nativeOpened = await this.leaderboard.showNative();
        if (nativeOpened) {
            this.paused = false;
            return;
        }

        // Fallback: in-game leaderboard popup
        const popup = document.getElementById('leaderboardPopup');
        const list = document.getElementById('leaderboardList');
        list.innerHTML = '';

        const topScores = this.leaderboard.getTopScores(10);

        topScores.forEach((score, idx) => {
            const div = document.createElement('div');
            div.className = 'leaderboard-item';

            div.innerHTML = `
                <div class="leaderboard-rank">#${idx + 1}</div>
                <div class="leaderboard-name">${score.name}</div>
                <div class="leaderboard-score">${score.score}</div>
            `;

            list.appendChild(div);
        });

        popup.style.display = 'block';
    }

    showSettings() {
        this.closeAllPopups();
        this.paused = true;
        const popup = document.getElementById('settingsPopup');

        // Sync current name into input each time settings opens
        const nameInput = document.getElementById('playerNameInput');
        if (nameInput) nameInput.value = this.telegram.getLocalPlayerName();
        const msg = document.getElementById('nameChangeMsg');
        if (msg) msg.textContent = '';

        // Sync audio toggles
        const musicToggle = document.getElementById('musicToggle');
        if (musicToggle) musicToggle.checked = this.audio.musicEnabled;
        const effectsToggle = document.getElementById('effectsToggle');
        if (effectsToggle) effectsToggle.checked = this.audio.effectsEnabled;

        popup.style.display = 'block';
        // Reset scroll to top when opening
        popup.querySelector('.popup-body')?.scrollTo?.({ top: 0 });
    }

    isBossLevel() {
        return this.currentLevel % 5 === 0;
    }

    startNextLevel() {
        // Reset wave state — start fresh each level
        this.kills = 0;
        this.frame = 0;
        this.lastSpawn = 0;
        this.spawnRate = 120;
        this.lastBossSpawn = Date.now();
        this.currentWave     = 0;
        this.waveActive      = false;
        this.waveEnemiesLeft = 0;
        this.waveSpawnTimer  = 0;
        this.wavePauseTimer  = 0;
        this.enemyPool.releaseAll(this.enemies.filter(e => !e.isBoss));
        this.bossPool.releaseAll(this.enemies.filter(e => e.isBoss));
        this.projectilePool.releaseAll(this.projectiles);
        this.enemies = [];
        this.projectiles = [];
        this.floatingTexts = [];
        this.speechBubbles = [];

        if (this.isBossLevel()) {
            // Boss level: fewer normal enemies, lots of bosses, short kill target
            this.levelKillTarget = 5 + Math.floor(this.currentLevel / 5);
        } else {
            // Normal level: longer and longer
            this.levelKillTarget = 100 + (this.currentLevel - 1) * 30;
        }
    }

    showLevelCompletePopup() {
        this.closeAllPopups();
        this.paused = true;
        const popup = document.getElementById('levelCompletePopup');
        document.getElementById('completedLevel').textContent = this.currentLevel;
        document.getElementById('levelKills').textContent = this.kills;
        const coinsEl = document.getElementById('levelCoins');
        if (coinsEl) coinsEl.textContent = this.player?.coins ?? 0;
        const pilotEl = document.getElementById('levelPilot');
        if (pilotEl) pilotEl.textContent = `Lv ${this.player?.level ?? 1}`;

        // Boss warning for the next sector
        const nextIsBoss = (this.currentLevel + 1) % 5 === 0;
        const badge = document.getElementById('bossLevelBadge');
        if (badge) {
            if (nextIsBoss) {
                badge.hidden = false;
                badge.textContent = '⚠ NEXT: BOSS LEVEL';
            } else {
                badge.hidden = true;
                badge.textContent = '';
            }
        }

        // Setup event listeners (remove old ones first)
        const nextLevelBtn = document.getElementById('nextLevelBtn');
        const viewLeaderboardBtn = document.getElementById('viewLeaderboardBtn');
        const shopSpaceshipsBtn = document.getElementById('shopSpaceshipsBtn');

        nextLevelBtn.replaceWith(nextLevelBtn.cloneNode(true));
        viewLeaderboardBtn.replaceWith(viewLeaderboardBtn.cloneNode(true));
        shopSpaceshipsBtn.replaceWith(shopSpaceshipsBtn.cloneNode(true));

        document.getElementById('nextLevelBtn').addEventListener('click', () => {
            this.currentLevel++;
            this.startNextLevel();
            popup.style.display = 'none';
            this.paused = false;
            this.telegram.hapticFeedback('impact');
        });

        document.getElementById('viewLeaderboardBtn').addEventListener('click', () => {
            popup.style.display = 'none';
            this.showLeaderboard();
        });

        document.getElementById('shopSpaceshipsBtn').addEventListener('click', () => {
            popup.style.display = 'none';
            this.showSpaceshipShop();
        });

        popup.style.display = 'block';
        this.audio.playLevelUp();
        this.telegram.hapticFeedback('success');
    }

    showSpaceshipShop() {
        this.closeAllPopups();
        this.paused = true;
        const popup = document.getElementById('spaceshipShop');
        const grid = document.getElementById('spaceshipGrid');
        grid.innerHTML = '';

        // Define spaceships (these could eventually change player stats)
        const spaceships = [
            { id: 'default', name: 'Basic Ship', price: 0, stars: 0, description: 'Standard spaceship', owned: true },
            { id: 'fighter', name: 'Fighter', price: 100, stars: 50, description: '+20% damage', owned: false },
            { id: 'tank', name: 'Tank', price: 150, stars: 75, description: '+50% max HP', owned: false },
            { id: 'speed', name: 'Speedster', price: 120, stars: 60, description: '+30% speed', owned: false },
            { id: 'collector', name: 'Collector', price: 100, stars: 50, description: '+100% coin magnet', owned: false },
            { id: 'ultimate', name: 'Ultimate', price: 300, stars: 150, description: '+20% all stats', owned: false }
        ];

        spaceships.forEach(ship => {
            const div = document.createElement('div');
            div.className = 'skin-item';
            if (ship.owned) div.classList.add('owned');

            const preview = document.createElement('div');
            preview.className = 'skin-preview';
            preview.innerHTML = ship.id === 'default' ? '🚀' :
                                ship.id === 'fighter' ? '✈️' :
                                ship.id === 'tank' ? '🛡️' :
                                ship.id === 'speed' ? '⚡' :
                                ship.id === 'collector' ? '🧲' : '👑';
            preview.style.fontSize = '40px';
            preview.style.display = 'flex';
            preview.style.alignItems = 'center';
            preview.style.justifyContent = 'center';
            div.appendChild(preview);

            const name = document.createElement('div');
            name.className = 'skin-name';
            name.textContent = ship.name;
            div.appendChild(name);

            const desc = document.createElement('div');
            desc.style.fontSize = '12px';
            desc.style.color = '#888';
            desc.style.marginTop = '5px';
            desc.textContent = ship.description;
            div.appendChild(desc);

            const price = document.createElement('div');
            price.className = 'skin-price';
            if (ship.owned) {
                price.textContent = 'Owned';
                price.style.color = '#00ff88';
            } else {
                price.textContent = `${ship.stars} ⭐`;
            }
            div.appendChild(price);

            if (!ship.owned) {
                div.onclick = async () => {
                    const success = await this.iap.purchaseSpaceship(ship.id, ship.stars);
                    if (success) {
                        ship.owned = true;
                        this.showSpaceshipShop(); // Refresh
                    }
                };
            }

            grid.appendChild(div);
        });

        // Close button
        const closeBtn = popup.querySelector('.popup-close');
        closeBtn.onclick = () => {
            popup.style.display = 'none';
            this.paused = false;
        };

        popup.style.display = 'block';
    }

    hidePopup(id) {
        document.getElementById(id).style.display = 'none';
        this.paused = false;
        this._activeShop = null;
    }

    closeAllPopups() {
        // Close all popups before opening a new one
        document.querySelectorAll('.popup').forEach(popup => {
            popup.style.display = 'none';
        });
        this.hideItemInfo();
        this.clearMovementKeys();
        this._activeShop = null;
        const dock = document.getElementById('shopDockBtn');
        if (dock && this.paused) dock.hidden = true;
    }

    // ── Space UI: inventory, trade, star map ─────────────────

    toggleInventory() {
        const el = document.getElementById('inventoryPopup');
        if (el.style.display === 'block') {
            el.style.display = 'none';
            this.hideItemInfo();
            this.paused = false;
            return;
        }
        this.closeAllPopups();
        this.paused = true;
        this.renderInventory();
        el.style.display = 'block';
    }

    toggleStarMap() {
        const el = document.getElementById('starMapPopup');
        if (el.style.display === 'block') {
            el.style.display = 'none';
            const planetPop = document.getElementById('planetPopup');
            if (planetPop) planetPop.style.display = 'none';
            this.paused = false;
            return;
        }
        this.closeAllPopups();
        this.paused = true;
        this.renderStarMap();
        el.style.display = 'block';
    }

    toggleTrade() {
        const el = document.getElementById('tradePopup');
        if (el?.style.display === 'block') {
            el.style.display = 'none';
            this._activeShop = null;
            this.paused = false;
            return;
        }
        // Prefer a nearby battlefield pad
        if (this.tryDockShop()) return;

        const planet = this.cosmos.getPlanet();
        const hasBeacon = this.tradeBeacon && this.tradeBeacon.stock?.length >= 0
            && Date.now() < (this.tradeBeacon.expires || 0);
        if (!planet?.hasStation && !hasBeacon) {
            this.floatingTexts.push({
                x: this.player?.x || 0,
                y: (this.player?.y || 0) - 40,
                text: 'Fly to a shop pad · or press Y for a beacon',
                color: '#ff8866',
                age: 0,
                duration: 100,
                pulse: false,
            });
            return;
        }
        this.closeAllPopups();
        this.paused = true;
        this.renderTrade();
        el.style.display = 'block';
    }

    _nearestShop() {
        if (!this.player || this.arenaMode || !this.worldShops?.length) return null;
        let best = null;
        let bestD = Infinity;
        for (const shop of this.worldShops) {
            const d = shop.distanceTo(this.player);
            if (d < shop.interactR && d < bestD) {
                best = shop;
                bestD = d;
            }
        }
        return best;
    }

    tryDockShop(shop = null) {
        if (!this.running || !this.player || this.arenaMode) return false;
        const pad = shop || this._nearestShop();
        if (!pad) return false;
        this.closeAllPopups();
        this._activeShop = pad;
        this.paused = true;
        this.renderTrade();
        const el = document.getElementById('tradePopup');
        if (el) el.style.display = 'block';
        this.telegram?.hapticFeedback?.('impact');
        return true;
    }

    _updateShopPrompt(shop) {
        const btn = this._shopDockBtn || (this._shopDockBtn = document.getElementById('shopDockBtn'));
        if (!btn) return;
        if (!shop || this.paused || this.arenaMode) {
            if (!btn.hidden) btn.hidden = true;
            return;
        }
        const label = `E · ${shop.verb} ${shop.name}`;
        if (btn.hidden || btn.textContent !== label) {
            btn.hidden = false;
            btn.textContent = label;
            btn.style.setProperty('--shop-accent', shop.accent);
        }
    }

    spawnWorldShops(planet) {
        this.worldShops = [];
        this._activeShop = null;
        if (!planet) return;
        const kinds = ['fuel'];
        const wealth = planet.wealth || 0.4;
        if (planet.hasStation || wealth > 0.28) kinds.push('market');
        if (planet.hasStation || wealth > 0.48) kinds.push('armory');
        const cx = WORLD_WIDTH / 2;
        const cy = WORLD_HEIGHT / 2;
        const ring = 620;
        kinds.forEach((kind, i) => {
            const a = -Math.PI / 2 + (i / kinds.length) * Math.PI * 2 + 0.62;
            const jx = (Math.random() - 0.5) * 80;
            const jy = (Math.random() - 0.5) * 80;
            const x = Math.max(200, Math.min(WORLD_WIDTH - 200, cx + Math.cos(a) * ring + jx));
            const y = Math.max(200, Math.min(WORLD_HEIGHT - 200, cy + Math.sin(a) * ring + jy));
            this.worldShops.push(new WorldShop(x, y, kind, {
                stock: generateShopStock(kind, wealth * 0.55),
                planetName: planet.name,
            }));
        });
    }

    fillFuelTank() {
        if (!this.inventory || !this.player) return;
        const missing = Math.max(0, this.inventory.maxFuel - this.inventory.fuel);
        if (missing <= 0) return;
        const cost = Math.max(1, Math.ceil(missing * 0.55));
        if (this.player.coins < cost) {
            this.floatingTexts.push({
                x: this.player.x, y: this.player.y - 40,
                text: `Need ${cost}✦ to fill tank`,
                color: '#ff8866', age: 0, duration: 70, pulse: false,
            });
            return;
        }
        this.player.coins -= cost;
        this.inventory.addFuel(missing);
        this.trackProgress({ stationTrades: 1 });
        this.renderTrade();
        this.updateUI();
    }

    /** Summon a mid-fight trader (works without a planet station). */
    callTradeBeacon() {
        if (!this.running || !this.player) return;
        if (this.isGameplayInputBlocked() && !this.paused) return;
        // Allow during combat (not paused menus only) — block if UI open unless we're just opening trade
        if (this.paused && document.getElementById('tradePopup')?.style.display !== 'block') {
            // if some other popup is open, close and refuse mid-menu
        }
        if (this.callTradeCd > 0) {
            this.floatingTexts.push({
                x: this.player.x, y: this.player.y - 40,
                text: `Beacon recharge ${Math.ceil(this.callTradeCd / 60)}s`,
                color: '#ffaa66', age: 0, duration: 70, pulse: false,
            });
            return;
        }
        if (this.arenaMode) {
            this.floatingTexts.push({
                x: this.player.x, y: this.player.y - 40,
                text: 'No signal in the void',
                color: '#ff6688', age: 0, duration: 80, pulse: false,
            });
            return;
        }
        const cost = 15;
        if (this.player.coins < cost) {
            this.floatingTexts.push({
                x: this.player.x, y: this.player.y - 40,
                text: `Need ${cost}✦ to call trader`,
                color: '#ff8866', age: 0, duration: 80, pulse: false,
            });
            return;
        }
        this.player.coins -= cost;
        this.trackProgress({ beaconsCalled: 1 });
        const luck = (this._planetDanger || 0.5) * 0.3;
        this.tradeBeacon = {
            stock: generateStationStock(8 + Math.floor(Math.random() * 4), luck),
            expires: Date.now() + 90000,
            x: this.player.x,
            y: this.player.y,
            name: 'Relay Trader',
        };
        // Price tweak — beacons slightly expensive
        for (const item of this.tradeBeacon.stock) {
            item.price = Math.round(item.price * 1.12);
        }
        this.callTradeCd = 60 * 45; // ~45s
        this.createParticles(this.player.x, this.player.y, '#ffd060', 20, 'spark');
        this.floatingTexts.push({
            x: this.player.x, y: this.player.y - 50,
            text: 'TRADE BEACON ONLINE',
            color: '#ffd060', age: 0, duration: 100, pulse: true,
        });
        this.updateUI();
        this.telegram.hapticFeedback('success');
        // Open trade UI
        this.closeAllPopups();
        this.paused = true;
        this.renderTrade();
        document.getElementById('tradePopup').style.display = 'block';
    }

    /** Call a small battlefield duel (elite pocket fight). */
    callBattlefield() {
        if (!this.running || !this.player) return;
        if (this.isGameplayInputBlocked()) return;
        if (this.arenaMode) {
            this.floatingTexts.push({
                x: this.player.x, y: this.player.y - 40,
                text: 'Already in a pocket fight',
                color: '#ff8866', age: 0, duration: 70, pulse: false,
            });
            return;
        }
        if (this.callFightCd > 0) {
            this.floatingTexts.push({
                x: this.player.x, y: this.player.y - 40,
                text: `Duel cool-down ${Math.ceil(this.callFightCd / 60)}s`,
                color: '#ffaa66', age: 0, duration: 70, pulse: false,
            });
            return;
        }
        const cost = 25;
        if (this.player.coins < cost) {
            this.floatingTexts.push({
                x: this.player.x, y: this.player.y - 40,
                text: `Need ${cost}✦ to open arena`,
                color: '#ff8866', age: 0, duration: 80, pulse: false,
            });
            return;
        }
        this.player.coins -= cost;
        this.callFightCd = 60 * 60; // ~60s
        this.trackProgress({ duelsCalled: 1 });
        this.enterDuelArena(this.player.x, this.player.y);
        this.updateUI();
    }

    /**
     * Small battlefield: hard boss + elite escorts, escape rift available.
     * Reuses void-arena stash/restore machinery.
     */
    enterDuelArena(fromX, fromY) {
        // Enter base void pocket first
        this.enterVoidArena(fromX, fromY);
        if (!this.arenaMode) return;

        this.arenaMode.kind = 'duel';
        // Add elite escorts around the boss
        const ax = WORLD_WIDTH / 2;
        const ay = WORLD_HEIGHT / 2;
        const theme = {
            faction: this._planetFaction || getFaction(this.cosmos?.getPlanet?.()),
            danger: Math.min(1, (this._planetDanger || 0.5) + 0.25),
        };
        const escorts = 3 + Math.floor(Math.random() * 3);
        for (let i = 0; i < escorts; i++) {
            const a = (i / escorts) * Math.PI * 2;
            const ex = ax + Math.cos(a) * 160;
            const ey = ay + Math.sin(a) * 120 - 40;
            const e = this.enemyPool.get(ex, ey, {
                ...theme,
                variant: ['interceptor', 'saucer', 'bomber', 'brute'][i % 4],
            });
            e.hp = Math.round(e.hp * 1.35);
            e.maxHp = e.hp;
            e.damage = Math.round(e.damage * 1.15);
            e._arenaElite = true;
            this.enemies.push(e);
        }
        this.floatingTexts.push({
            x: this.player.x,
            y: this.player.y - 85,
            text: 'DUEL ARENA · elites inbound',
            color: '#ff6688',
            age: 0,
            duration: 120,
            pulse: true,
        });
    }

    renderInventory() {
        applyLoadoutToPlayer(this.player, this.inventory);
        const stats = this.inventory.getEquipmentStats();
        const line = document.getElementById('invStatsLine');
        if (line) {
            line.textContent =
                `Fuel ${Math.floor(this.inventory.fuel)}/${this.inventory.maxFuel} · ` +
                `+${stats.damage || 0} dmg · +${stats.maxHp || 0} HP · +${(stats.speed || 0).toFixed(2)} spd · ` +
                `+${stats.range || 0} range`;
        }

        // Hangar tab visibility
        const tab = this._hangarTab || 'loadout';
        document.querySelectorAll('.hangar-tab').forEach(t => {
            const on = t.getAttribute('data-hangar-tab') === tab;
            t.classList.toggle('active', on);
            t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        document.querySelectorAll('[data-hangar-panel]').forEach(p => {
            const on = p.getAttribute('data-hangar-panel') === tab;
            p.hidden = !on;
        });

        if (tab === 'pilot') {
            this.renderHangarPilot();
            return;
        }
        if (tab === 'craft') {
            this.renderHangarCraft();
            return;
        }
        if (tab === 'badges') {
            this.renderHangarBadges();
            return;
        }
        if (tab === 'abilities') {
            this.renderHangarAbilities();
            return;
        }
        if (tab === 'systems') {
            this.renderHangarSystems();
            return;
        }

        const shipEl = document.getElementById('shipSlots');
        if (shipEl) {
            const slotMeta = {
                weapon:  'Weapon',
                weapon2: 'Turret',
                hull:    'Hull',
                engine:  'Engine',
                reactor: 'Reactor',
                sensor:  'Sensors',
                shield:  'Shield',
                wing:    'Wings',
                cargo:   'Cargo',
                special: 'Special',
            };
            const shipColor = this.player?.color || '#00d9ff';
            const hangarSrc = getHangarPreviewUrl(shipColor);
            const order = ['weapon', 'hull', 'weapon2', 'engine', 'reactor', 'sensor', 'shield', 'wing', 'cargo', 'special'];

            let slotsHtml = order.map(slot => {
                const label = slotMeta[slot] || slot;
                const item = this.inventory.equipped[slot];
                if (!item) {
                    return `<div class="bay-slot empty">
                        <span class="slot-label">${label}</span>
                        <span class="slot-empty">Empty</span>
                    </div>`;
                }
                const icon = resolveItemIcon(item);
                const broken = item.broken ? ' broken' : '';
                const dur = item.maxDurability
                    ? Math.round((item.durability ?? 100) / item.maxDurability * 100)
                    : 100;
                return `<div class="bay-slot filled r-${item.rarity}${broken}" data-slot="${slot}">
                    <button type="button" class="item-info-btn" data-info-slot="${slot}" title="Info">?</button>
                    <span class="slot-label">${label}${item.broken ? ' ⚠' : ''}</span>
                    <img src="${icon}" alt="${item.name}" decoding="async">
                    <span class="item-name">${item.name}</span>
                    <span class="dur-bar"><span class="dur-fill${item.broken ? ' broken' : ''}" style="width:${dur}%"></span></span>
                    ${item.broken ? `<button type="button" class="repair-btn" data-repair-slot="${slot}">Fix ${getRepairCost(item)}✦</button>` : ''}
                </div>`;
            }).join('');

            shipEl.innerHTML = `
                <div class="ship-center">
                    <div class="ship-center-ring"></div>
                    <img class="ship-center-img" src="${hangarSrc}" alt="Your ship" decoding="async">
                    <div class="ship-center-caption">YOUR SHIP</div>
                </div>
                <div class="bay-slot-grid">${slotsHtml}</div>
            `;

            shipEl.querySelectorAll('[data-info-slot]').forEach(btn => {
                btn.onclick = (e) => {
                    e.stopPropagation();
                    const slot = btn.getAttribute('data-info-slot');
                    this.showItemInfo(this.inventory.equipped[slot], { equippedSlot: slot });
                };
            });
            shipEl.querySelectorAll('[data-repair-slot]').forEach(btn => {
                btn.onclick = (e) => {
                    e.stopPropagation();
                    const slot = btn.getAttribute('data-repair-slot');
                    this.tryRepairEquipped(slot);
                };
            });
            shipEl.querySelectorAll('.bay-slot.filled').forEach(node => {
                node.onclick = () => {
                    const slot = node.getAttribute('data-slot');
                    if (!slot || !this.inventory.equipped[slot]) return;
                    // Click body unequips (not when pressing ? / Fix)
                    this.inventory.unequip(slot);
                    applyLoadoutToPlayer(this.player, this.inventory);
                    this.renderInventory();
                };
            });
        }

        const grid = document.getElementById('invGrid');
        if (grid) {
            this.inventory.ensureSize();
            grid.innerHTML = this.inventory.slots.map((item, i) => {
                if (!item) {
                    return `<div class="item-card item-card-empty" aria-hidden="true"></div>`;
                }
                const qty = item.qty > 1 ? `<span class="item-qty">×${item.qty}</span>` : '';
                const icon = resolveItemIcon(item);
                const broken = item.broken ? ' broken' : '';
                return `<div class="item-card r-${item.rarity}${broken}" data-bag="${i}">
                    <button type="button" class="item-info-btn" data-info-bag="${i}" title="Info">?</button>
                    ${qty}
                    <img src="${icon}" alt="${item.name}" decoding="async">
                    <div class="item-name">${item.name}</div>
                </div>`;
            }).join('');
            grid.querySelectorAll('[data-info-bag]').forEach(btn => {
                btn.onclick = (e) => {
                    e.stopPropagation();
                    const idx = +btn.getAttribute('data-info-bag');
                    this.showItemInfo(this.inventory.slots[idx], { bagIndex: idx });
                };
            });
            grid.querySelectorAll('[data-bag]').forEach(node => {
                node.onclick = () => {
                    const idx = +node.getAttribute('data-bag');
                    const item = this.inventory.slots[idx];
                    if (!item) return;
                    if (item.kind === 'module' && !item.broken) {
                        this.inventory.equip(idx);
                        applyLoadoutToPlayer(this.player, this.inventory);
                        this.renderInventory();
                    } else if (item.kind === 'module' && item.broken) {
                        this.showItemInfo(item, { bagIndex: idx });
                    } else if (item.shape === 'fuel') {
                        this.inventory.addFuel(item.stats?.fuel || 15);
                        if ((item.qty || 1) > 1) item.qty--;
                        else this.inventory.removeAt(idx);
                        this.renderInventory();
                        this.updateUI();
                    } else if (item.shape === 'armor' && item.stats?.maxHp) {
                        this.player.maxHp += item.stats.maxHp;
                        this.player.hp = Math.min(this.player.maxHp, this.player.hp + item.stats.maxHp);
                        if ((item.qty || 1) > 1) item.qty--;
                        else this.inventory.removeAt(idx);
                        this.renderInventory();
                    }
                };
            });
        }
    }

    /** Hangar → Abilities tab: weapons + level-up perks */
    renderHangarAbilities() {
        const body = document.getElementById('hangarAbilitiesBody');
        if (!body || !this.player) return;

        const weapons = this.player.weapons || [];
        const log = this._abilityLog || [];
        const weaponMeta = {
            laser: { icon: '🔴', label: 'Laser' },
            orbit: { icon: '🛡️', label: 'Orbitals' },
            shotgun: { icon: '🔫', label: 'Shotgun' },
            chain: { icon: '⚡', label: 'Chain' },
            boomerang: { icon: '🪃', label: 'Boomerang' },
        };

        let weaponsHtml = weapons.length
            ? weapons.map(w => {
                const meta = weaponMeta[w.type] || { icon: '✦', label: w.type };
                const bits = [];
                if (w.damage != null) bits.push(`dmg ${Math.round(w.damage)}`);
                if (w.maxCooldown != null) bits.push(`cd ${w.maxCooldown}`);
                if (w.count != null) bits.push(`×${w.count}`);
                if (w.radius != null) bits.push(`r ${w.radius}`);
                return `<div class="hab-card">
                    <div class="hab-icon">${meta.icon}</div>
                    <div class="hab-main">
                        <div class="hab-title">${meta.label}</div>
                        <div class="hab-meta">${bits.join(' · ') || 'active'}</div>
                    </div>
                    <span class="hab-badge">WEAPON</span>
                </div>`;
            }).join('')
            : `<div class="hab-empty">No special weapons yet — pick them up from level-ups or crystal bonuses.</div>`;

        // Aggregate perk picks (count duplicates)
        const perkCounts = new Map();
        for (const a of log) {
            const prev = perkCounts.get(a.id) || { ...a, count: 0 };
            prev.count += 1;
            perkCounts.set(a.id, prev);
        }
        const perks = [...perkCounts.values()];
        let perksHtml = perks.length
            ? perks.map(a => `<div class="hab-card">
                <div class="hab-icon">★</div>
                <div class="hab-main">
                    <div class="hab-title">${a.name}${a.count > 1 ? ` ×${a.count}` : ''}</div>
                    <div class="hab-meta">${a.description || ''}</div>
                </div>
                <span class="hab-badge dim">Lv ${a.level || '?'}</span>
            </div>`).join('')
            : `<div class="hab-empty">No level-up perks yet. Survive waves to unlock upgrades.</div>`;

        // Passive derived from equipment
        const s = this.inventory.getEquipmentStats();
        const passives = [
            s.damage && { icon: '💥', title: 'Weapon Amplifiers', meta: `+${s.damage} damage from modules` },
            s.fireRate && { icon: '🔥', title: 'Cooling Rigs', meta: `+${Math.round(s.fireRate * 100)}% fire rate` },
            s.speed && { icon: '⚡', title: 'Thruster Tuning', meta: `+${s.speed.toFixed(2)} speed` },
            s.maxHp && { icon: '❤', title: 'Hull Plating', meta: `+${s.maxHp} max HP` },
            s.shield && { icon: '🛡', title: 'Shield Matrix', meta: `+${s.shield} shield HP` },
            s.range && { icon: '◎', title: 'Sensor Lock', meta: `+${s.range} shoot range` },
            s.magnet && { icon: '🧲', title: 'Tractor Field', meta: `+${s.magnet} magnet range` },
            s.armor && { icon: '▣', title: 'Armor Weave', meta: `+${s.armor} armor` },
        ].filter(Boolean);

        const passHtml = passives.length
            ? passives.map(p => `<div class="hab-card compact">
                <div class="hab-icon">${p.icon}</div>
                <div class="hab-main">
                    <div class="hab-title">${p.title}</div>
                    <div class="hab-meta">${p.meta}</div>
                </div>
            </div>`).join('')
            : `<div class="hab-empty">Equip modules on Loadout to gain passive abilities.</div>`;

        body.innerHTML = `
            <div class="hab-section">
                <h3 class="hab-heading">Active weapons</h3>
                <div class="hab-list">${weaponsHtml}</div>
            </div>
            <div class="hab-section">
                <h3 class="hab-heading">Level-up perks</h3>
                <div class="hab-list">${perksHtml}</div>
            </div>
            <div class="hab-section">
                <h3 class="hab-heading">Module passives</h3>
                <div class="hab-list">${passHtml}</div>
            </div>
        `;
    }

    refreshPilotHud() {
        const look = this.pilotLook || defaultPilotLook();
        const hud = document.getElementById('pilotPortraitHud');
        if (hud) drawPilotPortrait(hud, look, { frame: false });
        const nameEl = document.getElementById('pilotNameHud');
        if (nameEl) nameEl.textContent = look.name || 'Pilot';
        if (this.player && look.name) this.player.name = look.name;
    }

    setPilotLook(patch) {
        const next = { ...(this.pilotLook || defaultPilotLook()), ...patch };
        const hairs = hairsForType(next.type);
        if (!hairs.some((h) => h.id === next.hair)) next.hair = hairs[0].id;
        this.pilotLook = savePilotLook(next);
        this.refreshPilotHud();
        if (this._hangarTab === 'pilot') this.renderHangarPilot();
    }

    renderHangarPilot() {
        const body = document.getElementById('hangarPilotBody');
        if (!body) return;
        const look = this.pilotLook || defaultPilotLook();
        const hairs = hairsForType(look.type);

        const chip = (group, id, label, on) =>
            `<button type="button" class="pilot-chip ${on ? 'on' : ''}" data-pilot="${group}" data-id="${id}">${label}</button>`;

        body.innerHTML = `
            <div class="pilot-creator">
                <div class="pilot-stage">
                    <div class="pilot-frame">
                        <canvas id="pilotPreview" width="360" height="360" aria-label="Pilot portrait"></canvas>
                    </div>
                    <div class="pilot-name-row">
                        <label class="pilot-name-label" for="pilotNameInput">Callsign</label>
                        <input id="pilotNameInput" class="pilot-name-input" maxlength="18" value="${escapeXml(look.name || 'Pilot')}" spellcheck="false">
                    </div>
                    <div class="pilot-look-line">${lookLabel(look)}</div>
                    <button type="button" class="button pilot-random" id="pilotRandomBtn">Random look</button>
                </div>
                <div class="pilot-controls">
                    <div class="pilot-row">
                        <div class="pilot-row-label">Type</div>
                        <div class="pilot-chips">
                            ${PILOT_TYPES.map((t) => chip('type', t.id, t.label, look.type === t.id)).join('')}
                        </div>
                    </div>
                    <div class="pilot-row">
                        <div class="pilot-row-label">Hair</div>
                        <div class="pilot-chips">
                            ${hairs.map((h) => chip('hair', h.id, h.label, look.hair === h.id)).join('')}
                        </div>
                    </div>
                    <div class="pilot-row">
                        <div class="pilot-row-label">Clothes</div>
                        <div class="pilot-chips">
                            ${PILOT_SUITS.map((s) => chip('suit', s.id, s.label, look.suit === s.id)).join('')}
                        </div>
                    </div>
                    <div class="pilot-row">
                        <div class="pilot-row-label">Hair dye</div>
                        <div class="pilot-chips">
                            ${PILOT_DYES.map((d) => chip('dye', d.id, d.label, look.dye === d.id)).join('')}
                        </div>
                    </div>
                    <div class="pilot-row">
                        <div class="pilot-row-label">Skin</div>
                        <div class="pilot-chips">
                            ${PILOT_TONES.map((t) => chip('tone', t.id, t.label, look.tone === t.id)).join('')}
                        </div>
                    </div>
                    <p class="pilot-hint">16-bit ranger portraits · mix hair, clothes and dye · saved on this device</p>
                </div>
            </div>
        `;

        const preview = document.getElementById('pilotPreview');
        if (preview) drawPilotPortrait(preview, look, { frame: false });

        body.querySelectorAll('[data-pilot]').forEach((btn) => {
            btn.onclick = () => {
                const group = btn.getAttribute('data-pilot');
                const id = btn.getAttribute('data-id');
                if (!group || !id) return;
                this.setPilotLook({ [group]: id });
            };
        });
        const nameIn = document.getElementById('pilotNameInput');
        if (nameIn) {
            nameIn.oninput = () => {
                const name = nameIn.value.trim().slice(0, 18) || 'Pilot';
                this.pilotLook = savePilotLook({ ...look, name });
                if (this.player) this.player.name = name;
                const hud = document.getElementById('pilotNameHud');
                if (hud) hud.textContent = name;
            };
        }
        document.getElementById('pilotRandomBtn')?.addEventListener('click', () => {
            this.setPilotLook(randomPilotLook(this.pilotLook));
        });
    }

    /** Hangar → Systems tab: vitals, fuel, parts status, ship settings */
    renderHangarSystems() {
        const body = document.getElementById('hangarSystemsBody');
        if (!body || !this.player) return;

        const p = this.player;
        const inv = this.inventory;
        const stats = inv.getEquipmentStats();
        const faction = this._planetFaction || getFaction(this.cosmos?.getPlanet?.());
        const planet = this.cosmos?.getPlanet?.();

        const hpPct = Math.round((p.hp / Math.max(1, p.maxHp)) * 100);
        const fuelPct = Math.round((inv.fuel / Math.max(1, inv.maxFuel)) * 100);
        const fireCd = p.autoShootMaxCooldown ?? 20;

        const vitals = [
            { label: 'Hull integrity', value: `${Math.round(p.hp)} / ${Math.round(p.maxHp)}`, pct: hpPct, color: hpPct > 50 ? '#33ff88' : hpPct > 25 ? '#ffcc00' : '#ff4466' },
            { label: 'Fuel cells', value: `${Math.floor(inv.fuel)} / ${inv.maxFuel}`, pct: fuelPct, color: '#ffd060' },
            { label: 'Pilot level', value: `Lv ${p.level} · XP ${p.xp}/${p.xpNeeded}`, pct: Math.round((p.xp / Math.max(1, p.xpNeeded)) * 100), color: '#66ccff' },
        ];

        const combat = [
            { k: 'Damage', v: Math.round(p.damage) },
            { k: 'Fire rate', v: `${fireCd} cd` },
            { k: 'Speed', v: p.speed.toFixed(2) },
            { k: 'Range', v: Math.round(SHOOT_RANGE + (p.shootRangeBonus || 0)) },
            { k: 'Armor', v: Math.round(p.armor || 0) },
            { k: 'Magnet', v: Math.round(p.magnetRange || 0) },
            { k: 'Crystals', v: p.coins },
            { k: 'Kills', v: this.kills || 0 },
        ];

        const slotMeta = {
            weapon: 'Weapon', weapon2: 'Turret', hull: 'Hull', engine: 'Engine',
            reactor: 'Reactor', sensor: 'Sensors', shield: 'Shield', wing: 'Wings',
            cargo: 'Cargo', special: 'Special',
        };
        const parts = SHIP_SLOTS.map(slot => {
            const item = inv.equipped[slot];
            if (!item) {
                return { slot, label: slotMeta[slot] || slot, empty: true };
            }
            const dur = item.maxDurability
                ? Math.round((item.durability ?? 100) / item.maxDurability * 100)
                : 100;
            return {
                slot,
                label: slotMeta[slot] || slot,
                name: item.name,
                rarity: item.rarity,
                broken: !!item.broken,
                dur,
                empty: false,
            };
        });

        body.innerHTML = `
            <div class="hsys-grid">
                <section class="hsys-card">
                    <h3 class="hab-heading">Vitals</h3>
                    ${vitals.map(v => `
                        <div class="hsys-bar-row">
                            <div class="hsys-bar-label"><span>${v.label}</span><b>${v.value}</b></div>
                            <div class="hsys-bar-track"><i style="width:${Math.min(100, v.pct)}%;background:${v.color}"></i></div>
                        </div>
                    `).join('')}
                </section>

                <section class="hsys-card">
                    <h3 class="hab-heading">Combat profile</h3>
                    <div class="hsys-stat-grid">
                        ${combat.map(c => `
                            <div class="hsys-stat">
                                <span>${c.k}</span>
                                <b>${c.v}</b>
                            </div>
                        `).join('')}
                    </div>
                </section>

                <section class="hsys-card hsys-wide">
                    <h3 class="hab-heading">Module systems</h3>
                    <div class="hsys-parts">
                        ${parts.map(pt => pt.empty
                            ? `<div class="hsys-part empty">
                                <span class="hsys-part-slot">${pt.label}</span>
                                <span class="hsys-part-name">— offline —</span>
                               </div>`
                            : `<div class="hsys-part ${pt.broken ? 'broken' : ''} r-${pt.rarity || 'common'}">
                                <span class="hsys-part-slot">${pt.label}</span>
                                <span class="hsys-part-name">${pt.name}${pt.broken ? ' ⚠' : ''}</span>
                                <span class="hsys-part-dur">
                                    <i style="width:${pt.dur}%"></i>
                                </span>
                                <span class="hsys-part-pct">${pt.dur}%</span>
                               </div>`
                        ).join('')}
                    </div>
                </section>

                <section class="hsys-card hsys-wide">
                    <h3 class="hab-heading">Ship configuration</h3>
                    <div class="hsys-config">
                        <div class="hsys-config-row">
                            <span>Callsign</span><b>${p.name || 'Pilot'}</b>
                        </div>
                        <div class="hsys-config-row">
                            <span>Hull style</span><b>${p.shipStyle || 'dart'}</b>
                        </div>
                        <div class="hsys-config-row">
                            <span>Primary livery</span>
                            <b class="hsys-swatch" style="--c:${p.color || '#00d9ff'}">${p.color || '#00d9ff'}</b>
                        </div>
                        <div class="hsys-config-row">
                            <span>Local faction</span>
                            <b style="color:${faction?.playerPrimary || '#7adfff'}">${faction?.label || 'Unknown'}</b>
                        </div>
                        <div class="hsys-config-row">
                            <span>Current world</span>
                            <b>${planet?.name || '—'} · ${planet?.type || ''}</b>
                        </div>
                        <div class="hsys-config-row">
                            <span>Cargo capacity</span>
                            <b>${inv.capacity} slots · +${stats.cargo || 0} bonus</b>
                        </div>
                        <div class="hsys-config-row">
                            <span>Module bonuses</span>
                            <b>+${stats.damage || 0} dmg · +${stats.maxHp || 0} HP · +${(stats.speed || 0).toFixed(2)} spd</b>
                        </div>
                    </div>
                    <p class="hsys-note">Change pilot name and audio in Settings · hyperjump via Hypermap (M) · repair broken parts on Loadout.</p>
                </section>
            </div>
        `;
    }

    /** Plain-language what this item is and what to do with it */
    _itemHowToUse(item, ctx = {}) {
        if (!item) return '';
        const kind = item.kind;
        const shape = item.shape;
        const slot = item.slot;
        const bits = [];
        if (item.artefact) {
            bits.push('Combat artefact. Equip it in the Special slot in Hangar (I) to gain its bonuses.');
            bits.push('Mutate or upgrade it in Hangar → Craft to twist or rank up the stats.');
        } else if (kind === 'module') {
            bits.push(`Ship module for the ${slot || 'matching'} bay.`);
            bits.push('In Hangar, click it in cargo to equip (or click an equipped slot to unequip).');
            bits.push('Craft tab: Mutate re-rolls stats · Upgrade ranks power (costs crystals).');
            if (item.broken) bits.push('Broken — repair it first or it gives no bonuses.');
        } else if (shape === 'fuel') {
            bits.push('Hyperjump fuel. In Hangar cargo, click it to pour fuel into the tank.');
            bits.push('You need fuel to jump between planets on the Hypermap (M).');
        } else if (shape === 'armor') {
            bits.push('Hull kit. Click it in Hangar cargo to raise max HP and heal that amount.');
        } else if (shape === 'crystal') {
            bits.push('Trade good. Sell at a station or beacon for crystals — keep stacks for later.');
        } else if (shape === 'scrap') {
            bits.push('Salvage. Sell for crystals. Handy when you need cash for upgrades.');
        } else if (shape === 'chip') {
            bits.push('Nav chip. Trade it, or keep as loot — sensor bonuses are listed in stats.');
        } else if (shape === 'drone') {
            bits.push('Utility drone. Sell it, or keep if it grants combat stats.');
        } else {
            bits.push('Trade commodity. Buy cheap / sell dear at stations and beacons.');
        }
        if (ctx.tradeBuyIndex != null) {
            bits.push(`This is on the market. Click the card (or Buy) to purchase for ${item.price} ✦.`);
        }
        if (ctx.tradeSell) {
            bits.push(`This is in your cargo. Click the card (or Sell) to sell for ${getSellPrice(item)} ✦.`);
        }
        return bits;
    }

    showItemInfo(item, ctx = {}) {
        const overlay = document.getElementById('itemInfoOverlay');
        const panel = document.getElementById('itemInfoPanel');
        if (!overlay || !panel || !item) return;
        const info = getItemInfo(item);
        const how = this._itemHowToUse(item, ctx);
        const statsHtml = info.stats.length
            ? `<ul class="info-stats">${info.stats.map(s => `<li><span>${s.k}</span><b>${s.v}</b></li>`).join('')}</ul>`
            : '<p class="info-muted">No combat stats</p>';
        const repairBtn = (item.broken || (info.durability < 100 && item.kind === 'module'))
            && ctx.tradeBuyIndex == null
            ? `<button type="button" class="button repair-btn-lg" id="infoRepairBtn">
                Repair — ${info.repairCost} ✦
               </button>`
            : '';
        const tradeBuy = ctx.tradeBuyIndex != null
            ? `<button type="button" class="button repair-btn-lg" id="infoBuyBtn">Buy — ${info.price} ✦</button>`
            : '';
        const tradeSell = ctx.tradeSell
            ? `<button type="button" class="button repair-btn-lg info-sell-btn" id="infoSellBtn">Sell — ${info.sell} ✦</button>`
            : '';
        const howHtml = how.length
            ? `<div class="info-howto"><div class="info-howto-title">What to do</div><ul>${how.map(h => `<li>${h}</li>`).join('')}</ul></div>`
            : '';
        panel.innerHTML = `
            <button type="button" class="info-close" id="infoCloseBtn" aria-label="Close">×</button>
            <div class="info-head">
                <img src="${resolveItemIcon(item)}" alt="">
                <div>
                    <div class="info-title r-${info.rarity}" id="itemInfoTitle">${info.title}</div>
                    <div class="info-meta">${(info.rarity || '').toUpperCase()} · ${info.slot || 'item'}
                        ${item.kind === 'module' ? ` · ${info.durability}% OK` : ''}
                        ${item.broken ? ' · <span class="bad">BROKEN</span>' : ''}
                    </div>
                </div>
            </div>
            <p class="info-body">${info.body || 'No extra description.'}</p>
            ${statsHtml}
            ${howHtml}
            <div class="info-meta info-prices">Buy ${info.price} ✦ · Sell ${info.sell} ✦</div>
            ${tradeBuy}
            ${tradeSell}
            ${repairBtn}
            <p class="info-hint">${item.kind === 'module' ? 'Broken parts give no bonuses until repaired.' : 'Prices change with planet wealth.'}</p>
        `;
        overlay.hidden = false;
        const close = () => this.hideItemInfo();
        document.getElementById('infoCloseBtn')?.addEventListener('click', close);
        overlay.querySelector('[data-info-dismiss]')?.addEventListener('click', close, { once: true });
        document.getElementById('infoRepairBtn')?.addEventListener('click', () => {
            if (ctx.equippedSlot) this.tryRepairEquipped(ctx.equippedSlot);
            else if (ctx.bagIndex != null) this.tryRepairBag(ctx.bagIndex);
        });
        document.getElementById('infoBuyBtn')?.addEventListener('click', () => {
            const planet = this.cosmos.getPlanet();
            this._buyTradeItem(ctx.tradeBuyIndex, ctx.tradeSource, planet);
        });
        document.getElementById('infoSellBtn')?.addEventListener('click', () => {
            this._sellTradeItem(ctx.bagIndex, ctx.tradeSource || 'station');
        });
        this._itemInfoEsc = (e) => {
            if (e.key === 'Escape') this.hideItemInfo();
        };
        window.addEventListener('keydown', this._itemInfoEsc);
    }

    hideItemInfo() {
        const overlay = document.getElementById('itemInfoOverlay');
        const panel = document.getElementById('itemInfoPanel');
        if (overlay) overlay.hidden = true;
        if (panel) panel.innerHTML = '';
        if (this._itemInfoEsc) {
            window.removeEventListener('keydown', this._itemInfoEsc);
            this._itemInfoEsc = null;
        }
    }

    tryRepairEquipped(slot) {
        const item = this.inventory.equipped[slot];
        if (!item) return;
        const cost = getRepairCost(item);
        if (this.player.coins < cost) {
            this.showItemInfo(item, { equippedSlot: slot });
            const panel = document.getElementById('itemInfoPanel');
            if (panel) {
                const hint = panel.querySelector('.info-hint');
                if (hint) hint.textContent = `Need ${cost} crystals (have ${this.player.coins}).`;
            }
            return;
        }
        this.player.coins -= cost;
        repairItem(item);
        applyLoadoutToPlayer(this.player, this.inventory);
        this.hideItemInfo();
        this.renderInventory();
        this.updateUI();
    }

    tryRepairBag(index) {
        const item = this.inventory.slots[index];
        if (!item) return;
        const cost = getRepairCost(item);
        if (this.player.coins < cost) return;
        this.player.coins -= cost;
        repairItem(item);
        this.hideItemInfo();
        this.renderInventory();
        this.updateUI();
    }

    renderStarMap() {
        const { galaxy, system, planet } = this.cosmos.getCurrentLocation();
        const loc = document.getElementById('mapLocationLine');
        if (loc) {
            loc.innerHTML = `<span class="zodiac-crumb">${galaxy?.name || '—'}</span>
                <span class="zodiac-sep">✶</span>
                <span class="zodiac-crumb">${system?.name || '—'}</span>
                <span class="zodiac-sep">✶</span>
                <span class="zodiac-crumb here">${planet?.name || '—'} · ${planet?.type || ''}</span>
                <span class="zodiac-sep">·</span>
                <span class="zodiac-fuel">Fuel ${Math.floor(this.inventory.fuel)}</span>`;
        }

        const view = document.getElementById('starMapView');
        const detail = document.getElementById('planetDetail');
        if (!view) return;

        if (this._mapMode === 'galaxy') {
            view.innerHTML = this._buildZodiacGalaxyMap();
            // Click galaxy hub → open first system orrery
            view.querySelectorAll('[data-galaxy]').forEach(n => {
                n.onclick = (e) => {
                    e.stopPropagation();
                    const g = this.cosmos.getGalaxy(n.getAttribute('data-galaxy'));
                    const p = g?.systems?.[0]?.planets?.[0];
                    if (p) {
                        this._selectedPlanetId = p.id;
                        this._mapMode = 'system';
                        document.querySelectorAll('.map-tab').forEach(t => {
                            t.classList.toggle('active', t.dataset.map === 'system');
                        });
                        this._browseSystemId = g.systems[0].id;
                        this.renderStarMap();
                    }
                };
            });
            // Click star system → open that orrery
            view.querySelectorAll('[data-system]').forEach(n => {
                n.onclick = (e) => {
                    e.stopPropagation();
                    const sid = n.getAttribute('data-system');
                    const sys = this.cosmos.getSystem(sid);
                    const p = sys?.planets?.[0];
                    if (!sys) return;
                    this._browseSystemId = sid;
                    this._selectedPlanetId = p?.id || null;
                    this._mapMode = 'system';
                    document.querySelectorAll('.map-tab').forEach(t => {
                        t.classList.toggle('active', t.dataset.map === 'system');
                    });
                    this.renderStarMap();
                };
            });
            // Click planet on mini-orbit → dossier popup
            view.querySelectorAll('[data-planet]').forEach(n => {
                n.onclick = (e) => {
                    e.stopPropagation();
                    const pid = n.getAttribute('data-planet');
                    const p = this.cosmos.getPlanet(pid);
                    if (!p) return;
                    this._selectedPlanetId = pid;
                    this._browseSystemId = p.systemId;
                    this.renderStarMap();
                    this.showPlanetDetail(pid, { open: true });
                };
            });
            return;
        }

        // System orrery
        const sys = this._browseSystemId
            ? this.cosmos.getSystem(this._browseSystemId)
            : system;
        this._browseSystemId = sys?.id || system?.id;

        view.innerHTML = this._buildZodiacSystemMap(sys);

        view.querySelectorAll('[data-planet]').forEach(n => {
            n.onclick = () => {
                const pid = n.getAttribute('data-planet');
                this._selectedPlanetId = pid;
                this.renderStarMap();
                this.showPlanetDetail(pid, { open: true });
            };
        });
    }

    /**
     * Zodiac cosmos map — galaxies on a house wheel, each a clean mini star-chart.
     * Systems are evenly spaced; planets are capped tiny pips so clusters stay readable.
     */
    _buildZodiacGalaxyMap() {
        const VB = 520;
        const cx = 260, cy = 260;
        const galaxies = this.cosmos.galaxies || [];
        const n = Math.max(galaxies.length, 1);
        // Room for ~55px galaxy domains without overlap (5 galaxies → chord ≈ 2 R sin(π/n))
        const R = n <= 4 ? 168 : n <= 6 ? 158 : 148;
        const domainR = n <= 4 ? 58 : n <= 6 ? 52 : 46;
        const houses = 12;
        const currentGalId = this.cosmos.current.galaxyId;
        const currentSysId = this.cosmos.current.systemId;
        const currentPlId = this.cosmos.current.planetId;
        const selectedPlId = this._selectedPlanetId || currentPlId;

        // Outer zodiac frame
        let rings = '';
        rings += `<circle class="z-ring z-ring-outer" cx="${cx}" cy="${cy}" r="246"/>`;
        rings += `<circle class="z-ring z-ring-mid" cx="${cx}" cy="${cy}" r="234"/>`;
        rings += `<circle class="z-ring z-ring-inner" cx="${cx}" cy="${cy}" r="36"/>`;
        rings += `<circle class="z-orbit z-galaxy-track" cx="${cx}" cy="${cy}" r="${R}"/>`;

        for (let i = 0; i < houses; i++) {
            const a = (i / houses) * Math.PI * 2 - Math.PI / 2;
            rings += `<line class="z-house" x1="${cx + Math.cos(a) * 234}" y1="${cy + Math.sin(a) * 234}"
                x2="${cx + Math.cos(a) * 246}" y2="${cy + Math.sin(a) * 246}"/>`;
            rings += `<line class="z-ray" x1="${cx}" y1="${cy}"
                x2="${cx + Math.cos(a) * 234}" y2="${cy + Math.sin(a) * 234}"/>`;
            const lx = cx + Math.cos(a + Math.PI / houses) * 240;
            const ly = cy + Math.sin(a + Math.PI / houses) * 240;
            const roman = ['I','II','III','IV','V','VI','VII','VIII','IX','X','XI','XII'][i];
            rings += `<text class="z-house-label" x="${lx}" y="${ly}">${roman}</text>`;
        }
        for (let i = 0; i < 72; i++) {
            const a = (i / 72) * Math.PI * 2;
            const long = i % 6 === 0;
            rings += `<line class="z-tick ${long ? 'long' : ''}"
                x1="${cx + Math.cos(a) * (long ? 228 : 230)}" y1="${cy + Math.sin(a) * (long ? 228 : 230)}"
                x2="${cx + Math.cos(a) * 234}" y2="${cy + Math.sin(a) * 234}"/>`;
        }

        // Even galaxy seats on the wheel
        const pts = galaxies.map((g, i) => {
            const a = (i / n) * Math.PI * 2 - Math.PI / 2;
            return { g, i, a, x: cx + Math.cos(a) * R, y: cy + Math.sin(a) * R };
        });

        // Soft arc links (no dense chords)
        let links = '';
        for (let i = 0; i < pts.length; i++) {
            const a = pts[i], b = pts[(i + 1) % pts.length];
            // Quadratic-ish bend via mid point pushed outward
            const mx = (a.x + b.x) / 2;
            const my = (a.y + b.y) / 2;
            const dx = mx - cx, dy = my - cy;
            const len = Math.hypot(dx, dy) || 1;
            const ox = cx + (dx / len) * (R * 0.78);
            const oy = cy + (dy / len) * (R * 0.78);
            links += `<path class="z-gal-link" d="M ${a.x.toFixed(1)} ${a.y.toFixed(1)} Q ${ox.toFixed(1)} ${oy.toFixed(1)} ${b.x.toFixed(1)} ${b.y.toFixed(1)}" fill="none"/>`;
            links += `<circle class="z-link-star" cx="${ox.toFixed(1)}" cy="${oy.toFixed(1)}" r="1.4"/>`;
        }

        // Cosmos hub
        let nodes = `
            <g class="z-hub" pointer-events="none">
                <circle class="z-hub-glow" cx="${cx}" cy="${cy}" r="28"/>
                <circle class="z-hub-core" cx="${cx}" cy="${cy}" r="7"/>
                <text class="z-hub-label" x="${cx}" y="${cy + 22}">COSMOS</text>
            </g>`;

        pts.forEach(({ g, x, y, a: galAngle }) => {
            const curGal = g.id === currentGalId;
            const hue = g.hue ?? 200;
            const fill = `hsl(${hue}, 68%, 55%)`;
            const fillDim = `hsla(${hue}, 55%, 40%, 0.35)`;
            const systems = g.systems || [];
            const sysN = systems.length;
            const sysCount = Math.max(sysN, 1);

            // Systems: 1–2 clean rings, even angles, never stacked on center
            const useDual = sysN > 4;
            const rOuter = domainR * 0.72;
            const rInner = domainR * 0.42;

            let local = '';
            local += `<circle class="z-gal-domain ${curGal ? 'current' : ''}" r="${domainR}" style="stroke:${fill};fill:${fillDim}"/>`;
            local += `<circle class="z-orbit z-sys-track" r="${rOuter.toFixed(1)}"/>`;
            if (useDual) local += `<circle class="z-orbit z-sys-track dim" r="${rInner.toFixed(1)}"/>`;

            // Phase so systems sit “open” toward cosmos center (less edge-to-edge collision)
            const phase = galAngle + Math.PI; // face inward-ish
            systems.forEach((sys, si) => {
                let sr;
                let sa;
                if (useDual) {
                    // Split evenly across two rings
                    const onInner = si % 2 === 1;
                    const ringIdx = Math.floor(si / 2);
                    const ringCount = Math.ceil(sysN / 2);
                    sr = onInner ? rInner : rOuter;
                    sa = (ringIdx / Math.max(ringCount, 1)) * Math.PI * 2 + phase + (onInner ? Math.PI / ringCount : 0);
                } else {
                    sr = rOuter;
                    sa = (si / sysCount) * Math.PI * 2 + phase;
                }
                const sx = Math.cos(sa) * sr;
                const sy = Math.sin(sa) * sr;
                const starCol = sys.starColor || '#ffeeaa';
                const curSys = sys.id === currentSysId;
                const planets = sys.planets || [];
                // Cap planets shown on overview (readability)
                const maxPl = Math.min(planets.length, 4);
                const pr = 6.5; // fixed micro-orbit — keeps systems compact

                let mini = '';
                if (maxPl > 0) {
                    mini += `<circle class="z-micro-orbit" r="${pr}"/>`;
                    for (let pi = 0; pi < maxPl; pi++) {
                        const pl = planets[pi];
                        const pa = (pi / maxPl) * Math.PI * 2 - Math.PI / 2;
                        const px = Math.cos(pa) * pr;
                        const py = Math.sin(pa) * pr;
                        const curPl = pl.id === currentPlId;
                        const selPl = pl.id === selectedPlId;
                        const gas = pl.type === 'gas' || pl.hasRings;
                        mini += `
                        <g class="z-node z-mini-planet ${curPl ? 'current' : ''} ${selPl ? 'selected' : ''} ${gas ? 'gas' : ''}"
                           data-planet="${pl.id}" transform="translate(${px.toFixed(1)},${py.toFixed(1)})">
                            <circle class="z-mini-hit" r="4.5"/>
                            ${gas ? `<circle class="z-mini-gas" r="3.6" fill="${pl.color || '#c9a06a'}"/>` : ''}
                            <circle r="${gas ? 2.1 : 1.6}"
                                style="fill:${pl.color || '#88a'};stroke:${pl.colorLight || '#cff'};stroke-width:0.5"/>
                            ${curPl ? '<circle class="z-here-pip" r="3.2"/>' : ''}
                        </g>`;
                    }
                    // Overflow marker if more planets exist
                    if (planets.length > maxPl) {
                        mini += `<text class="z-sys-more" y="${pr + 7}">+${planets.length - maxPl}</text>`;
                    }
                }

                local += `
                <g class="z-node z-star-sys ${curSys ? 'current' : ''}" data-system="${sys.id}"
                   transform="translate(${sx.toFixed(1)},${sy.toFixed(1)})">
                    ${mini}
                    <circle class="z-sys-halo" r="4.2" style="stroke:${starCol}"/>
                    <circle class="z-sys-body" r="2.6" style="fill:${starCol}"/>
                    <circle class="z-sys-core" r="0.9"/>
                    ${curSys ? '<circle class="z-sys-you-ring" r="7"/>' : ''}
                </g>`;
            });

            // Label pushed radially outward from cosmos so neighbors don’t overlap
            const labelDist = domainR + 12;
            const lx = Math.cos(galAngle) * labelDist;
            const ly = Math.sin(galAngle) * labelDist;
            const youY = -domainR - 8;

            const planetTotal = systems.reduce((s, sys) => s + (sys.planets?.length || 0), 0);

            nodes += `
            <g class="z-galaxy-cluster ${curGal ? 'current' : ''}" transform="translate(${x.toFixed(1)},${y.toFixed(1)})">
                ${local}
                <g class="z-node galaxy ${curGal ? 'current' : ''}" data-galaxy="${g.id}">
                    <circle class="z-node-halo" r="11" style="stroke:${fill}"/>
                    <circle class="z-node-body" r="6.5" style="fill:${fill}"/>
                    <circle class="z-node-core" r="2"/>
                    <text class="z-node-label z-gal-label" x="${lx.toFixed(1)}" y="${ly.toFixed(1)}">${escapeXml(g.name)}</text>
                    <text class="z-gal-meta" x="${lx.toFixed(1)}" y="${(ly + 10).toFixed(1)}">${sysN} sys · ${planetTotal} wrld</text>
                    ${curGal ? `<text class="z-you" y="${youY}">● YOU</text>` : ''}
                </g>
            </g>`;
        });

        const hues = galaxies.map(g => g.hue ?? 200);
        return `
        <div class="zodiac-frame">
            <div class="zodiac-corner tl"></div><div class="zodiac-corner tr"></div>
            <div class="zodiac-corner bl"></div><div class="zodiac-corner br"></div>
            <svg class="zodiac-svg" viewBox="0 0 ${VB} ${VB}" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Galaxy zodiac">
                <defs>
                    ${this._zodiacSpaceDefs('zG', VB)}
                    <filter id="zSoftGlow" x="-35%" y="-35%" width="170%" height="170%">
                        <feGaussianBlur stdDeviation="1.2" result="b"/>
                        <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
                    </filter>
                </defs>
                ${this._zodiacNebulaLayer(VB, 71, hues, 'zG')}
                ${this._zodiacFarGiants(VB, 88, galaxies.flatMap(g => (g.systems || []).flatMap(s => s.planets || [])).slice(0, 8), 'zG')}
                ${this._zodiacStarField(VB, 96)}
                <g filter="url(#zSoftGlow)">${rings}${links}${nodes}</g>
            </svg>
        </div>`;
    }

    /** System orrery — concentric orbits + house wheel (well-spaced planets) */
    _buildZodiacSystemMap(sys) {
        const VB = 460;
        const cx = 230, cy = 230;
        const planets = sys?.planets || [];
        const count = Math.max(planets.length, 1);
        const starColor = sys?.starColor || '#ffeeaa';
        const selectedId = this._selectedPlanetId || this.cosmos.current.planetId;
        const currentId = this.cosmos.current.planetId;

        // Wide, evenly stepped orbits — min ~22 units between rings so nodes don't collide
        const rMin = 78;
        const rMax = 168;
        const minGap = 22;
        const span = count <= 1 ? 0 : Math.max(rMax - rMin, (count - 1) * minGap);
        const orbitR = (i) => (count === 1 ? (rMin + rMax) / 2 : rMin + (i / (count - 1)) * span);

        // Precompute spread positions (even angles + stepped orbits)
        const placed = planets.map((p, i) => {
            const a = this._planetMapAngle(p, i, count);
            const r = orbitR(i);
            return {
                p, i, a, r,
                x: cx + Math.cos(a) * r,
                y: cy + Math.sin(a) * r,
            };
        });

        let decor = '';
        // Outer zodiac wheel
        decor += `<circle class="z-ring z-ring-outer" cx="${cx}" cy="${cy}" r="210"/>`;
        decor += `<circle class="z-ring z-ring-mid" cx="${cx}" cy="${cy}" r="198"/>`;

        const houses = 12;
        for (let i = 0; i < houses; i++) {
            const a = (i / houses) * Math.PI * 2 - Math.PI / 2;
            decor += `<line class="z-house" x1="${cx + Math.cos(a) * 198}" y1="${cy + Math.sin(a) * 198}"
                x2="${cx + Math.cos(a) * 210}" y2="${cy + Math.sin(a) * 210}"/>`;
            decor += `<line class="z-ray" x1="${cx}" y1="${cy}"
                x2="${cx + Math.cos(a) * 198}" y2="${cy + Math.sin(a) * 198}"/>`;
            const lx = cx + Math.cos(a + Math.PI / houses) * 204;
            const ly = cy + Math.sin(a + Math.PI / houses) * 204;
            const roman = ['I','II','III','IV','V','VI','VII','VIII','IX','X','XI','XII'][i];
            decor += `<text class="z-house-label" x="${lx}" y="${ly}">${roman}</text>`;
        }
        for (let i = 0; i < 72; i++) {
            const a = (i / 72) * Math.PI * 2;
            const long = i % 6 === 0;
            decor += `<line class="z-tick ${long ? 'long' : ''}"
                x1="${cx + Math.cos(a) * (long ? 193 : 195)}" y1="${cy + Math.sin(a) * (long ? 193 : 195)}"
                x2="${cx + Math.cos(a) * 198}" y2="${cy + Math.sin(a) * 198}"/>`;
        }

        // Orbit rings only (no constellation chords — they clutter close nodes)
        let orbits = '';
        for (let i = 0; i < count; i++) {
            const r = orbitR(i);
            orbits += `<circle class="z-orbit" cx="${cx}" cy="${cy}" r="${r}"/>`;
        }

        // Selection ray + soft arc
        let selectDecor = '';
        const sel = placed.find(o => o.p.id === selectedId);
        if (sel) {
            selectDecor = `<line class="z-select-ray" x1="${cx}" y1="${cy}" x2="${sel.x}" y2="${sel.y}"/>
                <circle class="z-select-arc" cx="${cx}" cy="${cy}" r="${sel.r}"/>`;
        }

        // Star (compact so inner orbits stay clear)
        const star = `
            <g class="z-star" pointer-events="none">
                <circle class="z-star-corona" cx="${cx}" cy="${cy}" r="22" style="fill:${starColor}"/>
                <circle class="z-star-body" cx="${cx}" cy="${cy}" r="11" style="fill:${starColor}"/>
                <circle class="z-star-core" cx="${cx}" cy="${cy}" r="3.5"/>
                <text class="z-star-label" x="${cx}" y="${cy + 36}">${escapeXml(sys?.name || 'SYSTEM')}</text>
            </g>`;

        // Planets — smaller bodies, labels pushed radially outward
        let nodes = '';
        placed.forEach(({ p, a, x, y, r }) => {
            const cur = p.id === currentId;
            const selN = p.id === selectedId;
            const typeGlyph = planetTypeGlyph(p.type);
            const isGas = p.type === 'gas' || (p.hasRings && p.type !== 'ice');
            // Gas worlds read larger; still capped so neighbors don't merge
            const size = Math.min(isGas ? 14 : 11, (isGas ? 9 : 7.5) + (p.size || 0.8) * 2.2);
            const labelR = size + 17;
            const lx = Math.cos(a) * labelR;
            const ly = Math.sin(a) * labelR;
            const gx = Math.cos(a) * -2;
            const gy = -size - 11;
            nodes += `
            <g class="z-node planet ${cur ? 'current' : ''} ${selN ? 'selected' : ''} ${isGas ? 'gas' : ''} type-${p.type || 'barren'}"
               data-planet="${p.id}" transform="translate(${x.toFixed(1)},${y.toFixed(1)})">
                <circle class="z-node-halo" r="${size + 8}"/>
                ${this._zodiacPlanetArt(p, size)}
                ${p.hasRings ? `<ellipse class="z-planet-rings" cx="0" cy="0" rx="${size + 6}" ry="${(size * 0.34).toFixed(1)}" transform="rotate(-25)"/>` : ''}
                <image href="${p.icon}" x="${(-size * 0.82).toFixed(1)}" y="${(-size * 0.82).toFixed(1)}"
                    width="${(size * 1.64).toFixed(1)}" height="${(size * 1.64).toFixed(1)}"
                    clip-path="circle(${(size * 0.82).toFixed(1)}px at center)" opacity="0.88"/>
                <text class="z-type-glyph" x="${gx.toFixed(1)}" y="${gy.toFixed(1)}">${typeGlyph}</text>
                <text class="z-node-label" x="${lx.toFixed(1)}" y="${ly.toFixed(1)}">${escapeXml(shortPlanetName(p.name))}</text>
                ${p.hasStation ? `<g class="z-station" transform="translate(${(size * 0.9).toFixed(1)},${(-size * 0.9).toFixed(1)})">
                    <rect x="-5" y="-4" width="10" height="7" rx="1"/>
                    <rect x="-2" y="-7" width="4" height="3" rx="0.5"/>
                </g>` : ''}
                ${cur ? `<text class="z-you" y="${(-size - 22).toFixed(1)}">● HERE</text>` : ''}
            </g>`;
        });

        return `
        <div class="zodiac-frame">
            <div class="zodiac-corner tl"></div><div class="zodiac-corner tr"></div>
            <div class="zodiac-corner bl"></div><div class="zodiac-corner br"></div>
            <svg class="zodiac-svg" viewBox="0 0 ${VB} ${VB}" preserveAspectRatio="xMidYMid meet" role="img" aria-label="System orrery">
                <defs>
                    ${this._zodiacSpaceDefs('zS', VB)}
                    <filter id="zGlowS" x="-40%" y="-40%" width="180%" height="180%">
                        <feGaussianBlur stdDeviation="1.5" result="b"/>
                        <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
                    </filter>
                </defs>
                ${this._zodiacNebulaLayer(VB, 53, planets.map(p => hueFromHex(p.color)), 'zS')}
                ${this._zodiacFarGiants(VB, 61, planets, 'zS')}
                ${this._zodiacStarField(VB, 84)}
                <g filter="url(#zGlowS)">
                    ${decor}
                    ${orbits}
                    ${selectDecor}
                    ${star}
                    ${nodes}
                </g>
            </svg>
        </div>`;
    }

    /**
     * Even angular spacing around the star so planets never clump.
     * Tiny deterministic jitter only — keeps map readable.
     */
    _planetMapAngle(p, index, count) {
        const n = Math.max(count, 1);
        // Even house-style spacing starting at top
        const base = (index / n) * Math.PI * 2 - Math.PI / 2;
        // Optional micro-jitter from id (≤ ±6°) so layouts feel organic without clustering
        let jitter = 0;
        if (p?.id) {
            let h = 0;
            for (let i = 0; i < p.id.length; i++) h = (h * 31 + p.id.charCodeAt(i)) | 0;
            jitter = ((h % 1000) / 1000 - 0.5) * (Math.PI / 15); // ±6°
        }
        // Keep jitter small enough that min angular gap stays ≥ ~20° for n≤12
        const maxJ = Math.min(Math.PI / 15, (Math.PI / n) * 0.2);
        jitter = Math.max(-maxJ, Math.min(maxJ, jitter));
        return base + jitter;
    }

    _zodiacStarField(size, count = 60) {
        let s = '';
        // Deterministic pseudo-stars for stable layout
        let seed = 42;
        const rnd = () => {
            seed = (seed * 16807) % 2147483647;
            return (seed - 1) / 2147483646;
        };
        for (let i = 0; i < count; i++) {
            const x = rnd() * size;
            const y = rnd() * size;
            const r = 0.4 + rnd() * 1.2;
            const o = 0.15 + rnd() * 0.55;
            s += `<circle class="z-dust" cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${r.toFixed(2)}" opacity="${o.toFixed(2)}"/>`;
        }
        // A few brighter "foreground" stars
        for (let i = 0; i < Math.round(count * 0.12); i++) {
            const x = rnd() * size;
            const y = rnd() * size;
            const r = 1.2 + rnd() * 1.6;
            s += `<circle class="z-dust z-dust-bright" cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${r.toFixed(2)}" opacity="${(0.55 + rnd() * 0.4).toFixed(2)}"/>`;
        }
        return `<g class="z-dust-layer">${s}</g>`;
    }

    /** Shared space-map gradients + blur filters (mist, gas giants). */
    _zodiacSpaceDefs(prefix, VB) {
        return `
            <radialGradient id="${prefix}Void" cx="48%" cy="40%" r="68%">
                <stop offset="0%" stop-color="#1a2450"/>
                <stop offset="32%" stop-color="#0c1430"/>
                <stop offset="68%" stop-color="#050814"/>
                <stop offset="100%" stop-color="#010208"/>
            </radialGradient>
            <radialGradient id="${prefix}Milky" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stop-color="rgba(200,210,255,0.32)"/>
                <stop offset="40%" stop-color="rgba(140,100,200,0.14)"/>
                <stop offset="100%" stop-color="transparent"/>
            </radialGradient>
            <filter id="${prefix}Mist" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="16"/>
            </filter>
            <filter id="${prefix}Gas" x="-90%" y="-90%" width="280%" height="280%">
                <feGaussianBlur stdDeviation="6"/>
            </filter>
            <filter id="${prefix}PlanetGlow" x="-80%" y="-80%" width="260%" height="260%">
                <feGaussianBlur stdDeviation="3.4"/>
            </filter>
        `;
    }

    _zodiacNebulaLayer(VB, seed, hues = [], prefix = 'zS') {
        let s = (seed || 91) | 0;
        const rnd = () => {
            s = (s * 16807) % 2147483647;
            return (s - 1) / 2147483646;
        };
        const list = (hues && hues.length) ? hues : [210, 280, 30, 160];
        let blobs = '';
        for (let i = 0; i < 6; i++) {
            // Keep mist cool/space-like so it never paints a brown square over the sky
            const raw = list[i % list.length] ?? (210 + rnd() * 50);
            const hue = 200 + ((raw - 200) * 0.25);
            const cx = rnd() * VB;
            const cy = rnd() * VB;
            const rx = 80 + rnd() * 150;
            const ry = 44 + rnd() * 100;
            const rot = rnd() * 90 - 45;
            const op = 0.05 + rnd() * 0.07;
            blobs += `<ellipse class="z-nebula" cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}"
                rx="${rx.toFixed(1)}" ry="${ry.toFixed(1)}"
                transform="rotate(${rot.toFixed(1)} ${cx.toFixed(1)} ${cy.toFixed(1)})"
                fill="hsla(${Math.round(hue)},72%,58%,${op.toFixed(2)})"/>`;
        }
        blobs += `<ellipse class="z-milky" cx="${(VB / 2).toFixed(1)}" cy="${(VB / 2).toFixed(1)}"
            rx="${(VB * 0.5).toFixed(1)}" ry="${(VB * 0.085).toFixed(1)}"
            transform="rotate(-26 ${(VB / 2).toFixed(1)} ${(VB / 2).toFixed(1)})"
            fill="url(#${prefix}Milky)"/>`;
        return `<g class="z-mist-layer" filter="url(#${prefix}Mist)">${blobs}</g>`;
    }

    _zodiacFarGiants(VB, seed, planets = [], prefix = 'zS') {
        let s = (seed || 44) | 0;
        const rnd = () => {
            s = (s * 16807) % 2147483647;
            return (s - 1) / 2147483646;
        };
        const gas = (planets || []).filter(p => p.type === 'gas' || p.hasRings);
        const extras = [];
        const n = 2 + (gas.length ? 1 : 0);
        for (let i = 0; i < n; i++) {
            const src = gas[i] || planets[i] || {};
            const col = src.color || `hsl(${Math.round(30 + rnd() * 40)},60%,55%)`;
            const light = src.colorLight || '#ffe8b0';
            const cx = 40 + rnd() * (VB - 80);
            const cy = 36 + rnd() * (VB - 72);
            const r = 22 + rnd() * 28;
            extras.push(`
            <g class="z-far-giant" pointer-events="none" transform="translate(${cx.toFixed(1)},${cy.toFixed(1)})">
                <circle r="${(r * 2.1).toFixed(1)}" fill="${col}" opacity="0.28" filter="url(#${prefix}Gas)"/>
                <circle r="${r.toFixed(1)}" fill="${col}" opacity="0.22" filter="url(#${prefix}PlanetGlow)"/>
                <ellipse rx="${(r * 1.55).toFixed(1)}" ry="${(r * 0.28).toFixed(1)}"
                    transform="rotate(-22)" fill="none" stroke="${light}" stroke-width="1.6" opacity="0.2"/>
            </g>`);
        }
        return `<g class="z-far-layer">${extras.join('')}</g>`;
    }

    _zodiacPlanetArt(p, size) {
        const id = String(p.id || 'p').replace(/[^a-z0-9]/gi, '');
        const mid = p.color || '#88aacc';
        const dark = p.colorDark || '#223344';
        const light = p.colorLight || '#cfe8ff';
        const type = p.type || 'barren';
        const isGas = type === 'gas';
        let extra = '';
        if (isGas) {
            extra += `<circle class="z-gas-halo" r="${(size * 2.15).toFixed(1)}" fill="${mid}" filter="url(#zSGas)"/>`;
            extra += `<ellipse class="z-gas-band" cx="0" cy="${(-size * 0.18).toFixed(1)}" rx="${(size * 0.98).toFixed(1)}" ry="${(size * 0.22).toFixed(1)}" fill="${light}" opacity="0.32"/>`;
            extra += `<ellipse class="z-gas-band" cx="0" cy="${(size * 0.24).toFixed(1)}" rx="${(size * 0.9).toFixed(1)}" ry="${(size * 0.15).toFixed(1)}" fill="${dark}" opacity="0.4"/>`;
        } else if (type === 'ice') {
            extra += `<circle r="${(size * 1.4).toFixed(1)}" fill="${light}" opacity="0.24" filter="url(#zSPlanetGlow)"/>`;
        } else if (type === 'lava') {
            extra += `<circle r="${(size * 1.45).toFixed(1)}" fill="#ff5522" opacity="0.3" filter="url(#zSPlanetGlow)"/>`;
            extra += `<circle cx="${(-size * 0.22).toFixed(1)}" cy="${(size * 0.12).toFixed(1)}" r="${(size * 0.2).toFixed(1)}" fill="#ffcc55" opacity="0.75"/>`;
        } else if (type === 'storm') {
            extra += `<circle r="${(size * 1.5).toFixed(1)}" fill="#8866ff" opacity="0.2" filter="url(#zSGas)"/>`;
            extra += `<path d="M ${(-size * 0.45).toFixed(1)} 0 Q 0 ${(-size * 0.5).toFixed(1)} ${(size * 0.42).toFixed(1)} 0"
                fill="none" stroke="${light}" stroke-width="1.1" opacity="0.55"/>`;
        } else if (type === 'toxic') {
            extra += `<circle r="${(size * 1.5).toFixed(1)}" fill="#88ff22" opacity="0.18" filter="url(#zSGas)"/>`;
        } else if (type === 'crystal') {
            extra += `<circle r="${(size * 1.25).toFixed(1)}" fill="${light}" opacity="0.22" filter="url(#zSPlanetGlow)"/>`;
        } else if (type === 'ocean') {
            extra += `<circle cx="${(-size * 0.2).toFixed(1)}" cy="${(-size * 0.15).toFixed(1)}" r="${(size * 0.35).toFixed(1)}" fill="${light}" opacity="0.25"/>`;
        }
        return `
            ${extra}
            <circle class="z-node-body" r="${size}"
                fill="url(#zBody_${id})" stroke="${light}" stroke-width="${isGas ? 1.8 : 1.35}"/>
            <defs>
                <radialGradient id="zBody_${id}" cx="32%" cy="28%" r="74%">
                    <stop offset="0%" stop-color="${light}"/>
                    <stop offset="48%" stop-color="${mid}"/>
                    <stop offset="100%" stop-color="${dark}"/>
                </radialGradient>
            </defs>`;
    }

    showPlanetDetail(planetId, opts = {}) {
        const detail = document.getElementById('planetDetail');
        if (!detail) return;
        const p = this.cosmos.getPlanet(planetId);
        if (!p) {
            detail.innerHTML = '';
            return;
        }
        const cost = this.cosmos.fuelCost(p.id);
        const here = p.id === this.cosmos.current.planetId;
        const faction = getFaction(p);
        const dangerPct = (p.danger * 100) | 0;
        const wealthPct = (p.wealth * 100) | 0;
        const accent = p.colorLight || p.color || '#00d9ff';
        const fuelHave = Math.floor(this.inventory.fuel);
        const canJump = !here && fuelHave >= cost && cost > 0;

        detail.innerHTML = `
            <div class="zodiac-detail" style="--zd-accent:${accent}">
                <div class="zd-top">
                    <div class="zd-left">
                        <div class="zd-icon-wrap">
                            <img class="zd-icon" src="${p.icon}" alt="">
                            <span class="zd-glyph-big">${planetTypeGlyph(p.type)}</span>
                        </div>
                    </div>
                    <div class="zd-body">
                        <div class="zd-title-row">
                            <div class="zd-name">${p.name}</div>
                            ${here ? `<span class="zd-status here">IN ORBIT</span>` : `<span class="zd-status remote">REMOTE</span>`}
                        </div>
                        <div class="zd-chips">
                            <span class="zd-chip type">${p.type}</span>
                            <span class="zd-chip faction" style="--chip:${faction.playerPrimary}">${faction.label}</span>
                            ${p.hasStation
                                ? `<span class="zd-chip station">⚓ ${p.stationName}</span>`
                                : `<span class="zd-chip muted">No station</span>`}
                            <span class="zd-chip shop">⛽ Fuel pad</span>
                            ${(p.hasStation || (p.wealth || 0) > 0.28) ? `<span class="zd-chip shop">✚ Market</span>` : ''}
                            ${(p.hasStation || (p.wealth || 0) > 0.48) ? `<span class="zd-chip shop">⚔ Armory</span>` : ''}
                        </div>
                    </div>
                </div>

                <div class="zd-metrics">
                    <div class="zd-metric">
                        <div class="zd-metric-head">
                            <span>Danger</span>
                            <b>${dangerPct}%</b>
                        </div>
                        <div class="zd-metric-track">
                            <i class="danger" style="width:${dangerPct}%"></i>
                        </div>
                    </div>
                    <div class="zd-metric">
                        <div class="zd-metric-head">
                            <span>Wealth</span>
                            <b>${wealthPct}%</b>
                        </div>
                        <div class="zd-metric-track">
                            <i class="wealth" style="width:${wealthPct}%"></i>
                        </div>
                    </div>
                </div>

                <div class="zd-footer">
                    ${here
                        ? `<div class="zd-here-banner">
                            <span class="zd-here-dot"></span>
                            You are orbiting this world
                           </div>`
                        : ''}
                    <div class="zd-jump-row">
                        <div class="zd-cost-block">
                            <span class="zd-cost-label">${here ? 'Current orbit' : 'Hyperjump fuel'}</span>
                            <span class="zd-cost-value ${here ? 'ok' : canJump ? 'ok' : 'bad'}">
                                <b>${here ? 0 : cost}</b>
                                <small>/ ${fuelHave} avail</small>
                            </span>
                        </div>
                        <button type="button" class="button jump-btn ${canJump ? '' : 'disabled'}" id="doJumpBtn"
                            ${canJump ? '' : 'disabled'}>
                            ⚡ Hyperjump
                        </button>
                    </div>
                </div>
            </div>
        `;
        const btn = document.getElementById('doJumpBtn');
        if (btn && canJump) btn.onclick = () => this.doHyperjump(p.id);

        const title = document.getElementById('planetPopupTitle');
        if (title) title.textContent = p.name;
        const sub = document.getElementById('planetPopupSub');
        if (sub) {
            const loc = this.cosmos.getCurrentLocation();
            const sys = this.cosmos.getSystem(p.systemId);
            sub.textContent = `${sys?.name || loc.system?.name || 'System'} · ${p.type}${here ? ' · in orbit' : ''}`;
        }

        const view = document.getElementById('starMapView');
        view?.querySelectorAll('.z-node.planet, .z-mini-planet').forEach(n => {
            n.classList.toggle('selected', n.getAttribute('data-planet') === p.id);
        });

        if (opts.open) {
            const pop = document.getElementById('planetPopup');
            if (pop) pop.style.display = 'block';
        }
    }

    doHyperjump(planetId) {
        const { ok, cost } = this.cosmos.canTravel(planetId, this.inventory.fuel);
        if (!ok) {
            alert(`Not enough fuel (need ${cost}, have ${Math.floor(this.inventory.fuel)})`);
            return;
        }
        this.inventory.spendFuel(cost);
        const overlay = document.getElementById('hyperOverlay');
        const text = document.getElementById('hyperText');
        const planet = this.cosmos.getPlanet(planetId);
        if (text) text.textContent = `JUMP → ${planet.name}`;
        if (overlay) overlay.style.display = 'flex';

        setTimeout(() => {
            // Leaving a planet voids any pocket-dimension stash
            if (this.arenaMode?.stash) {
                const st = this.arenaMode.stash;
                (st.enemies || []).forEach(e => {
                    if (e.isBoss) this.bossPool.release(e);
                    else this.enemyPool.release(e);
                });
                if (st.projectiles) this.projectilePool.releaseAll(st.projectiles);
            }
            this.arenaMode = null;

            const dest = this.cosmos.travelTo(planetId);
            this.applyPlanetEnvironment(dest);
            this.trackProgress({
                hyperjumps: 1,
                planetsVisited: dest.id,
                planetTypes: dest.type ? { [dest.type]: true } : {},
            });
            // Soft reset combat pressure on arrival
            this.enemies.forEach(e => {
                if (e.isBoss) this.bossPool.release(e);
                else this.enemyPool.release(e);
            });
            this.enemies = [];
            this.projectilePool.releaseAll(this.projectiles);
            this.projectiles = [];
            this.portals = [];
            this.player.x = WORLD_WIDTH / 2;
            this.player.y = WORLD_HEIGHT / 2;
            this.camera.x = this.player.x - this.canvas.width / 2;
            this.camera.y = this.player.y - this.canvas.height / 2;
            this.spawnPortal();
            if (overlay) overlay.style.display = 'none';
            this.closeAllPopups();
            this.paused = false;
            this.updateUI();
            const faction = this._planetFaction;
            this.floatingTexts.push({
                x: this.player.x,
                y: this.player.y - 50,
                text: `Arrived: ${dest.name}`,
                color: dest.colorLight || faction?.playerPrimary || '#00d9ff',
                age: 0,
                duration: 120,
                pulse: true,
            });
            if (faction?.label) {
                this.floatingTexts.push({
                    x: this.player.x,
                    y: this.player.y - 20,
                    text: faction.label,
                    color: faction.playerPrimary || '#88eeff',
                    age: 0,
                    duration: 100,
                    pulse: false,
                });
            }
            if (this.worldShops?.length) {
                this.floatingTexts.push({
                    x: this.player.x,
                    y: this.player.y + 10,
                    text: `${this.worldShops.length} shop pads · E to dock`,
                    color: '#ffd070',
                    age: 0,
                    duration: 110,
                    pulse: false,
                });
            }
        }, 900);
    }

    applyPlanetEnvironment(planet) {
        if (!planet) return;
        const atmo = planet.atmosphere || {
            bg: planet.bgColor || '#03040a',
            accent: planet.bgAccent || planet.colorDark || '#0a2848',
            starColor: '#cfe8ff',
            mist: 0.2,
            mistColor: '40,60,90',
            starCount: 100,
            mistSeed: 0,
            flare: false,
        };
        this.currentBackground = {
            id: planet.id,
            name: planet.name,
            color: atmo.bg,
            accent: atmo.accent,
            starColor: atmo.starColor,
            mist: atmo.mist,
            mistColor: atmo.mistColor,
            flare: atmo.flare,
            flareColor: atmo.flareColor || planet.colorLight,
            type: planet.type,
        };
        this._atmosphere = atmo;
        this._planetDanger = planet.danger || 0.5;

        // Faction: new enemies + player/bot ships for this world
        const faction = getFaction(planet);
        this._planetFaction = faction;
        if (this.player) this.player.applyFactionTheme(faction);
        if (this.bots?.length) {
            for (const bot of this.bots) {
                if (!bot.isDead) bot.applyFactionTheme(faction);
            }
        }

        // Rebuild stars + mist for this world
        this._ensureStars(atmo, true);
        this._buildSkyBodies(planet);
        if (!this.arenaMode) this.spawnWorldShops(planet);
        // Match canvas CSS clear color
        if (this.canvas) this.canvas.style.background = atmo.bg;
    }

    /** Distant gas giants / local world hanging in the sky — changes per planet. */
    _buildSkyBodies(planet) {
        const type = planet?.type || 'barren';
        const color = planet?.color || '#6688aa';
        const light = planet?.colorLight || '#cfe8ff';
        const dark = planet?.colorDark || '#1a2230';
        const gas = type === 'gas' || type === 'storm';
        this.skyBodies = [
            {
                nx: gas ? 0.82 : 0.14,
                ny: gas ? 0.22 : 0.18,
                r: gas ? 300 : type === 'lava' ? 190 : 150,
                color, light, dark, type,
                rings: !!(planet?.hasRings || type === 'gas'),
                blur: gas || type === 'toxic' || type === 'ice',
            },
            {
                nx: 0.88,
                ny: 0.78,
                r: type === 'gas' ? 90 : 62,
                color: light,
                light,
                dark,
                type: 'gas',
                rings: true,
                blur: true,
            },
        ];
        if (type === 'ice' || type === 'crystal') {
            this.skyBodies.push({
                nx: 0.55, ny: 0.08, r: 36,
                color: light, light, dark, type: 'ice',
                rings: false, blur: true,
            });
        }
    }

    renderTrade() {
        const planet = this.cosmos.getPlanet();
        const beaconLive = this.tradeBeacon && Date.now() < (this.tradeBeacon.expires || 0);
        const shop = this._activeShop;
        const line = document.getElementById('tradeStationLine');
        const title = document.querySelector('#tradePopup h2');

        let stock = null;
        let stockSource = null; // 'station' | 'beacon' | 'shop'
        if (shop) {
            stock = shop.stock || [];
            stockSource = 'shop';
            if (line) {
                line.textContent = `${shop.name} · ${shop.label} · ${shop.planetName || planet?.name || 'orbit'}`;
            }
            if (title) title.textContent = shop.kind === 'fuel' ? '⛽ Fuel Depot' : shop.kind === 'armory' ? '⚔ Armory' : '✚ Market Pad';
        } else if (beaconLive) {
            stock = this.tradeBeacon.stock;
            stockSource = 'beacon';
            const left = Math.max(0, Math.ceil((this.tradeBeacon.expires - Date.now()) / 1000));
            if (line) {
                line.textContent = `${this.tradeBeacon.name} · beacon · ${left}s left · buy gear to grow power`;
            }
            if (title) title.textContent = '📡 Trade Beacon';
        } else if (planet?.hasStation) {
            stock = planet.stock || [];
            stockSource = 'station';
            if (line) {
                line.textContent = `${planet.stationName} · ${planet.name} · wealth ${(planet.wealth * 100) | 0}%`;
            }
            if (title) title.textContent = '🏪 Station Market';
        } else {
            if (line) line.textContent = 'No market — fly to a battlefield pad or press Y';
            if (title) title.textContent = '🏪 Market';
            stock = [];
        }

        const svc = document.getElementById('tradeServices');
        if (svc) {
            if (shop?.kind === 'fuel' || (shop?.kind === 'market' && this.inventory)) {
                const missing = Math.max(0, Math.floor(this.inventory.maxFuel - this.inventory.fuel));
                const cost = Math.max(1, Math.ceil(missing * 0.55));
                svc.innerHTML = missing > 0
                    ? `<button type="button" class="button trade-svc-btn" id="fillFuelBtn">Fill tank +${missing} · ${cost}✦</button>`
                    : `<span class="trade-svc-ok">Tank full</span>`;
                document.getElementById('fillFuelBtn')?.addEventListener('click', () => this.fillFuelTank());
            } else if (shop?.kind === 'armory') {
                svc.innerHTML = `<span class="trade-svc-note">Guns, hull and artefacts — equip them in Hangar (I)</span>`;
            } else {
                svc.innerHTML = '';
            }
        }

        const credits = document.getElementById('tradeCredits');
        if (credits) credits.textContent = `${this.player.coins} ✦`;

        const stockEl = document.getElementById('tradeStock');
        if (stockEl) {
            stockEl.innerHTML = (stock || []).map((item, i) => `
                <div class="item-card r-${item.rarity}" data-buy="${i}" title="${item.desc || ''}">
                    <button type="button" class="item-info-btn" data-info-buy="${i}" title="What is this?">?</button>
                    <img src="${resolveItemIcon(item)}" alt="${item.name}" loading="lazy" decoding="async">
                    <div class="item-name">${item.artefact ? '✦ ' : ''}${item.name}</div>
                    <div class="item-price">${item.price} ✦</div>
                </div>
            `).join('') || '<em style="color:#668">Empty</em>';
            stockEl.querySelectorAll('[data-info-buy]').forEach(btn => {
                btn.onclick = (e) => {
                    e.stopPropagation();
                    const i = +btn.getAttribute('data-info-buy');
                    const list = stockSource === 'beacon'
                        ? this.tradeBeacon.stock
                        : stockSource === 'shop'
                            ? this._activeShop?.stock
                            : planet.stock;
                    const item = list?.[i];
                    if (item) this.showItemInfo(item, { tradeBuyIndex: i, tradeSource: stockSource });
                };
            });
            stockEl.querySelectorAll('[data-buy]').forEach(n => {
                n.onclick = () => {
                    const i = +n.getAttribute('data-buy');
                    this._buyTradeItem(i, stockSource, planet);
                };
            });
        }

        const bagEl = document.getElementById('tradePlayer');
        if (bagEl) {
            bagEl.innerHTML = this.inventory.slots.map((item, i) => {
                if (!item) return '';
                const sell = getSellPrice(item);
                return `<div class="item-card r-${item.rarity}" data-sell="${i}" title="Sell for ${sell}">
                    <button type="button" class="item-info-btn" data-info-sell="${i}" title="What is this?">?</button>
                    <img src="${resolveItemIcon(item)}" alt="${item.name}" loading="lazy" decoding="async">
                    <div class="item-name">${item.artefact ? '✦ ' : ''}${item.name}</div>
                    <div class="item-price">sell ${sell}</div>
                </div>`;
            }).join('') || '<em style="color:#668">Empty cargo</em>';
            bagEl.querySelectorAll('[data-info-sell]').forEach(btn => {
                btn.onclick = (e) => {
                    e.stopPropagation();
                    const i = +btn.getAttribute('data-info-sell');
                    const item = this.inventory.slots[i];
                    if (item) this.showItemInfo(item, { bagIndex: i, tradeSell: true });
                };
            });
            bagEl.querySelectorAll('[data-sell]').forEach(n => {
                n.onclick = () => {
                    const i = +n.getAttribute('data-sell');
                    this._sellTradeItem(i, stockSource);
                };
            });
        }
    }

    _buyTradeItem(i, stockSource, planet) {
        const list = stockSource === 'beacon'
            ? this.tradeBeacon?.stock
            : stockSource === 'shop'
                ? this._activeShop?.stock
                : planet?.stock;
        const item = list?.[i];
        if (!item) return;
        if (this.player.coins < item.price) return;
        if (!this.inventory.add(item)) return;
        this.player.coins -= item.price;
        if (item.shape === 'fuel') this.inventory.addFuel(item.stats?.fuel || 10);
        list.splice(i, 1);
        if (stockSource === 'station' || stockSource === 'shop') this.trackProgress({ stationTrades: 1 });
        if (item.artefact) this.trackProgress({ artefactsFound: 1 });
        applyLoadoutToPlayer(this.player, this.inventory);
        this.hideItemInfo();
        this.renderTrade();
        this.updateUI();
    }

    _sellTradeItem(i, stockSource) {
        const item = this.inventory.slots[i];
        if (!item) return;
        const sell = getSellPrice(item);
        this.player.coins += sell * (item.qty || 1);
        this.trackProgress({
            crystalsEarned: sell * (item.qty || 1),
            stationTrades: (stockSource === 'station' || stockSource === 'shop') ? 1 : 0,
        });
        this.inventory.removeAt(i);
        this.hideItemInfo();
        this.renderTrade();
        this.updateUI();
    }

    _onBadgeUnlock(badge) {
        if (!badge) return;
        const stack = document.getElementById('badgeToastStack');
        if (stack) {
            const el = document.createElement('div');
            el.className = `badge-toast tier-${badge.tier || 'bronze'}`;
            el.innerHTML = `
                <span class="badge-toast-icon">${badge.icon}</span>
                <div class="badge-toast-text">
                    <div class="badge-toast-label">Badge unlocked</div>
                    <div class="badge-toast-name">${badge.name}</div>
                </div>`;
            stack.appendChild(el);
            setTimeout(() => el.classList.add('show'), 20);
            setTimeout(() => {
                el.classList.remove('show');
                setTimeout(() => el.remove(), 400);
            }, 3800);
        }
        if (this.player) {
            this.floatingTexts.push({
                x: this.player.x,
                y: this.player.y - 70,
                text: `${badge.icon} ${badge.name}`,
                color: this.progress.tierColor(badge.tier),
                age: 0,
                duration: 120,
                pulse: true,
            });
        }
        this.telegram?.hapticFeedback?.('success');
    }

    /** Record progression stats + evaluate badges */
    trackProgress(patch) {
        if (!this.progress) return;
        this.progress.record(patch);
    }

    renderHangarBadges() {
        const body = document.getElementById('hangarBadgesBody');
        if (!body || !this.progress) return;
        this.progress.syncPilotLevel(this.player);
        const { unlocked, total } = this.progress.count();
        const s = this.progress.stats;
        const pilotLv = Math.max(this.player?.level || 1, s.pilotLevel || 1);
        const xp = this.player?.xp ?? s.pilotXp ?? 0;
        const need = this.player?.xpNeeded ?? 50;
        const pct = Math.round((xp / Math.max(1, need)) * 100);

        const unlockedIds = new Set(Object.keys(this.progress.unlocked));
        const cards = BADGE_DEFS.map((b) => {
            const on = unlockedIds.has(b.id);
            return `<div class="badge-card ${on ? 'unlocked' : 'locked'} tier-${b.tier}" title="${b.desc}">
                <div class="badge-card-icon">${on ? b.icon : '🔒'}</div>
                <div class="badge-card-name">${b.name}</div>
                <div class="badge-card-desc">${on ? b.desc : '???'}</div>
                <div class="badge-card-tier">${b.tier}${b.funny ? ' · fun' : ''}</div>
            </div>`;
        }).join('');

        body.innerHTML = `
            <div class="prog-header">
                <div class="prog-pilot">
                    <div class="prog-title">Pilot rank</div>
                    <div class="prog-level">Lv ${pilotLv}</div>
                    <div class="prog-xp-track"><i style="width:${Math.min(100, pct)}%"></i></div>
                    <div class="prog-xp-label">${xp} / ${need} XP · run + career</div>
                </div>
                <div class="prog-stats-mini">
                    <div><span>Kills</span><b>${s.kills}</b></div>
                    <div><span>Bosses</span><b>${s.bossKills}</b></div>
                    <div><span>Jumps</span><b>${s.hyperjumps}</b></div>
                    <div><span>Badges</span><b>${unlocked}/${total}</b></div>
                </div>
            </div>
            <p class="craft-intro">50 space badges — unlocks persist in this browser. Some are ridiculous on purpose.</p>
            <div class="badge-grid">${cards}</div>
        `;
    }

    /** Hangar Craft tab — mutate / upgrade modules & artefacts */
    renderHangarCraft() {
        const body = document.getElementById('hangarCraftBody');
        if (!body || !this.player) return;
        this.inventory.ensureSize();

        const rows = [];
        // Equipped
        for (const [slot, item] of Object.entries(this.inventory.equipped)) {
            if (!item || item.kind !== 'module') continue;
            rows.push({ item, where: 'eq', key: slot, label: `Equipped · ${slot}` });
        }
        // Cargo modules
        this.inventory.slots.forEach((item, i) => {
            if (!item || item.kind !== 'module') return;
            rows.push({
                item,
                where: 'bag',
                key: i,
                label: item.artefact ? 'Cargo · artefact' : 'Cargo · module',
            });
        });

        if (!rows.length) {
            body.innerHTML = `<div class="hab-empty">
                No modules or artefacts yet. Defeat enemies to loot gear, call a trade beacon (Y),
                or buy at stations — then mutate and upgrade here to grow stronger.
            </div>`;
            return;
        }

        body.innerHTML = `
            <p class="craft-intro">Mutate twists stats · Upgrade ranks power (and may raise rarity). Costs crystals ✦</p>
            <div class="craft-list">
                ${rows.map((row, idx) => {
                    const it = row.item;
                    const mCost = getMutateCost(it);
                    const uCost = getUpgradeCost(it);
                    const icon = resolveItemIcon(it);
                    return `<div class="craft-row r-${it.rarity}">
                        <img src="${icon}" alt="">
                        <div class="craft-meta">
                            <div class="craft-name">${it.artefact ? '✦ ' : ''}${it.name}</div>
                            <div class="craft-sub">${row.label} · ${(it.rarity || '').toUpperCase()}
                                ${it.mutations ? ` · mut ×${it.mutations}` : ''}
                                ${it.upgrades ? ` · up +${it.upgrades}` : ''}
                                ${it.broken ? ' · <span class="bad">BROKEN</span>' : ''}
                            </div>
                            <div class="craft-desc">${it.desc || ''}</div>
                        </div>
                        <div class="craft-actions">
                            <button type="button" class="craft-btn mutate" data-craft="mutate" data-idx="${idx}"
                                ${it.broken ? 'disabled' : ''}>Mutate ${mCost}✦</button>
                            <button type="button" class="craft-btn upgrade" data-craft="upgrade" data-idx="${idx}"
                                ${it.broken || (it.upgrades || 0) >= 5 ? 'disabled' : ''}>Upgrade ${uCost}✦</button>
                        </div>
                    </div>`;
                }).join('')}
            </div>
        `;

        body.querySelectorAll('[data-craft]').forEach(btn => {
            btn.onclick = () => {
                const idx = +btn.getAttribute('data-idx');
                const row = rows[idx];
                if (!row) return;
                const action = btn.getAttribute('data-craft');
                const cost = action === 'mutate' ? getMutateCost(row.item) : getUpgradeCost(row.item);
                if (this.player.coins < cost) {
                    this.floatingTexts.push({
                        x: this.player.x, y: this.player.y - 40,
                        text: `Need ${cost}✦`,
                        color: '#ff8866', age: 0, duration: 60, pulse: false,
                    });
                    return;
                }
                const result = action === 'mutate' ? mutateItem(row.item) : upgradeItem(row.item);
                if (!result.ok) return;
                this.player.coins -= cost;
                if (action === 'mutate') this.trackProgress({ mutations: 1 });
                else this.trackProgress({ upgrades: 1 });
                applyLoadoutToPlayer(this.player, this.inventory);
                this.renderHangarCraft();
                this.updateUI();
                this.telegram.hapticFeedback('impact');
            };
        });
    }

    gameOver() {
        this.multiplayer.sendState(this.player); // final state sync
        this.closeAllPopups();
        // Drop void-arena snapshot so a rematch starts clean
        this.arenaMode = null;
        this.running = false;
        this.trackProgress({ deaths: 1, setPilotLevel: this.player?.level });
        this.audio.playDeath();
        this.crazyGames.gameplayStop();

        const playerName = this.crazyGames.getUserName() ?? this.telegram.getPlayerName();
        this.leaderboard.addScore(playerName, this.player.coins, this.kills);

        document.getElementById('finalScore').textContent = this.player.coins;
        document.getElementById('finalKills').textContent = this.kills;
        const finalLevel = document.getElementById('finalLevel');
        if (finalLevel) finalLevel.textContent = this.currentLevel || this.player.level || 1;
        document.getElementById('gameOver').style.display = 'block';
        this.telegram.hapticFeedback('error');

        // Show midgame ad on game over (SDK enforces its own cooldown)
        this.crazyGames.showMidgameAd();
    }

    // Pause/resume helpers used by CrazyGames ad callbacks
    _cgPause() {
        this.paused = true;
        this.audio.setMusicEnabled(false);
    }

    _cgResume() {
        this.paused = false;
        this.audio.setMusicEnabled(true);
    }

    _hudEls() {
        if (this._hud) return this._hud;
        const id = (name) => document.getElementById(name);
        this._hud = {
            hp: id('hp'),
            coins: id('coins'),
            level: id('level'),
            xp: id('xp'),
            xpNeeded: id('xpNeeded'),
            kills: id('kills'),
            fuel: id('fuelStat'),
            cargoBadge: id('cargoBadge'),
            menuBadge: id('menuBadge'),
            loc: id('locationLabel'),
            hpFill: id('hpBarFill'),
            xpFill: id('xpBarFill'),
            killFill: id('killBarFill'),
            wave: id('waveLabel'),
            chips: id('powerUpChips'),
        };
        return this._hud;
    }

    _setText(el, value) {
        if (!el) return;
        const text = String(value);
        if (el.textContent !== text) el.textContent = text;
    }

    updateUI() {
        if (!this.player) return;
        const hud = this._hudEls();
        // Throttle DOM writes — full HUD every 4 frames is enough
        if ((this.frame & 3) !== 0 && this._uiCache) {
            // Still update HP bar every frame when low (feedback)
            if (this.player.hp / this.player.maxHp < 0.35 && hud.hpFill) {
                const ratio = Math.max(0, this.player.hp / this.player.maxHp);
                const width = `${ratio * 100}%`;
                if (this._hpWidth !== width) {
                    this._hpWidth = width;
                    hud.hpFill.style.width = width;
                }
            }
            return;
        }

        const hp = Math.max(0, Math.floor(this.player.hp));
        this._setText(hud.hp, hp);
        this._setText(hud.coins, this.player.coins);
        this._setText(hud.level, this.player.level);
        this._setText(hud.xp, this.player.xp);
        this._setText(hud.xpNeeded, this.player.xpNeeded);
        this._setText(hud.kills, this.kills);

        if (hud.fuel && this.inventory) this._setText(hud.fuel, Math.floor(this.inventory.fuel));

        // Cargo badge = filled slots (only show if bag not empty)
        const cargoBadge = hud.cargoBadge;
        const menuBadge = hud.menuBadge;
        if (cargoBadge && this.inventory) {
            let filled = 0;
            const slots = this.inventory.slots;
            for (let i = 0; i < slots.length; i++) if (slots[i]) filled++;
            if (filled > 0) {
                cargoBadge.hidden = false;
                this._setText(cargoBadge, filled);
                if (menuBadge) {
                    menuBadge.hidden = false;
                    this._setText(menuBadge, filled);
                }
            } else {
                cargoBadge.hidden = true;
                if (menuBadge) menuBadge.hidden = true;
            }
        }

        if ((this.frame & 31) === 0) this.refreshPilotHud();

        if (hud.loc && this.cosmos && (this.frame & 31) === 0) {
            const { galaxy, system, planet } = this.cosmos.getCurrentLocation();
            const fac = this._planetFaction?.label ? ` · ${this._planetFaction.label}` : '';
            this._setText(hud.loc, `${galaxy?.name || ''} › ${system?.name || ''} › ${planet?.name || ''} · ${(planet?.type || '').toUpperCase()}${fac}`);
        }

        if (hud.hpFill) {
            const ratio = Math.max(0, Math.min(1, this.player.hp / this.player.maxHp));
            const width = `${ratio * 100}%`;
            if (this._hpWidth !== width) {
                this._hpWidth = width;
                hud.hpFill.style.width = width;
            }
            const low = ratio < 0.3;
            const mid = ratio >= 0.3 && ratio < 0.55;
            if (this._hpLow !== low) {
                this._hpLow = low;
                hud.hpFill.classList.toggle('low', low);
            }
            if (this._hpMid !== mid) {
                this._hpMid = mid;
                hud.hpFill.classList.toggle('mid', mid);
            }
        }
        if (hud.xpFill) {
            const xr = Math.max(0, Math.min(1, this.player.xp / Math.max(1, this.player.xpNeeded)));
            const width = `${xr * 100}%`;
            if (this._xpWidth !== width) {
                this._xpWidth = width;
                hud.xpFill.style.width = width;
            }
        }
        if (hud.killFill) {
            const kr = Math.max(0, Math.min(1, this.kills / Math.max(1, this.levelKillTarget)));
            const width = `${kr * 100}%`;
            if (this._killWidth !== width) {
                this._killWidth = width;
                hud.killFill.style.width = width;
            }
        }
        if (hud.wave) {
            this._setText(hud.wave, this.isBossLevel?.() ? 'B' : String(this.currentWave || 1));
        }

        // Active power-up chips — only rebuild when set changes
        const chips = hud.chips;
        if (chips && this.player.activePowerUps) {
            const now = Date.now();
            const key = this.player.activePowerUps.map(p =>
                `${p.type}:${Math.ceil((p.endTime - now) / 1000)}`
            ).join('|');
            if (key !== this._uiChipKey) {
                this._uiChipKey = key;
                chips.innerHTML = this.player.activePowerUps.map(p => {
                    const left = Math.max(0, Math.ceil((p.endTime - now) / 1000));
                    return `<span class="pu-chip" data-type="${p.type}">${p.name} ${left}s</span>`;
                }).join('');
            }
        }
        this._uiCache = true;
    }

    /** Wave system: enemies spawn in discrete waves with a pause between them. */
    tickWaves() {
        if (this.arenaMode) return; // void pocket uses a single boss only
        if (this.isBossLevel()) return; // boss levels handled separately

        const lvl = this.currentLevel;

        if (!this.waveActive) {
            // Countdown between waves (3-5 seconds of breathing room)
            if (this.wavePauseTimer > 0) { this.wavePauseTimer--; return; }

            // Start next wave
            this.currentWave++;
            const wavesPerLevel = 4 + Math.floor(lvl / 2);           // 4→8 waves
            const baseCount     = 8  + (lvl - 1) * 3;               // 8, 11, 14 … enemies
            const waveCount     = baseCount + Math.floor(this.currentWave * 2.5);
            this.waveEnemiesLeft = waveCount;
            this.waveActive      = true;
            this.waveSpawnTimer  = 0;

            // Show wave announcement
            const waveLabel = this.currentWave <= wavesPerLevel
                ? `Wave ${this.currentWave} / ${wavesPerLevel}`
                : `Bonus Wave ${this.currentWave - wavesPerLevel}!`;
            this.floatingTexts.push({
                x: this.player.x,
                y: this.player.y - 60,
                text: waveLabel,
                color: '#ffaa00',
                age: 0,
                duration: 180,
                pulse: true,
            });
        }

        // Drip-spawn enemies inside the active wave
        if (this.waveActive) {
            this.waveSpawnTimer--;
            if (this.waveSpawnTimer <= 0 && this.waveEnemiesLeft > 0) {
                const interval = Math.max(10, 45 - lvl * 3); // faster on higher levels
                this.waveSpawnTimer = interval;
                // Batch size grows by 1 every WAVE_BATCH_GROWTH_INTERVAL waves
                const batchSize = Math.min(
                    WAVE_SPAWN_BATCH_MAX,
                    1 + Math.floor((this.currentWave - 1) / WAVE_BATCH_GROWTH_INTERVAL)
                );
                const toSpawn = Math.min(batchSize, this.waveEnemiesLeft);
                for (let i = 0; i < toSpawn; i++) {
                    this.spawnEnemy();
                }
                this.waveEnemiesLeft -= toSpawn;
            }
            // Wave ends when all enemies are spawned AND killed
            if (this.waveEnemiesLeft === 0) {
                let regularLeft = false;
                for (let wi = 0; wi < this.enemies.length; wi++) {
                    if (!this.enemies[wi].isBoss) { regularLeft = true; break; }
                }
                if (!regularLeft) {
                    this.waveActive     = false;
                    this.wavePauseTimer = 120; // 2 second pause before next wave
                }
            }
        }
    }

    updateCamera() {
        const targetX = this.player.x - this.canvas.width  / 2;
        const targetY = this.player.y - this.canvas.height / 2;
        const clampedX = Math.max(0, Math.min(WORLD_WIDTH  - this.canvas.width,  targetX));
        const clampedY = Math.max(0, Math.min(WORLD_HEIGHT - this.canvas.height, targetY));
        // Smooth lerp follow
        this.camera.x += (clampedX - this.camera.x) * 0.12;
        this.camera.y += (clampedY - this.camera.y) * 0.12;
    }

    drawControlHint() {
        const ctx = this.ctx;
        const pad = this.input.gamepadConnected;
        const lines = pad
            ? [
                'GAMEPAD  L-stick move · R-stick aim/fire',
                this.bombCooldown > 0
                    ? `BOMB  A/B  (${Math.ceil(this.bombCooldown / 60)}s) · fly over cyan geodes`
                    : 'BOMB  A/B/X · fly over cyan geodes',
              ]
            : [
                'WASD move · E dock shop · T trade · M map',
                'Fly over cyan bars to BOMB · Y beacon · F duel',
              ];
        const insets = this.screenInsets();
        ctx.save();
        ctx.font = '600 11px Rajdhani, sans-serif';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'bottom';
        ctx.globalAlpha = 0.55;
        ctx.fillStyle = '#7adfff';
        let y = this.canvas.height - 14 - insets.bottom;
        const x = 14 + insets.left;
        for (let i = lines.length - 1; i >= 0; i--) {
            ctx.fillText(lines[i], x, y);
            y -= 14;
        }
        ctx.restore();
    }

    drawMinimap() {
        if (!this.player) return;
        const ctx = this.ctx;
        const size = 110;
        const pad = 12;
        const insets = this.screenInsets();
        const x = this.canvas.width - size - pad - insets.right;
        const y = this.canvas.height - size - pad - insets.bottom;
        const scaleX = size / WORLD_WIDTH;
        const scaleY = size / WORLD_HEIGHT;

        ctx.save();
        ctx.globalAlpha = 0.88;
        ctx.fillStyle = 'rgba(6, 10, 28, 0.72)';
        ctx.strokeStyle = 'rgba(0, 217, 255, 0.45)';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.roundRect(x - 4, y - 4, size + 8, size + 8, 10);
        ctx.fill();
        ctx.stroke();

        // Viewport rectangle
        ctx.strokeStyle = 'rgba(255,255,255,0.25)';
        ctx.lineWidth = 1;
        ctx.strokeRect(
            x + this.camera.x * scaleX,
            y + this.camera.y * scaleY,
            this.canvas.width * scaleX,
            this.canvas.height * scaleY
        );

        // Battlefield shops
        if (!this.arenaMode) {
            for (const shop of this.worldShops || []) {
                ctx.fillStyle = shop.accent || '#ffd060';
                ctx.fillRect(x + shop.x * scaleX - 2, y + shop.y * scaleY - 2, 4, 4);
            }
        }

        // Enemies — rects, not arcs. A blur on a 3px dot is a full offscreen pass.
        for (let i = 0; i < this.enemies.length; i++) {
            const e = this.enemies[i];
            ctx.fillStyle = e.isBoss ? '#ff3366' : e.color;
            const s = e.isBoss ? 4 : 2;
            ctx.fillRect(x + e.x * scaleX - s * 0.5, y + e.y * scaleY - s * 0.5, s, s);
        }

        ctx.fillStyle = '#00d9ff';
        ctx.fillRect(x + this.player.x * scaleX - 2, y + this.player.y * scaleY - 2, 4, 4);
        ctx.restore();
    }

    drawWorldBackground() {
        const ctx = this.ctx;
        const bgCfg = this.currentBackground || {};
        const bg = bgCfg.color || '#05060d';
        const accent = bgCfg.accent || '#0e2a4a';
        const starColor = bgCfg.starColor || '#8ab4d8';
        const mistAmt = bgCfg.mist ?? 0.2;
        const mistRGB = bgCfg.mistColor || '40,60,90';
        const cam = this.camera;
        const cw = this.canvas.width, ch = this.canvas.height;
        this._ensureStars(this._atmosphere);

        const pad = 120;
        // Deep sky fill
        ctx.fillStyle = bg;
        ctx.fillRect(cam.x - pad, cam.y - pad, cw + pad * 2, ch + pad * 2);

        // Soft vignette / planetary sky gradient (screen-space in world coords)
        if (this.perf?.quality === 'high') {
            const gx = cam.x + cw * 0.5;
            const gy = cam.y + ch * 0.35;
            const grd = ctx.createRadialGradient(gx, gy, 40, gx, gy, Math.max(cw, ch) * 0.85);
            grd.addColorStop(0, this._hexAlpha(accent, 0.18));
            grd.addColorStop(0.55, this._hexAlpha(accent, 0.06));
            grd.addColorStop(1, 'transparent');
            ctx.fillStyle = grd;
            ctx.fillRect(cam.x - pad, cam.y - pad, cw + pad * 2, ch + pad * 2);
        }

        // Distant world / gas giants (parallax sky, typed per planet)
        this._drawSkyBodies(ctx, cam, cw, ch);

        // Optional local star flare (bright corner sun)
        if (bgCfg.flare && this.perf?.quality === 'high') {
            const fx = cam.x + cw * 0.12;
            const fy = cam.y + ch * 0.15;
            const flare = ctx.createRadialGradient(fx, fy, 0, fx, fy, Math.min(cw, ch) * 0.45);
            flare.addColorStop(0, this._hexAlpha(bgCfg.flareColor || '#ffeecc', 0.22));
            flare.addColorStop(0.4, this._hexAlpha(bgCfg.flareColor || '#ffeecc', 0.06));
            flare.addColorStop(1, 'transparent');
            ctx.fillStyle = flare;
            ctx.fillRect(cam.x, cam.y, cw, ch);
        }

        // Drifting mist / nebula clouds
        if (mistAmt > 0.05 && this.nebulae?.length) {
            const t = (this.frame || 0) * 0.004;
            const q = this.perf?.quality;
            const step = q === 'high' ? 1 : 2;
            for (let i = 0; i < this.nebulae.length; i += step) {
                const n = this.nebulae[i];
                const ox = n.x + Math.sin(t * n.drift + n.phase) * 40;
                const oy = n.y + Math.cos(t * n.drift * 0.7 + n.phase) * 28;
                if (ox + n.r < cam.x - 40 || ox - n.r > cam.x + cw + 40) continue;
                if (oy + n.r < cam.y - 40 || oy - n.r > cam.y + ch + 40) continue;
                const a = n.a * (0.7 + mistAmt);
                if (q !== 'high') {
                    ctx.globalAlpha = Math.min(0.22, a);
                    ctx.fillStyle = `rgb(${mistRGB})`;
                    ctx.beginPath();
                    ctx.ellipse(ox, oy, n.r, n.r * (n.stretch || 1), n.phase * 0.25, 0, Math.PI * 2);
                    ctx.fill();
                    ctx.globalAlpha = 1;
                    continue;
                }
                ctx.save();
                ctx.translate(ox, oy);
                ctx.rotate(n.phase * 0.25);
                ctx.scale(1, n.stretch || 1);
                const g = ctx.createRadialGradient(0, 0, 0, 0, 0, n.r);
                g.addColorStop(0, `rgba(${mistRGB},${Math.min(0.38, a)})`);
                g.addColorStop(0.45, `rgba(${mistRGB},${Math.min(0.2, a * 0.5)})`);
                g.addColorStop(1, 'transparent');
                ctx.fillStyle = g;
                ctx.beginPath();
                ctx.arc(0, 0, n.r, 0, Math.PI * 2);
                ctx.fill();
                ctx.restore();
            }
        }

        // Stars (planet-tinted)
        const q = this.perf?.quality;
        const starStep = q === 'low' ? 3 : q === 'medium' ? 2 : 1;
        const twinkle = q === 'high';
        const tt = twinkle ? (this.frame || 0) * 0.05 : 0;
        // Base star color batch
        ctx.fillStyle = starColor;
        ctx.beginPath();
        for (let i = 0; i < this.stars.length; i += starStep) {
            const s = this.stars[i];
            if (s.tint) continue;
            if (s.x < cam.x - 20 || s.x > cam.x + cw + 20) continue;
            if (s.y < cam.y - 20 || s.y > cam.y + ch + 20) continue;
            const a = twinkle ? s.a * (0.55 + 0.45 * Math.sin(tt + s.tw)) : s.a;
            // Can't set alpha per path easily — approximate with radius
            const rr = s.r * (0.7 + a * 0.5);
            ctx.moveTo(s.x + rr, s.y);
            ctx.arc(s.x, s.y, rr, 0, Math.PI * 2);
        }
        ctx.globalAlpha = 0.7;
        ctx.fill();
        ctx.globalAlpha = 1;

        // Tinted accent stars
        if (q !== 'low') {
            ctx.fillStyle = bgCfg.flareColor || accent;
            ctx.beginPath();
            for (let i = 0; i < this.stars.length; i += starStep) {
                const s = this.stars[i];
                if (!s.tint) continue;
                if (s.x < cam.x - 20 || s.x > cam.x + cw + 20) continue;
                if (s.y < cam.y - 20 || s.y > cam.y + ch + 20) continue;
                ctx.moveTo(s.x + s.r, s.y);
                ctx.arc(s.x, s.y, s.r * 1.1, 0, Math.PI * 2);
            }
            ctx.globalAlpha = 0.75;
            ctx.fill();
            ctx.globalAlpha = 1;
        }

        // Geometry Wars warp grid
        this.warpGrid.draw(ctx, cam, cw, ch, this.player, accent);

        // World border — only when camera near edges
        const nearBorder =
            cam.x < 200 || cam.y < 200 ||
            cam.x + cw > WORLD_WIDTH - 200 ||
            cam.y + ch > WORLD_HEIGHT - 200;
        if (nearBorder) {
            ctx.save();
            ctx.strokeStyle = 'rgba(255, 40, 120, 0.75)';
            if (this.perf?.useShadows()) {
                ctx.shadowColor = '#ff2288';
                ctx.shadowBlur = 28;
            }
            ctx.lineWidth = 5;
            ctx.strokeRect(3, 3, WORLD_WIDTH - 6, WORLD_HEIGHT - 6);
            ctx.shadowBlur = 0;
            ctx.strokeStyle = 'rgba(120, 220, 255, 0.15)';
            ctx.lineWidth = 1;
            ctx.strokeRect(10, 10, WORLD_WIDTH - 20, WORLD_HEIGHT - 20);
            ctx.restore();
        }
    }

    _hexAlpha(hex, a) {
        const c = (hex || '#ffffff').replace('#', '');
        if (c.length !== 6) return hex;
        const r = parseInt(c.slice(0, 2), 16);
        const g = parseInt(c.slice(2, 4), 16);
        const b = parseInt(c.slice(4, 6), 16);
        return `rgba(${r},${g},${b},${a})`;
    }

    _drawSkyBodies(ctx, cam, cw, ch) {
        if (!this.skyBodies?.length || this.perf?.quality === 'low') return;
        const rich = this.perf.quality === 'high';
        const t = (this.frame || 0) * 0.003;
        for (const body of this.skyBodies) {
            const x = cam.x + cw * body.nx + Math.sin(t + body.r) * (rich ? 10 : 0);
            const y = cam.y + ch * body.ny + Math.cos(t * 0.7) * (rich ? 6 : 0);
            if (!rich) {
                ctx.globalAlpha = 0.5;
                ctx.fillStyle = body.color;
                ctx.beginPath();
                ctx.arc(x, y, body.r, 0, Math.PI * 2);
                ctx.fill();
                ctx.globalAlpha = 1;
                continue;
            }
            ctx.save();
            const haloR = body.r * (body.blur ? 2.4 : 1.8);
            const halo = ctx.createRadialGradient(x, y, body.r * 0.15, x, y, haloR);
            halo.addColorStop(0, this._hexAlpha(body.color, body.blur ? 0.28 : 0.18));
            halo.addColorStop(0.42, this._hexAlpha(body.color, 0.08));
            halo.addColorStop(1, 'transparent');
            ctx.fillStyle = halo;
            ctx.beginPath();
            ctx.arc(x, y, haloR, 0, Math.PI * 2);
            ctx.fill();

            const bodyG = ctx.createRadialGradient(
                x - body.r * 0.32, y - body.r * 0.3, body.r * 0.08,
                x, y, body.r
            );
            bodyG.addColorStop(0, body.light || '#e8f0ff');
            bodyG.addColorStop(0.45, body.color);
            bodyG.addColorStop(1, body.dark || '#101018');
            ctx.globalAlpha = 0.5;
            ctx.fillStyle = bodyG;
            ctx.beginPath();
            ctx.arc(x, y, body.r, 0, Math.PI * 2);
            ctx.fill();
            ctx.globalAlpha = 1;

            if (body.type === 'gas' || body.blur) {
                ctx.globalAlpha = 0.18;
                ctx.fillStyle = body.light || body.color;
                ctx.beginPath();
                ctx.ellipse(x, y - body.r * 0.15, body.r * 0.92, body.r * 0.22, -0.3, 0, Math.PI * 2);
                ctx.fill();
                ctx.globalAlpha = 1;
            }

            if (body.rings) {
                ctx.save();
                ctx.translate(x, y);
                ctx.rotate(-0.42);
                ctx.scale(1, 0.26);
                ctx.strokeStyle = this._hexAlpha(body.light || '#ffe8b0', 0.32);
                ctx.lineWidth = Math.max(3, body.r * 0.06);
                ctx.beginPath();
                ctx.arc(0, 0, body.r * 1.48, 0, Math.PI * 2);
                ctx.stroke();
                ctx.restore();
            }
            ctx.restore();
        }
    }

    gameLoop() {
        if (!this.running) return;

        // FPS / adaptive quality (always tick so stats stay alive while paused)
        const { quality } = this.perf.tick();
        setRenderQuality(quality);
        applyCanvasShadowPolicy(this.ctx, quality === 'high');
        if (this._lastQuality !== quality) {
            this._lastQuality = quality;
            this.warpGrid.setQuality(quality);
            this.ctx.imageSmoothingEnabled = quality !== 'low';
        }

        if (!this.paused) {
            this.frame++;

            // --- pure logic (no drawing) ---
            if (this.bombCooldown > 0) this.bombCooldown--;
            this.updatePlayer();
            this.updateCamera();
            this.autoShoot();

            // ===== WORLD-SPACE DRAWING =====
            const ctx = this.ctx;
            // Screen shake offset (applied after camera)
            if (this.shake > 0.2) {
                this.shake *= VFX.SHAKE_DECAY;
            } else {
                this.shake = 0;
            }
            const shakeX = this.shake ? (Math.random() - 0.5) * this.shake * 2 : 0;
            const shakeY = this.shake ? (Math.random() - 0.5) * this.shake * 2 : 0;

            ctx.save();
            ctx.translate(-Math.round(this.camera.x) + shakeX, -Math.round(this.camera.y) + shakeY);

            this.drawWorldBackground();
            this.updateWeapons(); // also draws orbital shields

            // Cooldowns for call-trade / call-duel
            if (this.callTradeCd > 0) this.callTradeCd--;
            if (this.callFightCd > 0) this.callFightCd--;
            if (this.tradeBeacon && Date.now() >= this.tradeBeacon.expires) {
                this.tradeBeacon = null;
            }

            // Progression snapshots (throttled via record max fields)
            if (this.player && (this.frame & 31) === 0) {
                this.trackProgress({
                    setPilotLevel: this.player.level,
                    setMaxMagnet: this.player.magnetRange || 0,
                });
                if ((this.inventory.fuel || 0) < 10) this.trackProgress({ lowFuel: 1 });
                if (this.player.coins === 0) this.trackProgress({ timesBroke: 1 });
                this.inventory.ensureSize();
                if (this.inventory.countFree() === 0) this.trackProgress({ cargoFull: 1 });
                if ((this.player.damage || 0) >= 40 && (this.player.maxHp || 100) <= 80) {
                    this.trackProgress({ glassCannon: 1 });
                }
            }

            // Wave-based enemy spawning
            this.tickWaves();

            // Spawn boss — much more frequent on boss levels (not in void arena)
            if (!this.arenaMode) {
                const bossInterval = this.isBossLevel() ? 10000 : BOSS_SPAWN_INTERVAL;
                if (Date.now() - this.lastBossSpawn > bossInterval) {
                    this.spawnBoss();
                    this.lastBossSpawn = Date.now();
                }

                // Spawn Geometry Wars bomb geodes periodically
                if (Date.now() - this.lastGeoBombSpawn > 12000 && this.geoBombs.length < 10) {
                    this.spawnGeoBomb();
                    this.lastGeoBombSpawn = Date.now();
                }

                // Spawn portals periodically
                if (Date.now() - this.lastPortalSpawn > 60000 && this.portals.length < 3) {
                    this.spawnPortal();
                    this.lastPortalSpawn = Date.now();
                }
            }

            // Update bots
            this.bots.forEach((bot, idx) => {
                if (bot.isDead) {
                    if (bot.canRespawn()) {
                        const margin = 50;
                        const x = margin + Math.random() * (WORLD_WIDTH - margin * 2);
                        const y = margin + Math.random() * (WORLD_HEIGHT - margin * 2);
                        bot.respawn(x, y);
                    }
                    return;
                }

                bot.update({ width: WORLD_WIDTH, height: WORLD_HEIGHT }, this.enemies, this.player);
                bot.draw(this.ctx);

                // Bot shooting
                const proj = bot.shoot();
                if (proj) {
                    this.projectiles.push(this.projectilePool.get(proj.x, proj.y, proj.vx, proj.vy, proj.damage, 'bot'));
                }
            });

            // Update and draw clones
            for (let i = this.clones.length - 1; i >= 0; i--) {
                const clone = this.clones[i];
                if (!clone.isAlive()) { this.clones.splice(i, 1); continue; }
                clone.update();
                clone.draw(this.ctx);
                const shot = clone.shoot(this.enemies);
                if (shot) {
                    this.projectiles.push(this.projectilePool.get(shot.x, shot.y, shot.vx, shot.vy, shot.damage, 'player'));
                }
            }

            // Geometry Wars bomb geodes — fly over to detonate
            for (let i = this.geoBombs.length - 1; i >= 0; i--) {
                const bomb = this.geoBombs[i];
                if (!bomb.update()) {
                    this.geoBombs.splice(i, 1);
                    continue;
                }
                if (bomb.checkPlayerCollision(this.player)) {
                    const bx = bomb.x;
                    const by = bomb.y;
                    const radius = bomb.blastRadius;
                    bomb.alive = false;
                    this.geoBombs.splice(i, 1);
                    this.trackProgress({ geodesPicked: 1, bombsUsed: 1 });
                    // Detonate at the geode position (classic GW pickup feel)
                    this.detonateRadiusBomb(bx, by, radius, {
                        bossDamage: 110,
                        color: '#00e8ff',
                    });
                    this.telegram.hapticFeedback('impact');
                    continue;
                }
                if (this.inView(bomb.x, bomb.y, 80)) {
                    bomb.draw(this.ctx);
                }
            }

            // Update black-hole portals / void rifts
            this.portals.forEach(portal => {
                portal.update();
                const teleport = portal.checkPlayerCollision(this.player);
                if (teleport) {
                    const fromX = this.player.x;
                    const fromY = this.player.y;
                    this._portalTransitFx(fromX, fromY);

                    if (teleport.arenaExit && this.arenaMode) {
                        // Escape void arena back to open world
                        this.exitVoidArena();
                    } else if (teleport.voidPull && !this.arenaMode) {
                        // Sometimes a rift swallows you into a boss pocket
                        this.enterVoidArena(fromX, fromY);
                    } else {
                        // Normal bidirectional hop
                        this.player.x = teleport.x;
                        this.player.y = teleport.y;
                        this.player.vx = 0;
                        this.player.vy = 0;
                        this.trackProgress({ portalUses: 1 });
                        this._portalTransitFx(teleport.x, teleport.y, '#9944ee');
                        this.audio.playPortal();
                        this.telegram.hapticFeedback('impact');
                    }
                }
                portal.draw(this.ctx);
            });

            // Battlefield shop pads
            if (!this.arenaMode && this.worldShops?.length) {
                let near = null;
                for (const shop of this.worldShops) {
                    shop.update();
                    const n = shop.inRange(this.player);
                    if (n) near = shop;
                    if (this.inView(shop.x, shop.y, 200)) {
                        shop.draw(this.ctx, { nearby: n });
                    }
                }
                this._updateShopPrompt(near);
            } else {
                this._updateShopPrompt(null);
            }

            // Arena HUD cue
            if (this.arenaMode && this.inView?.(this.player.x, this.player.y, 200) !== false) {
                const bossAlive = this.enemies.some(e => e.isBoss && e._arenaBoss);
                if (this.arenaMode.bossAlive && !bossAlive) {
                    this.arenaMode.bossAlive = false;
                    this.arenaMode.killedBoss = true;
                    // Reward for clearing the pocket boss
                    const bonus = 40 + this.currentLevel * 15;
                    this.player.coins += bonus;
                    this.trackProgress({ crystalsEarned: bonus });
                    this.floatingTexts.push({
                        x: this.player.x,
                        y: this.player.y - 55,
                        text: `VOID BOSS DOWN · +${bonus}✦`,
                        color: '#ffd060',
                        age: 0,
                        duration: 140,
                        pulse: true,
                    });
                    this.audio.playLevelUp?.();
                }
            }

            // Update power-ups
            for (let i = this.powerUps.length - 1; i >= 0; i--) {
                const powerUp = this.powerUps[i];

                if (!powerUp.update()) {
                    this.powerUps.splice(i, 1);
                    continue;
                }

                if (powerUp.checkCollision(this.player)) {
                    this.handlePowerUpCollection(powerUp);
                    this.audio.playPowerUp();
                    this.powerUps.splice(i, 1);
                    this.telegram.hapticFeedback('success');
                    this.createParticles(powerUp.x, powerUp.y, powerUp.type.color, 15);
                } else {
                    powerUp.draw(this.ctx);
                }
            }

            // Update and draw enemies (cull draw off-screen)
            const nowMs = Date.now();
            const enemies = this.enemies;
            for (let ei = 0; ei < enemies.length; ei++) {
                const enemy = enemies[ei];
                const frozen = enemy.frozenUntil && nowMs < enemy.frozenUntil;
                if (!frozen) {
                    enemy.update(this.player);
                }
                const visible = this.inView(enemy.x, enemy.y, enemy.radius + 40);
                if (visible) {
                    enemy.draw(this.ctx);
                }

                // Draw freeze bubble
                if (frozen && visible) {
                    const ctx = this.ctx;
                    ctx.save();
                    const pulse = Math.sin(Date.now() / 150) * 3;
                    ctx.globalAlpha = 0.35 + Math.sin(Date.now() / 200) * 0.1;
                    ctx.fillStyle = '#55ccff';
                    if (this.perf.useShadows()) {
                        ctx.shadowBlur = 20;
                        ctx.shadowColor = '#aaeeff';
                    }
                    ctx.beginPath();
                    ctx.arc(enemy.x, enemy.y, enemy.radius + 8 + pulse, 0, Math.PI * 2);
                    ctx.fill();
                    ctx.globalAlpha = 0.7;
                    ctx.strokeStyle = '#ffffff';
                    ctx.lineWidth = 2;
                    ctx.stroke();
                    ctx.restore();
                }

                // Enemy shooting (blocked when frozen)
                const enemyProj = frozen ? null : enemy.shoot(this.player);
                if (enemyProj) {
                    this.projectiles.push(this.projectilePool.get(
                        enemyProj.x,
                        enemyProj.y,
                        enemyProj.vx,
                        enemyProj.vy,
                        enemyProj.damage,
                        'enemy'
                    ));
                }

                // Collision with player
                const dx = enemy.x - this.player.x;
                const dy = enemy.y - this.player.y;
                const dist = Math.sqrt(dx * dx + dy * dy);

                if (dist < enemy.radius + this.player.size / 2) {
                    const dmg = enemy.damage * 0.1;
                    const hpBefore = this.player.hp;
                    if (this.player.takeDamage(dmg)) {
                        this.gameOver();
                    } else if (!this.player.invincible) {
                        this.addShake(VFX.HIT_SHAKE * 0.6);
                        this.hitFlash = 8;
                        if (hpBefore > 1 && this.player.hp <= 1) {
                            this.trackProgress({ nearDeathSurvives: 1 });
                        }
                        if (enemy.variant === 'saucer') {
                            this.trackProgress({ hitBySaucer: 1 });
                        }
                    }
                }

                // Bots can damage enemies too
                const bots = this.bots;
                for (let bi = 0; bi < bots.length; bi++) {
                    const bot = bots[bi];
                    if (bot.isDead) continue;
                    const bdx = enemy.x - bot.x;
                    const bdy = enemy.y - bot.y;
                    const bdist = Math.sqrt(bdx * bdx + bdy * bdy);

                    if (bdist < enemy.radius + bot.size) {
                        bot.takeDamage(enemy.damage * 0.05);
                    }
                }
            }

            // Wingman badge: any living bot (once)
            let livingBot = false;
            for (let bi = 0; bi < this.bots.length; bi++) {
                if (!this.bots[bi].isDead) { livingBot = true; break; }
            }
            if (
                livingBot
                && this.progress
                && !this.progress.isUnlocked('friendly_neighborhood')
            ) {
                this.trackProgress({ botAssists: 1 });
            }

            // Update and draw projectiles
            for (let i = this.projectiles.length - 1; i >= 0; i--) {
                const proj = this.projectiles[i];

                if (!proj.update({ width: WORLD_WIDTH, height: WORLD_HEIGHT }, this.player)) {
                    this.projectilePool.release(proj);
                    this.projectiles.splice(i, 1);
                    continue;
                }

                let hit = false;

                // Enemy projectiles hit player and bots
                if (proj.type === 'enemy') {
                    // Check player collision
                    const pdx = this.player.x - proj.x;
                    const pdy = this.player.y - proj.y;
                    const pdist = Math.sqrt(pdx * pdx + pdy * pdy);

                    if (pdist < this.player.size / 2 + proj.radius) {
                        if (this.player.takeDamage(proj.damage)) {
                            this.gameOver();
                        } else if (!this.player.invincible) {
                            this.addShake(VFX.HIT_SHAKE);
                            this.hitFlash = 10;
                            this.createParticles(proj.x, proj.y, '#ff4466', 6, 'spark');
                        }
                        this.projectilePool.release(proj);
                        this.projectiles.splice(i, 1);
                        hit = true;
                    }

                    // Check bot collisions
                    if (!hit) {
                        for (let b = 0; b < this.bots.length; b++) {
                            const bot = this.bots[b];
                            if (bot.isDead) continue;

                            const bdx = bot.x - proj.x;
                            const bdy = bot.y - proj.y;
                            const bdist = Math.sqrt(bdx * bdx + bdy * bdy);

                            if (bdist < bot.size / 2 + proj.radius) {
                                bot.takeDamage(proj.damage);
                                this.projectilePool.release(proj);
                                this.projectiles.splice(i, 1);
                                hit = true;
                                break;
                            }
                        }
                    }
                } else {
                    // Player/Bot projectiles hit enemies
                    for (let j = this.enemies.length - 1; j >= 0; j--) {
                        const enemy = this.enemies[j];
                        const dx = enemy.x - proj.x;
                        const dy = enemy.y - proj.y;
                        const dist = Math.sqrt(dx * dx + dy * dy);

                        if (dist < enemy.radius + proj.radius) {
                            if (enemy.takeDamage(proj.damage)) {
                                this.handleEnemyDeath(enemy, j);
                            } else {
                                this.audio.playHit();
                                this.createParticles(proj.x, proj.y, enemy.color, 4, 'spark');
                                if (quality !== 'low') {
                                    this.createParticles(proj.x, proj.y, '#ffffff', 1, 'glow', { radius: 10, life: 8 });
                                    this.floatingTexts.push({
                                        x: enemy.x + (Math.random() - 0.5) * 10,
                                        y: enemy.y - enemy.radius,
                                        text: `-${Math.round(proj.damage)}`,
                                        color: '#ff6688',
                                        age: 0,
                                        duration: 50,
                                        pulse: false,
                                    });
                                }
                            }
                            this.projectilePool.release(proj);
                            this.projectiles.splice(i, 1);
                            hit = true;
                            break;
                        }
                    }
                }

                if (!hit && this.inView(proj.x, proj.y, 40)) {
                    proj.draw(this.ctx);
                }
            }

            // Update and draw solar crystals (currency)
            for (let i = this.coins.length - 1; i >= 0; i--) {
                const coin = this.coins[i];
                const coinResult = coin.update(this.player);
                if (coinResult === 'collected') {
                    const val = coin.value || 1;
                    this.player.coins += val;
                    this.trackProgress({ crystalsEarned: val });
                    if (this.player.coins === 0) this.trackProgress({ timesBroke: 1 });
                    this.audio.playCoin();
                    this.createParticles(coin.x, coin.y, '#ffcc44', 6, 'spark');
                    this.floatingTexts.push({
                        x: coin.x,
                        y: coin.y - 8,
                        text: `+${val}`,
                        color: '#ffd060',
                        age: 0,
                        duration: 45,
                        pulse: false,
                    });
                    this.coinPool.release(coin);
                    this.coins.splice(i, 1);
                } else if (coinResult === 'expired') {
                    this.coinPool.release(coin);
                    this.coins.splice(i, 1);
                } else if (this.inView(coin.x, coin.y, 36)) {
                    coin.draw(this.ctx);
                }
            }

            // Update and draw XP crystals
            for (let i = this.collectibles.length - 1; i >= 0; i--) {
                const col = this.collectibles[i];
                const colResult = col.update(this.player);
                if (colResult === 'collected') {
                    const xp = col.xpValue || 1;
                    this.collectibleCount++;
                    // Grant XP on crystal pickup
                    if (this.player.addXP(xp)) {
                        this.player.levelUp();
                        this.audio.playLevelUp();
                        this.showAbilityPopup();
                    }
                    this.audio.playPowerUp();
                    const sparkColor = col.palette?.mid || '#33ffe0';
                    this.createParticles(col.x, col.y, sparkColor, 8, 'spark');
                    this.floatingTexts.push({
                        x: col.x,
                        y: col.y - 10,
                        text: `+${xp} XP`,
                        color: sparkColor,
                        age: 0,
                        duration: 50,
                        pulse: false,
                    });
                    this.collectiblePool.release(col);
                    this.collectibles.splice(i, 1);

                    // Ability choice every N crystals
                    if (this.collectibleCount >= this.collectibleThreshold) {
                        this.collectibleCount = 0;
                        this.showCollectibleAbilityPopup();
                    }
                } else if (colResult === 'expired') {
                    this.collectiblePool.release(col);
                    this.collectibles.splice(i, 1);
                } else if (this.inView(col.x, col.y, 36)) {
                    col.draw(this.ctx);
                }
            }

            // Update and draw particles
            const maxP = this.perf.maxParticles();
            for (let i = this.particles.length - 1; i >= 0; i--) {
                const p = this.particles[i];
                if (!p.update() || i >= maxP) {
                    // Only return real pooled Particle instances (gates may push ad-hoc VFX)
                    if (typeof p.reset === 'function' && p.kind !== undefined) {
                        this.particlePool.release(p);
                    }
                    this.particles.splice(i, 1);
                } else if (this.inView(p.x, p.y, 24)) {
                    p.draw(this.ctx);
                }
            }

            // Floating death-point texts — 5 s lifetime with pulse + opacity fade
            for (let i = this.floatingTexts.length - 1; i >= 0; i--) {
                const ft = this.floatingTexts[i];
                ft.age++;
                if (ft.age >= ft.duration) { this.floatingTexts.splice(i, 1); continue; }

                const progress = ft.age / ft.duration;   // 0 → 1

                // Rise quickly then hover
                const rise = Math.min(ft.age * 0.7, 40);
                const y = ft.y - rise;

                // Fade only in the last 30% of lifetime
                const fadeStart = 0.7;
                const alpha = progress > fadeStart
                    ? 1 - (progress - fadeStart) / (1 - fadeStart)
                    : 1;

                // Pulse font size in first 60% of lifetime
                let fontSize = 15;
                if (ft.pulse && progress < 0.6) {
                    fontSize = 15 + Math.sin(Date.now() / 180) * 3;
                }

                if (!this.inView(ft.x, y, 40)) continue;
                this.ctx.save();
                this.ctx.globalAlpha = alpha;
                this.ctx.font = `bold ${fontSize}px Rajdhani, sans-serif`;
                this.ctx.fillStyle = ft.color;
                if (this.perf.useShadows()) {
                    this.ctx.shadowBlur = ft.pulse ? 14 : 8;
                    this.ctx.shadowColor = ft.color;
                }
                this.ctx.textAlign = 'center';
                this.ctx.textBaseline = 'middle';
                this.ctx.fillText(ft.text, ft.x, y);
                this.ctx.restore();
            }

            // Draw boss speech bubbles
            for (let i = this.speechBubbles.length - 1; i >= 0; i--) {
                const b = this.speechBubbles[i];
                b.age++;
                b.alpha = Math.max(0, 1 - b.age / b.duration);
                const floatY = b.y - (b.age * 0.4);
                if (b.age >= b.duration) { this.speechBubbles.splice(i, 1); continue; }

                const ctx = this.ctx;
                ctx.save();
                ctx.globalAlpha = b.alpha;
                ctx.font = 'bold 13px Rajdhani, sans-serif';
                const tw = ctx.measureText(b.text).width;
                const pad = 10;
                const bw = tw + pad * 2;
                const bh = 28;
                const bx = b.x - bw / 2;
                const by = floatY - bh - 10;

                // Bubble background
                ctx.fillStyle = '#ffffff';
                ctx.strokeStyle = '#ff0066';
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.roundRect(bx, by, bw, bh, 8);
                ctx.fill();
                ctx.stroke();

                // Tail
                ctx.beginPath();
                ctx.moveTo(b.x - 6, by + bh);
                ctx.lineTo(b.x, by + bh + 10);
                ctx.lineTo(b.x + 6, by + bh);
                ctx.fillStyle = '#ffffff';
                ctx.fill();
                ctx.strokeStyle = '#ff0066';
                ctx.stroke();

                // Text
                ctx.fillStyle = '#1a1a2e';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(b.text, b.x, by + bh / 2);
                ctx.restore();
            }

            // Engagement range ring (so you see max shoot distance)
            if (this.player && this.perf?.quality !== 'low') {
                const rr = this.getShootRange();
                const ctx2 = this.ctx;
                ctx2.save();
                ctx2.strokeStyle = 'rgba(0, 220, 255, 0.14)';
                ctx2.lineWidth = 1;
                ctx2.setLineDash([6, 10]);
                ctx2.beginPath();
                ctx2.arc(this.player.x, this.player.y, rr, 0, Math.PI * 2);
                ctx2.stroke();
                ctx2.setLineDash([]);
                ctx2.restore();
            }

            // Draw player (world-space)
            this.player.draw(this.ctx);

            // Draw remote multiplayer players
            this.multiplayer.drawRemotePlayers(this.ctx);

            // ===== END WORLD-SPACE DRAWING =====
            ctx.restore();

            // ===== SCREEN-SPACE OVERLAYS =====

            // Vignette — one full-screen radial gradient. High quality only.
            if (quality === 'high') {
                const w = this.canvas.width, h = this.canvas.height;
                const vg = this.ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.35, w / 2, h / 2, Math.max(w, h) * 0.72);
                vg.addColorStop(0, 'transparent');
                vg.addColorStop(1, 'rgba(0,0,0,0.45)');
                this.ctx.fillStyle = vg;
                this.ctx.fillRect(0, 0, w, h);
            }

            // Damage hit flash (red edge)
            if (this.hitFlash > 0) {
                this.ctx.save();
                this.ctx.globalAlpha = (this.hitFlash / 10) * 0.35;
                this.ctx.fillStyle = '#ff2040';
                this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
                this.ctx.restore();
                this.hitFlash--;
            }

            // Bomb screen flash
            if (this.bombFlash > 0) {
                this.ctx.save();
                this.ctx.globalAlpha = this.bombFlash / 12 * 0.5;
                this.ctx.fillStyle = '#ff4400';
                this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
                this.ctx.restore();
                this.bombFlash--;
            }

            // Boss level HUD banner
            if (this.isBossLevel()) {
                const pulse = 0.7 + Math.sin(Date.now() / 300) * 0.3;
                this.ctx.save();
                this.ctx.globalAlpha = pulse;
                this.ctx.font = 'bold 22px Orbitron, sans-serif';
                this.ctx.fillStyle = '#ff0066';
                if (this.perf.useShadows()) {
                    this.ctx.shadowBlur = 20;
                    this.ctx.shadowColor = '#ff0066';
                }
                this.ctx.textAlign = 'center';
                this.ctx.textBaseline = 'top';
                const bossY = this.screenInsets().top + 10;
                this.ctx.fillText(`⚠ BOSS LEVEL — ${this.levelKillTarget - this.kills} bosses left ⚠`, this.canvas.width / 2, bossY);
                this.ctx.restore();
            }

            // Mini minimap + control legend (skip on low quality)
            if (this.perf.drawExtras()) {
                this.drawMinimap();
                this.drawControlHint();
            }

            // Update UI
            this.updateUI();

            // Feed entity counts to FPS panel
            this.perf.setCounts({
                enemies: this.enemies.length,
                projectiles: this.projectiles.length,
                particles: this.particles.length,
                coins: this.coins.length,
                warps: this.warpGrid.warps.length,
                geoBombs: this.geoBombs.length,
            });
        } else {
            // Still refresh counts while paused
            this.perf.setCounts({
                enemies: this.enemies.length,
                projectiles: this.projectiles.length,
                particles: this.particles.length,
                coins: this.coins.length,
                warps: this.warpGrid.warps.length,
                geoBombs: this.geoBombs.length,
            });
        }

        requestAnimationFrame(() => this.gameLoop());
    }
}

/** Zodiac map helpers */
function escapeXml(s) {
    return String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function shortPlanetName(name) {
    const n = String(name || '');
    return n.length > 12 ? n.slice(0, 11) + '…' : n;
}

function planetTypeGlyph(type) {
    const g = {
        terran: '♁', desert: '♄', ice: '♆', lava: '♂', gas: '♃',
        toxic: '☣', ocean: '☾', storm: '⚡', crystal: '◇', barren: '☽',
        jungle: '❀', metal: '⚙',
    };
    return g[type] || '✦';
}

function hueFromHex(hex) {
    const c = String(hex || '#6688aa').replace('#', '');
    if (c.length !== 6) return 210;
    const r = parseInt(c.slice(0, 2), 16) / 255;
    const g = parseInt(c.slice(2, 4), 16) / 255;
    const b = parseInt(c.slice(4, 6), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    const d = max - min;
    if (d < 1e-6) return 210;
    let h = 0;
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h = Math.round(h * 60);
    if (h < 0) h += 360;
    return h;
}

// Initialize game when DOM is loaded
window.addEventListener('DOMContentLoaded', () => {
    // Signal to CrazyGames that assets are loading
    window.CrazyGames?.SDK?.game.loadingStart();

    window.game = new Game();

    // Connect to multiplayer room if roomId is in URL query params or localStorage
    const urlParams = new URLSearchParams(window.location.search);
    const roomId = urlParams.get('room') || localStorage.getItem('mp_room');
    if (roomId) {
        const playerName = window.game.telegram.getPlayerName();
        const skin = window.game.skinManager.getCurrentSkin();
        window.game.multiplayer.connect(roomId, playerName, skin);
        window.game.multiplayer.setPlayer(window.game.player);
    }

    // Assets ready — SDK will stop showing loading spinner
    window.CrazyGames?.SDK?.game.loadingStop();

    // Setup close buttons
    document.querySelectorAll('.popup-close').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.target.closest('.popup').style.display = 'none';
            window.game.paused = false;
            window.game._activeShop = null;
        });
    });

    // Play again button
    document.getElementById('playAgainBtn').addEventListener('click', () => {
        document.getElementById('gameOver').style.display = 'none';
        window.game.start();
    });

    // Share button
    document.getElementById('shareBtn').addEventListener('click', () => {
        window.game.telegram.shareScore(window.game.player.coins, window.game.kills);
    });

    // ---- Multiplayer UI ----
    function _mpUpdateStatus() {
        const mp = window.game.multiplayer;
        const statusEl = document.getElementById('mpStatus');
        const leaveBtn = document.getElementById('mpLeaveBtn');
        if (mp.isConnected()) {
            const count = mp.getRoomPlayerCount();
            const rid = localStorage.getItem('mp_room') || '';
            statusEl.textContent = `Connected: ${count} player${count !== 1 ? 's' : ''} in room "${rid}"`;
            statusEl.style.color = '#00ff88';
            leaveBtn.style.display = 'inline-block';
        } else {
            statusEl.textContent = 'Not connected';
            statusEl.style.color = '#ccc';
            leaveBtn.style.display = 'none';
        }
    }

    document.getElementById('multiplayerBtn').addEventListener('click', () => {
        const popup = document.getElementById('multiplayerPopup');
        popup.style.display = 'block';
        window.game.paused = true;
        _mpUpdateStatus();
        const storedRoom = localStorage.getItem('mp_room') || '';
        if (storedRoom) {
            document.getElementById('mpRoomInput').value = storedRoom.toUpperCase();
        }
    });

    document.getElementById('mpJoinBtn').addEventListener('click', () => {
        const rid = document.getElementById('mpRoomInput').value.trim().toUpperCase();
        if (!rid) return;
        localStorage.setItem('mp_room', rid);
        window.game.multiplayer.disconnect();
        const playerName = window.game.telegram.getPlayerName();
        const skin = window.game.skinManager.getCurrentSkin();
        window.game.multiplayer.connect(rid, playerName, skin);
        window.game.multiplayer.setPlayer(window.game.player);
        window.game.multiplayer.on('connected', _mpUpdateStatus);
        window.game.multiplayer.on('disconnected', _mpUpdateStatus);
        window.game.multiplayer.on('player_joined', _mpUpdateStatus);
        window.game.multiplayer.on('player_left', _mpUpdateStatus);
        setTimeout(_mpUpdateStatus, 300);
    });

    document.getElementById('mpCreateBtn').addEventListener('click', () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let code = '';
        for (let i = 0; i < 6; i++) {
            code += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        document.getElementById('mpRoomInput').value = code;
    });

    document.getElementById('mpLeaveBtn').addEventListener('click', () => {
        window.game.multiplayer.disconnect();
        localStorage.removeItem('mp_room');
        _mpUpdateStatus();
    });

    // Register mp event listeners if already connected from URL params
    if (window.game.multiplayer.isConnected()) {
        window.game.multiplayer.on('connected', _mpUpdateStatus);
        window.game.multiplayer.on('disconnected', _mpUpdateStatus);
        window.game.multiplayer.on('player_joined', _mpUpdateStatus);
        window.game.multiplayer.on('player_left', _mpUpdateStatus);
    }
});
